import { CardData, CardKind, getCardKind, isExtraDeckCard } from '../types/cards';
import { DEFAULT_CARD_FOIL } from '../types/foil';
import {
  ActionType,
  PlayerAction,
} from '../types/actions';
import {
  EventType,
  type AttackCancelReason,
  type ChainLinkView,
  type DuelErrorCode,
  type DuelMessageCode,
  type GameEvent,
} from '../types/events';
import {
  CardInstance,
  CardPosition,
  type ChainLink,
  EnginePhase,
  MatchState,
  PendingAttack,
  PendingChoice,
  PendingFlipEffect,
  PlayerInfo,
  parseResponseMode,
  Seat,
  SpellSpeed,
  STARTING_HAND_SIZE,
  STARTING_LIFE_POINTS,
  HAND_SIZE_LIMIT,
  otherSeat,
  isOpeningTurn,
  nextEnginePhase,
  type CoinSide,
  type DuelPromptCode,
} from '../types/state';
import {
  CardLibrary,
  buildLibrary,
  emptyZones,
  findCard,
  firstFreeIndex,
  getCardData,
  getLevel,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
  kindOf,
  makeInstance,
  requiredTributes,
  canIgniteUltimateOffering,
  canNormalOrTributeFromHand,
  soulExchangeTributeUids,
} from './helpers';
import { nextRandom, shuffle } from './rng';
import type { ReplayAction } from '../replay/duelReplay';
import { lingeringSpecFor } from './lingeringTurns';
import { pinOpeningHand, seedIdsForSeat, seedTestCardsIntoDeck } from './testHandSeeds';
import { EXODIA_PIECE_IDS, EXODIA_PIECE_SET } from './aiRecipes';
import {
  emptyDuelMissionMetrics,
  type DuelMissionMetrics,
} from '../missions/metrics';
import {
  EngineApi,
  EngineEffect,
  EngineEffectTrigger,
  engineEffects,
  attributeAuraDelta,
  attributeAuraOf,
  BATTLEGUARD_PER_PARTNER,
  CASTLE_OF_DARK_ILLUSIONS,
  CASTLE_PULSE,
  fieldZombies,
  GHOUL_PER_MONSTER,
  INSECT_SOLDIERS,
  INSECT_SOLDIERS_BOOST,
  LAVA_BATTLEGUARD,
  MUKA_MUKA,
  MUKA_PER_CARD,
  PUMPKING,
  PUMPKING_FLAT,
  PUMPKING_STANDBYS,
  SHADOW_GHOUL,
  SWAMP_BATTLEGUARD,
  getFieldSpellConfig,
  registerEngineEffects,
  attackLockCode,
  cardAttacksDirectly,
  coinIgnitionOf,
  tributeIgnitionOf,
  tributeCandidateUids,
  type TributeIgnition,
  BLAST_JUGGLER,
  TRIBUTE_TO_THE_DOOMED,
  CHEERFUL_COFFIN,
  SHARE_THE_PAIN,
  MUSHROOM_MAN_2,
  DARK_ELF,
  DARK_ELF_COST,
  ELECTRIC_LIZARD,
  magnetismTargets,
  STEEL_SCORPION,
  STEEL_SCORPION_ENDS,
  UNHAPPY_MAIDEN,
  CRASS_CLOWN,
  DREAM_CLOWN,
  isDragonProtected,
  positionTriggerOf,
  type PositionTrigger,
  declaresAttackWithCoin,
  graveSearchOf,
  handIgnitionOf,
  IMMORTAL_OF_THUNDER,
  MASKED_SORCERER,
  ROBBIN_GOBLIN,
  SWORD_OF_DEEP_SEATED,
  getEquipConfig,
  THE_BISTRO_BUTCHER,
  THUNDER_DRAGON,
  WHITE_MAGICAL_HAT,
  ELEGANT_EGOTIST,
  FISSURE,
  fissureMinGroup,
  GATE_GUARDIAN,
  HARPIE_LADY,
  HARPIE_LADY_SISTERS,
  cannotNormalSummon,
  COCOON_OF_EVOLUTION,
  evolveTribute,
  isGateTributeSet,
  mothEvolveTurn,
  PETIT_MOTH,
  STARTER_IDS,
  twoProngedTradeForAi,
  type GraveSearch,
} from './effects';
import {
  aiLowValueDiscard,
  aiPreferRecruiterTribute,
  planAiChainResponses,
  shouldDropKuriboh,
} from './aiResponses';

export interface CreateMatchOptions {
  id: string;
  seats: [PlayerInfo, PlayerInfo];
  decks: [CardData[], CardData[]];
  extraDecks?: [CardData[], CardData[]];
  seed: number;
  /** Seat that won the die-off and takes the first turn. Defaults to 0. */
  firstPlayer?: Seat;
  /**
   * Full catalog used to inject OPENING_HAND_TEST_IDS even when a seat's
   * saved deck does not contain those cards.
   */
  catalog?: CardData[];
}

function isResignAction(type: ActionType): boolean {
  return (
    type === ActionType.SURRENDER ||
    type === ActionType.FORFEIT ||
    type === ActionType.TIME_OUT
  );
}

/** Preference / courtesy: must not consume the turn or wake the AI. */
export function isNonPlayAction(type: ActionType): boolean {
  return type === ActionType.SET_RESPONSE_MODE || type === ActionType.SHUFFLE_HAND;
}

let uidCounter = 0;
/**
 * Card ids for one match. This stream is not `rngState`, so the shuffle
 * cursor stays the same as before uids were reproducible.
 */
function uidFromSeed(seed: number): () => string {
  let state = (Math.imul((seed >>> 0) || 1, 0x9e3779b9) ^ 0x85ebca6b) >>> 0;
  if (state === 0) state = 1;
  let n = 0;
  return () => {
    n += 1;
    const rolled = nextRandom(state);
    state = rolled.state;
    const suffix = Math.floor(rolled.value * 36 ** 6)
      .toString(36)
      .padStart(6, '0');
    const uid = `c${n}_${suffix}`;
    noteUid(uid);
    return uid;
  };
}

function noteUid(uid: string): void {
  const match = /^c(\d+)_/.exec(uid);
  if (!match) return;
  const n = Number(match[1]);
  if (Number.isFinite(n) && n > uidCounter) uidCounter = n;
}

/** Raise the process-wide card id so a restored board does not mint duplicates. */
function seedUidCounter(state: MatchState): void {
  for (const zones of state.zones) {
    const cards: (CardInstance | null | undefined)[] = [
      ...zones.deck,
      ...zones.extraDeck,
      ...zones.hand,
      ...zones.monsters,
      ...zones.spellTraps,
      ...zones.graveyard,
      ...zones.banished,
      zones.fieldSpell,
    ];
    for (const card of cards) {
      if (card) noteUid(card.uid);
    }
  }
}

const DISCARD_HEALS = new Set(['38199696', '11868825', '84257639']);
const DISCARD_SMALL_BURN = new Set(['76103675', '46130346']);
const DISCARD_FLIPS = new Set([
  '54652250',
  '7089711',
  '33066139',
  '9076207',
  '46461247',
]);
const DISCARD_KEEPERS = new Set([
  '55144522',
  '12580477',
  '53129443',
  '83764718',
  '4031928',
  '24094653',
]);

/**
 * Lowest score is discarded. Pieces, removal, flips and bosses stay.
 * A heal goes first when life is already high.
 */
function pickAiDiscardUid(
  hand: CardInstance[],
  library: CardLibrary,
  lifePoints: number,
  extra: CardInstance[],
): string | null {
  if (hand.length === 0) return null;
  const materialNames = fusionMaterialNames(extra, library);
  let worst: CardInstance | null = null;
  let worstScore = Infinity;
  for (const card of hand) {
    const score = discardKeepScore(card, library, lifePoints, materialNames);
    if (score < worstScore) {
      worstScore = score;
      worst = card;
    }
  }
  return worst?.uid ?? null;
}

function fusionMaterialNames(extra: CardInstance[], library: CardLibrary): Set<string> {
  const names = new Set<string>();
  for (const fusion of extra) {
    const desc = getCardData(library, fusion.cardId).desc ?? '';
    const matches = desc.match(/"([^"]+)"/g) ?? [];
    for (const raw of matches) names.add(raw.replace(/"/g, ''));
  }
  return names;
}

function discardKeepScore(
  card: CardInstance,
  library: CardLibrary,
  lifePoints: number,
  materialNames: Set<string>,
): number {
  if (EXODIA_PIECE_SET.has(card.cardId)) return 100_000;
  if (DISCARD_KEEPERS.has(card.cardId)) return 8_000;
  const data = getCardData(library, card.cardId);
  const kind = getCardKind(data);
  if (DISCARD_FLIPS.has(card.cardId)) return 5_000;
  if (kind === CardKind.TRAP) return 3_500;
  if (kind === CardKind.MONSTER) {
    const atk = data.atk ?? 0;
    const level = data.level ?? 0;
    if (materialNames.has(data.name)) return 4_500 + atk;
    if (level >= 5) return 4_000 + atk;
    if ((data.typeline ?? []).includes('Normal') && atk < 1400) return 80 + atk / 10;
    if (atk < 1000) return 200 + atk;
    return 1_000 + atk;
  }
  if (DISCARD_SMALL_BURN.has(card.cardId)) return 50;
  if (DISCARD_HEALS.has(card.cardId)) return lifePoints >= 5000 ? 100 : 2_000;
  return 1_200;
}

/** Safety cap so a bug cannot grow a chain forever. */
const MAX_CHAIN_LINKS = 10;

/** Shown to the seat that is not choosing. Does not name the window. */
const HIDDEN_PRIORITY_PROMPT = 'Activate a Trap?';

/**
 * Per-seat event view. Draws and private peeks stay hidden, and a trap
 * window does not list the other seat's legal Set cards or its timing.
 */
export function redactEventsForSeat(events: GameEvent[], viewer: Seat): GameEvent[] {
  return events.map((e) => {
    if (e.type === EventType.CARD_DRAWN && e.seat !== viewer) {
      return { ...e, cardId: null };
    }
    if (e.type === EventType.PEEK_CARDS && e.seat !== viewer && !e.reveal) {
      return { ...e, cards: [] };
    }
    if (e.type === EventType.REQUEST_PRIORITY && e.seat !== viewer) {
      return {
        ...e,
        activatableUids: [],
        prompt: HIDDEN_PRIORITY_PROMPT,
        promptCode: 'trapActivate',
      };
    }
    return e;
  });
}

/**
 * Public watch. Neither seat's draw, private peek, or trap list is shown.
 * A public reveal (The Stern Mystic) keeps its cards.
 */
export function redactEventsForPublic(events: GameEvent[]): GameEvent[] {
  return events.map((e) => {
    if (e.type === EventType.CARD_DRAWN) {
      return { ...e, cardId: null };
    }
    if (e.type === EventType.PEEK_CARDS && !e.reveal) {
      return { ...e, cards: [] };
    }
    if (e.type === EventType.REQUEST_PRIORITY) {
      return {
        ...e,
        activatableUids: [],
        prompt: HIDDEN_PRIORITY_PROMPT,
        promptCode: 'trapActivate',
      };
    }
    return e;
  });
}

/**
 * Hide both seats' private cards on a board snapshot. Safe to run on the
 * admin watch clone: decks are already blank, and this also blanks hands
 * and face-down cards. Does not mutate `state`.
 */
export function maskStateForPublicSpectator(state: MatchState): MatchState {
  const clone: MatchState = JSON.parse(JSON.stringify(state));
  clone.trapHolePassedUids = [];
  clone.battlePassedUids = [];
  clone.activatableUids = [];
  clone.aiKnownCards = [[], []];
  clone.responseMode = ['auto', 'auto'];
  const mask = (c: CardInstance | null): CardInstance | null => {
    if (!c) return null;
    return { ...c, cardId: '', foil: DEFAULT_CARD_FOIL };
  };
  const maskSet = (c: CardInstance | null): CardInstance | null => {
    if (!c || c.faceUp) return c;
    return mask(c);
  };
  for (const seat of [0, 1] as Seat[]) {
    const z = clone.zones[seat];
    z.hand = z.hand.map((c) => mask(c)!) as CardInstance[];
    z.deck = z.deck.map((c) => mask(c)!) as CardInstance[];
    z.extraDeck = z.extraDeck.map((c) => mask(c)!) as CardInstance[];
    z.monsters = z.monsters.map(maskSet);
    z.spellTraps = z.spellTraps.map(maskSet);
    if (z.fieldSpell) z.fieldSpell = maskSet(z.fieldSpell);
  }
  if (clone.pendingTargets) {
    clone.pendingTargets = { ...clone.pendingTargets, candidateUids: [] };
  }
  if (clone.pendingFlipEffect) {
    clone.pendingFlipEffect = { ...clone.pendingFlipEffect, targetUids: [] };
  }
  if (clone.pendingPriority) {
    clone.pendingPriority = {
      ...clone.pendingPriority,
      activatableUids: [],
      prompt: HIDDEN_PRIORITY_PROMPT,
      promptCode: 'trapActivate',
      triggerUid: null,
    };
    if (clone.battleStep) clone.battleStep = 'BATTLE';
    if (clone.pendingAttack?.stage === 'preDamage') {
      clone.pendingAttack = { ...clone.pendingAttack, stage: 'declared' };
    }
  }
  return clone;
}

const TWO_PRONGED_ID = 83887306;

function chainLinkView(link: ChainLink): ChainLinkView {
  return {
    link: link.link,
    controller: link.controller,
    cardUid: link.sourceUid,
    cardId: link.cardId,
  };
}

export type SeatMissionAcc = {
  cardsDrawn: number;
  summons: number;
  tributes: number;
  spells: number;
  traps: number;
  fusions: number;
  attacks: number;
  maxBattleDamageTurn: number;
  damageThisTurn: number;
  damageTurn: number;
};

function emptySeatMissionAcc(): SeatMissionAcc {
  return {
    cardsDrawn: 0,
    summons: 0,
    tributes: 0,
    spells: 0,
    traps: 0,
    fusions: 0,
    attacks: 0,
    maxBattleDamageTurn: 0,
    damageThisTurn: 0,
    damageTurn: -1,
  };
}

/**
 * Bump when the checkpoint shape or the meaning of a restored field changes.
 * A deploy that does not match refuses the photo and pays the cut coins.
 */
export const ENGINE_CHECKPOINT_VERSION = 1;

/** Fields that stay set between actions and are not on `MatchState`. */
export interface EngineCheckpoint {
  version: number;
  state: MatchState;
  missionStats: [SeatMissionAcc, SeatMissionAcc];
  priorityCounter: number;
  fastTrapWindowKey: string | null;
  pendingBattleGy: string[];
  /** Legacy photos only. New checkpoints write null; restore folds it into the zone. */
  lastWillTriggerSeat: Seat | null;
  summonFlipUid: string | null;
  summonFlipTargetUid: string | null;
  /** Normal or Flip Summon whose Puppeteer gain is waiting out negation. */
  pendingPuppeteerUid: string | null;
  resumeOfferingWindow: boolean;
}

function asCount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

function missionAccFrom(raw: unknown): SeatMissionAcc {
  const empty = emptySeatMissionAcc();
  if (!raw || typeof raw !== 'object') return empty;
  const src = raw as Partial<SeatMissionAcc>;
  return {
    cardsDrawn: asCount(src.cardsDrawn),
    summons: asCount(src.summons),
    tributes: asCount(src.tributes),
    spells: asCount(src.spells),
    traps: asCount(src.traps),
    fusions: asCount(src.fusions),
    attacks: asCount(src.attacks),
    maxBattleDamageTurn: asCount(src.maxBattleDamageTurn),
    damageThisTurn: asCount(src.damageThisTurn),
    damageTurn: typeof src.damageTurn === 'number' && Number.isFinite(src.damageTurn)
      ? Math.floor(src.damageTurn)
      : -1,
  };
}

function asSeatOrNull(value: unknown): Seat | null {
  return value === 0 || value === 1 ? value : null;
}

/** Null when the photo is from another checkpoint version or is not a match. */
export function readEngineCheckpoint(raw: unknown): EngineCheckpoint | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Partial<EngineCheckpoint>;
  if (src.version !== ENGINE_CHECKPOINT_VERSION) return null;
  const state = src.state;
  if (!state || !Array.isArray(state.zones) || state.zones.length !== 2) return null;
  const stats = src.missionStats;
  return {
    version: ENGINE_CHECKPOINT_VERSION,
    state,
    missionStats: [
      missionAccFrom(Array.isArray(stats) ? stats[0] : null),
      missionAccFrom(Array.isArray(stats) ? stats[1] : null),
    ],
    priorityCounter: asCount(src.priorityCounter),
    fastTrapWindowKey: typeof src.fastTrapWindowKey === 'string' ? src.fastTrapWindowKey : null,
    pendingBattleGy: Array.isArray(src.pendingBattleGy)
      ? src.pendingBattleGy.filter((uid): uid is string => typeof uid === 'string')
      : [],
    lastWillTriggerSeat: asSeatOrNull(src.lastWillTriggerSeat),
    summonFlipUid: typeof src.summonFlipUid === 'string' ? src.summonFlipUid : null,
    summonFlipTargetUid:
      typeof src.summonFlipTargetUid === 'string' ? src.summonFlipTargetUid : null,
    pendingPuppeteerUid:
      typeof src.pendingPuppeteerUid === 'string' ? src.pendingPuppeteerUid : null,
    resumeOfferingWindow: src.resumeOfferingWindow === true,
  };
}

const SOLEMN_JUDGMENT = 41420027;
const MAGIC_JAMMER = 77414722;
const SEVEN_TOOLS = 3819470;
const HORN_OF_HEAVEN = 98069388;
const FAKE_TRAP = 3027001;
const KURIBOH = 40640057;
const SANGA = 25955164;
const KAZEJIN = 62340868;
const SUIJIN = 98434877;
const COUNTER_TRAP_IDS = new Set([SOLEMN_JUDGMENT, MAGIC_JAMMER, SEVEN_TOOLS, HORN_OF_HEAVEN]);
const QUICK_MONSTER_IDS = new Set([KURIBOH, SANGA, KAZEJIN, SUIJIN]);

type GraveJob =
  | { kind: 'immortal'; controller: Seat; sourceUid: string; cardId: string }
  | {
      kind: 'search';
      owner: Seat;
      sourceUid: string;
      cardId: string;
      which: GraveSearch;
    }
  | { kind: 'sword'; owner: Seat; sourceUid: string; cardId: string }
  | {
      kind: 'discardRandom' | 'draw1' | 'drawOpp2';
      controller: Seat;
      sourceUid: string;
      cardId: string;
    };

export class GameEngine {
  private state: MatchState;
  private readonly library: CardLibrary;
  private events: GameEvent[] = [];
  private missionStats: [SeatMissionAcc, SeatMissionAcc] = [
    emptySeatMissionAcc(),
    emptySeatMissionAcc(),
  ];
  /** lazily-built card name -> cardId lookup (used to resolve fusion materials). */
  private nameToId: Map<string, string> | null = null;
  /** monotonic id source for trap-response (priority) windows. */
  private priorityCounter = 0;
  /** Actions that actually applied. A restored duel with a gap drops this. */
  private replayLog: ReplayAction[] = [];
  private replayIntact = true;
  /**
   * The monster whose trap-response window is being evaluated or answered right now.
   * Set only for the duration of those calls, so an effect gated on a response (Trap
   * Hole) sees the trigger while nothing else can activate it out of turn.
   */
  private responseTriggerUid: string | null = null;
  /**
   * Battle position a Special Summon from the current activation should use.
   * Reset after resolveActivation so a later effect does not inherit it.
   */
  private chosenSummonPosition: CardPosition = CardPosition.ATTACK;
  /**
   * Per-target positions for an activation that Special Summons more than one
   * monster. Keyed by the summoned card's uid. Cleared with chosenSummonPosition.
   */
  private chosenSummonPositions = new Map<string, CardPosition>();
  /**
   * Once-per-phase key for the 2002 "activate a trap on the opponent's turn"
   * window, so passing it does not immediately re-open the same prompt.
   */
  private fastTrapWindowKey: string | null = null;
  /**
   * The End Phase button already asked to pass the turn. The Battle End Step
   * window ate that second advance; finishing the window completes it.
   */
  private passTurnAfterEnd = false;
  /**
   * Monsters destroyed by the current battle that still owe their Flip Effect.
   * They stay on the field until that effect finishes, then go to the GY.
   * They are not legal targets while they wait (Hane-Hane cannot bounce itself
   * out of a battle that already destroyed it).
   */
  private pendingBattleGy: string[] = [];
  /** The Unhappy Maiden was sent to the GY by battle; the Battle Phase ends. */
  private maidenEndsBattle = false;
  /**
   * Position-change triggers waiting until no chain or prompt is open.
   * Crass Clown, Dream Clown, Tainted Wisdom, Yado Karu.
   */
  private positionJobs: { uid: string; cardId: string; seat: Seat; kind: PositionTrigger }[] = [];
  /**
   * Flip Effects whose monster is already face-up, waiting because another
   * Flip decision is open. Swords of Revealing Light can flip Big Eye and
   * Man-Eater Bug together; answering one used to be rejected while the
   * other window was also pending.
   */
  private queuedFlips: { uid: string; seat: Seat }[] = [];
  /** Blast Juggler and Mushroom Man #2 prompts still waiting this phase. */
  private choiceJobs: { kind: 'juggler' | 'mushroom'; seat: Seat; uid: string }[] = [];
  /**
   * Tribute to the Doomed already discarded for the activation being built.
   * Cleared when that activation joins the chain or fizzles. The same card
   * brought back from the Graveyard pays the discard again.
   */
  private doomedPaid = new Set<string>();
  /**
   * Battle-damage triggers (Hat, Masked Sorcerer, Butcher, Robbin' Goblin).
   * Flushed before the Graveyard queue so the two timings stay separate chains.
   */
  private battleQueue: GraveJob[] = [];
  /**
   * Graveyard triggers queued by sendToGraveyard. When the action is idle
   * they become one chain: the turn player orders theirs, then the opponent,
   * then a faster card may chain. Resolve last-in first-out. A Deck search
   * pauses that link until the player picks.
   */
  private graveQueue: GraveJob[] = [];
  /** Groups still waiting to be placed, turn player first. */
  private graveBuild: GraveJob[][] | null = null;
  /** The open chain was built from mandatory Graveyard triggers. */
  private graveChainPending = false;
  private resolvingGraveChain = false;
  /** Ultimate Offering controllers found while walking the current chain. */
  private chainOfferingSeats: Seat[] = [];
  /** True while resolveChain is walking links (do not open nested windows). */
  private resolvingChain = false;
  /**
   * Flip Summon whose Flip Effect is waiting on the summon-response window.
   * The target, if any, is chosen first. Trap Hole may then destroy the
   * monster; that does not cancel the effect.
   */
  private summonFlipUid: string | null = null;
  /** Target already chosen for `summonFlipUid`, resolved after Trap Hole. */
  private summonFlipTargetUid: string | undefined = undefined;
  /**
   * Normal or Flip Summon that has not cleared Horn of Heaven / Solemn
   * Judgment yet. Mysterious Puppeteer gains 500 only after that window
   * closes without negating the summon.
   */
  private pendingPuppeteerUid: string | null = null;
  /** Seats whose hand must shuffle after this action (a card bounced home). */
  private handShuffleSeats = new Set<Seat>();
  /** Normal, Flip, or Special, for Horn of Heaven versus Solemn Judgment. */
  private summonKind: 'normal' | 'flip' | 'special' | null = null;
  /** Chain links currently resolving, last-in first-out. */
  private resolvingLinks: ChainLink[] = [];
  private resolvingIndex = -1;
  /** Seat whose traps Fake Trap is protecting, and the chain link it replaced. */
  private fakeTrapSeat: Seat | null = null;
  private fakeTrapSourceUid: string | null = null;
  /** Face-down traps spared by that replacement, confirmed once. */
  private sparedFaceDown: { uid: string; cardId: string }[] = [];
  /** Kuriboh: this attack's battle damage to this seat is 0. */
  private zeroDamageSeat: Seat | null = null;
  /** Summons a Counter Trap undid. Their Flip Effects do not resolve. */
  private negatedSummonUids = new Set<string>();
  /**
   * True only while Solemn Judgment or Horn of Heaven is destroying the
   * monster whose summon it negated. That send is not "from the field".
   */
  private sendingNegatedSummon = false;
  /**
   * The defender just paid Ultimate Offering during the opponent's Battle
   * Phase. After the summon window closes, ask again before damage or the
   * next attack so they can pay once more.
   */
  private resumeOfferingWindow = false;

  constructor(state: MatchState, library: CardLibrary) {
    registerEngineEffects();
    this.state = state;
    this.library = library;
    this.state.responseMode ??= ['minimal', 'minimal'];
    this.state.pendingChainOrder ??= null;
    this.state.pendingChoice ??= null;
    this.state.trapHolePassedUids ??= [];
    this.state.battlePassedUids ??= [];
    this.state.aiKnownCards ??= [[], []];
    this.normalizeLastWillZones();
    this.normalizeSoulExchange();
  }

  /** Board plus the fields that stay set between actions. */
  exportCheckpoint(): EngineCheckpoint {
    return {
      version: ENGINE_CHECKPOINT_VERSION,
      state: structuredClone(this.state),
      missionStats: structuredClone(this.missionStats),
      priorityCounter: this.priorityCounter,
      fastTrapWindowKey: this.fastTrapWindowKey,
      pendingBattleGy: [...this.pendingBattleGy],
      lastWillTriggerSeat: null,
      summonFlipUid: this.summonFlipUid,
      summonFlipTargetUid: this.summonFlipTargetUid ?? null,
      pendingPuppeteerUid: this.pendingPuppeteerUid,
      resumeOfferingWindow: this.resumeOfferingWindow,
    };
  }

  /** Rebuilds an engine from a photo taken between actions. */
  static restore(checkpoint: EngineCheckpoint, library: CardLibrary): GameEngine {
    if (checkpoint.version !== ENGINE_CHECKPOINT_VERSION) {
      throw new Error('checkpoint version');
    }
    const state = structuredClone(checkpoint.state);
    seedUidCounter(state);
    const engine = new GameEngine(state, library);
    engine.missionStats = [
      missionAccFrom(checkpoint.missionStats[0]),
      missionAccFrom(checkpoint.missionStats[1]),
    ];
    engine.priorityCounter = asCount(checkpoint.priorityCounter);
    engine.fastTrapWindowKey = checkpoint.fastTrapWindowKey;
    engine.pendingBattleGy = [...checkpoint.pendingBattleGy];
    const legacyWill = asSeatOrNull(checkpoint.lastWillTriggerSeat);
    if (legacyWill !== null) {
      const zone = engine.state.zones[legacyWill];
      zone.lastWillReady = true;
      if (zone.lastWillCharges > 0) zone.lastWillAsk = true;
    }
    engine.summonFlipUid = checkpoint.summonFlipUid;
    engine.summonFlipTargetUid = checkpoint.summonFlipTargetUid ?? undefined;
    engine.pendingPuppeteerUid = checkpoint.pendingPuppeteerUid;
    engine.resumeOfferingWindow = checkpoint.resumeOfferingWindow === true;
    return engine;
  }

  takeReplayActions(): ReplayAction[] {
    return this.replayLog;
  }

  replayIsIntact(): boolean {
    return this.replayIntact;
  }

  /** Replace the log after a process restart that still has every action. */
  restoreReplayActions(actions: ReplayAction[]): void {
    this.replayLog = actions.map((row) => ({
      s: row.s,
      a: structuredClone(row.a),
    }));
    this.replayIntact = true;
  }

  /** The log no longer matches the board. Do not persist a replay. */
  dropReplay(): void {
    this.replayIntact = false;
    this.replayLog = [];
  }

  private rememberReplay(seat: Seat, action: PlayerAction): void {
    if (!this.replayIntact) return;
    this.replayLog.push({ s: seat, a: structuredClone(action) });
    if (this.replayLog.length > 8000) this.dropReplay();
  }

  static createMatch(opts: CreateMatchOptions): GameEngine {
    registerEngineEffects();
    const byId = new Map<string, CardData>();
    for (const card of opts.catalog ?? []) byId.set(String(card.id), card);
    for (const deck of [opts.decks[0], opts.decks[1]]) {
      for (const card of deck) byId.set(String(card.id), card);
    }
    const findData = (id: string): CardData | undefined => byId.get(String(id));
    const decks: [CardData[], CardData[]] = [
      seedTestCardsIntoDeck(opts.decks[0], findData, undefined, 0),
      seedTestCardsIntoDeck(opts.decks[1], findData, undefined, 1),
    ];
    const library = buildLibrary([
      decks[0],
      decks[1],
      opts.extraDecks?.[0] ?? [],
      opts.extraDecks?.[1] ?? [],
    ]);

    const firstPlayer: Seat = opts.firstPlayer === 1 ? 1 : 0;
    const state: MatchState = {
      id: opts.id,
      seats: opts.seats,
      zones: [emptyZones(STARTING_LIFE_POINTS), emptyZones(STARTING_LIFE_POINTS)],
      turnPlayer: firstPlayer,
      // Opening hands are 5 each; the first player's sixth card is their
      // Draw Phase. The HUD starts on DP, then the host auto-advances to SP.
      phase: EnginePhase.DRAW,
      turnCount: 1,
      absoluteTurnCount: 1,
      status: 'active',
      winner: null,
      winReason: null,
      rngState: opts.seed,
      pendingPriority: null,
      pendingAttack: null,
      chain: [],
      chainPasses: 0,
      chainTriggerUid: null,
      battleStep: null,
      battleLeaveTo: null,
      pendingDiscard: null,
      pendingFlipEffect: null,
      pendingTargets: null,
      pendingOfferingSummon: null,
      pendingCoinCall: null,
      pendingChoice: null,
      pendingChainOrder: null,
      reverseStatMods: false,
      battleDamageImmune: [false, false],
      battleDestroyImmune: [false, false],
      responseMode: ['minimal', 'minimal'],
      trapHolePassedUids: [],
      battlePassedUids: [],
      aiKnownCards: [[], []],
    };

    const mintUid = uidFromSeed(opts.seed);
    for (const seat of [0, 1] as Seat[]) {
      const deck = decks[seat].map((c) =>
        makeInstance(mintUid(), c.id, seat, c.foil),
      );
      const extra = (opts.extraDecks?.[seat] ?? []).map((c) =>
        makeInstance(mintUid(), c.id, seat, c.foil),
      );
      const shuffled = shuffle(deck, state.rngState);
      state.rngState = shuffled.rngState;
      const items = shuffled.items;
      pinOpeningHand(items, seedIdsForSeat(seat), (id) => {
        const data = findData(id);
        if (!data) return undefined;
        return makeInstance(mintUid(), data.id, seat, data.foil);
      });
      state.zones[seat].deck = items;
      state.zones[seat].extraDeck = extra;
    }

    const engine = new GameEngine(state, library);
    // Deal opening hands silently (no events; the snapshot carries them).
    for (const seat of [0, 1] as Seat[]) {
      for (let i = 0; i < STARTING_HAND_SIZE; i++) {
        const card = state.zones[seat].deck.shift();
        if (card) state.zones[seat].hand.push(card);
      }
    }
    // 2002 TCG: the first player conducts their Draw Phase (open 5, draw to 6).
    // The "first player skips the draw" rule is a 14 July 2014 change.
    const firstDraw = state.zones[firstPlayer].deck.shift();
    if (firstDraw) state.zones[firstPlayer].hand.push(firstDraw);
    // A player dealt all five Exodia pieces (or who drew the fifth) wins immediately.
    for (const seat of [0, 1] as Seat[]) engine.checkExodia(seat);
    return engine;
  }

  getState(): MatchState {
    return this.state;
  }

  getLibrary(): CardLibrary {
    return this.library;
  }

  /**
   * Action counters for one seat. Outcome flags are filled when the match
   * has already finished.
   */
  getDuelStats(seat: Seat): DuelMissionMetrics {
    const acc = this.missionStats[seat];
    const out = emptyDuelMissionMetrics();
    out.cardsDrawn = acc.cardsDrawn;
    out.summons = acc.summons;
    out.tributes = acc.tributes;
    out.spells = acc.spells;
    out.traps = acc.traps;
    out.fusions = acc.fusions;
    out.attacks = acc.attacks;
    out.maxBattleDamageTurn = acc.maxBattleDamageTurn;
    out.finalLp = this.state.zones[seat].lifePoints;
    out.turnCount = this.state.absoluteTurnCount;
    out.shownTurn = this.state.turnCount;
    out.winReason = this.state.winReason;
    if (this.state.status === 'finished') {
      out.played = 1;
      out.won = this.state.winner === seat ? 1 : 0;
    }
    return out;
  }

  /** Perspective-filtered snapshot: hides the opponent's hidden information from `viewer`. */
  getStateForSeat(viewer: Seat): MatchState {
    const clone: MatchState = JSON.parse(JSON.stringify(this.state));
    clone.trapHolePassedUids = [];
    clone.battlePassedUids = [];
    clone.aiKnownCards[otherSeat(viewer)] = [];
    const opp = otherSeat(viewer);
    const z = clone.zones[opp];
    const mask = (c: CardInstance | null): CardInstance | null => {
      if (!c) return null;
      return { ...c, cardId: '', foil: DEFAULT_CARD_FOIL };
    };
    // A Set is the controller's secret. Change of Heart can leave their Set
    // on your field (you may look) or yours on theirs (hidden from you).
    const maskSet = (c: CardInstance | null): CardInstance | null => {
      if (!c || c.faceUp) return c;
      return mask(c);
    };
    z.hand = z.hand.map((c) => mask(c)!) as CardInstance[];
    z.deck = z.deck.map((c) => mask(c)!) as CardInstance[];
    z.extraDeck = z.extraDeck.map((c) => mask(c)!) as CardInstance[];
    z.monsters = z.monsters.map(maskSet);
    z.spellTraps = z.spellTraps.map(maskSet);
    if (z.fieldSpell) z.fieldSpell = maskSet(z.fieldSpell);
    // Also hide the viewer's own deck order, except cards they are currently
    // choosing (Last Will Special Summon from Deck).
    const revealDeck = new Set(
      clone.pendingTargets?.chooser === viewer ? clone.pendingTargets.candidateUids : [],
    );
    clone.zones[viewer].deck = this.state.zones[viewer].deck.map((c) =>
      revealDeck.has(c.uid) ? { ...c } : mask(c)!,
    ) as CardInstance[];
    // Tell the viewer which of their spells/traps can actually be activated right
    // now, so the UI can hide the "Activate" option when conditions are not met.
    clone.activatableUids = this.computeActivatableUids(viewer);
    clone.responseMode[opp] = 'auto';
    if (clone.pendingTargets && clone.pendingTargets.chooser !== viewer) {
      clone.pendingTargets = { ...clone.pendingTargets, candidateUids: [] };
    }
    // Which set cards are legal, and which Battle substep the window is in,
    // classify the opponent's face-down cards (Damage Step vs Battle Step).
    // The responder still receives the real list and step.
    if (clone.pendingPriority && clone.pendingPriority.responder !== viewer) {
      clone.pendingPriority = {
        ...clone.pendingPriority,
        activatableUids: [],
        prompt: HIDDEN_PRIORITY_PROMPT,
        promptCode: 'trapActivate',
        triggerUid: null,
      };
      if (clone.battleStep) clone.battleStep = 'BATTLE';
      if (clone.pendingAttack?.stage === 'preDamage') {
        clone.pendingAttack = { ...clone.pendingAttack, stage: 'declared' };
      }
    }
    return clone;
  }

  /**
   * Admin watch snapshot. Both hands keep their identities. Set cards keep
   * theirs too, so a hover can name them while the board still draws a back.
   * Deck and Extra order stay hidden. The activate list is cleared so a
   * watch client does not offer plays.
   */
  getStateForSpectator(): MatchState {
    const clone: MatchState = JSON.parse(JSON.stringify(this.state));
    clone.trapHolePassedUids = [];
    clone.battlePassedUids = [];
    clone.activatableUids = [];
    const mask = (c: CardInstance): CardInstance => ({
      ...c,
      cardId: '',
      foil: DEFAULT_CARD_FOIL,
    });
    for (const seat of [0, 1] as Seat[]) {
      const z = clone.zones[seat];
      z.deck = z.deck.map(mask);
      z.extraDeck = z.extraDeck.map(mask);
    }
    return clone;
  }

  /**
   * Public watch snapshot. Both hands, both decks, and every face-down card
   * lose their identity. Deck order stays hidden. The activate list is
   * cleared so a watch client does not offer plays.
   */
  getStateForPublicSpectator(): MatchState {
    return maskStateForPublicSpectator(this.state);
  }

  // ---------------------------------------------------------------------------
  // Action dispatch
  // ---------------------------------------------------------------------------

  applyAction(seat: Seat, action: PlayerAction): GameEvent[] {
    if (this.state.status === 'finished') {
      this.events = [];
      return this.events;
    }
    const events = this.applyActionBody(seat, action);
    this.rememberReplay(seat, action);
    return events;
  }

  private applyActionBody(seat: Seat, action: PlayerAction): GameEvent[] {
    this.events = [];

    // Preference, not a play: legal while any window is open, and on either
    // player's turn. No GameEvent — the seat's own client already knows.
    if (action.type === ActionType.SET_RESPONSE_MODE) {
      this.state.responseMode[seat] = parseResponseMode(action.mode);
      return this.events;
    }

    // Courtesy shuffle: legal in any window, including the opponent's turn.
    if (action.type === ActionType.SHUFFLE_HAND) {
      this.shuffleHandFor(seat);
      return this.events;
    }

    // Last Will, and the Battle Phase window after Ultimate Offering, used to
    // open on top of the chain-order list. Each guard then rejected the other
    // answer, so Confirm and cancel both came back.
    if (action.type === ActionType.ORDER_CHAIN) {
      this.deferOverlappingLastWill();
      this.deferOverlappingOfferingWindow();
    }

    // While a trap-response window is open the turn is paused: only the responder
    // may act, and only to answer the window (or to surrender).
    if (
      this.state.pendingPriority &&
      action.type !== ActionType.RESPOND_PRIORITY &&
      !isResignAction(action.type)
    ) {
      // The End Phase button sends "go to End" and then "pass the turn".
      // The first half opens this Battle End Step window, so the follow-up
      // must not toast an error. Passing the window passes the turn.
      if (
        action.type === ActionType.ADVANCE_PHASE &&
        action.to == null &&
        this.state.phase === EnginePhase.BATTLE &&
        this.state.battleStep === 'END' &&
        this.state.battleLeaveTo === EnginePhase.END
      ) {
        this.passTurnAfterEnd = true;
        return this.events;
      }
      throw new EngineError('Waiting for a trap response', 'waitingTrapResponse');
    }
    // While a discard is owed the turn is paused: only that player may act, and
    // only by discarding (or surrendering).
    if (
      this.state.pendingDiscard &&
      action.type !== ActionType.DISCARD &&
      !isResignAction(action.type)
    ) {
      throw new EngineError('You must discard down to the hand limit first', 'discardFirst');
    }
    // While a Flip-Effect target selection is open the turn is paused: only the
    // chooser may act, and only to pick the target (or to surrender).
    if (
      this.state.pendingFlipEffect &&
      action.type !== ActionType.RESOLVE_FLIP_TARGET &&
      !isResignAction(action.type)
    ) {
      throw new EngineError('Waiting for a Flip Effect target selection', 'waitingFlipTarget');
    }
    if (
      this.state.pendingTargets &&
      action.type !== ActionType.SELECT_TARGETS &&
      !isResignAction(action.type)
    ) {
      throw new EngineError('Waiting for a target selection', 'waitingTarget');
    }
    if (
      this.state.pendingCoinCall &&
      action.type !== ActionType.CALL_COIN &&
      !isResignAction(action.type)
    ) {
      throw new EngineError('Call the coin first', 'waitingCoin');
    }
    if (
      this.state.pendingChoice &&
      action.type !== ActionType.ANSWER_CHOICE &&
      !isResignAction(action.type)
    ) {
      // The End Phase button sends "go to End" and then "pass the turn".
      // The first half opens this prompt, so the follow-up must not toast
      // an error. Answering the prompt passes the turn.
      if (
        action.type === ActionType.ADVANCE_PHASE &&
        this.state.phase === EnginePhase.END
      ) {
        return this.events;
      }
      throw new EngineError('Answer the prompt first', 'waitingTarget');
    }
    if (
      this.state.pendingChainOrder &&
      action.type !== ActionType.ORDER_CHAIN &&
      !isResignAction(action.type)
    ) {
      throw new EngineError('Choose the chain order first', 'waitingChainOrder');
    }
    if (
      this.state.pendingOfferingSummon !== null &&
      action.type !== ActionType.ORDER_CHAIN &&
      !isResignAction(action.type)
    ) {
      if (seat !== this.state.pendingOfferingSummon) {
        throw new EngineError('Waiting for Ultimate Offering', 'waitingOffering');
      }
      if (action.type === ActionType.ADVANCE_PHASE) {
        // The 500 LP is a cost to activate. Passing before that costs nothing.
        // After it is paid, c80604091.lua summons (min 1). Declining would
        // discard the summon, keep the LP, and open the same question again.
        if (this.state.zones[seat].extraNormalSummons > 0) {
          throw new EngineError(
            'Ultimate Offering: Normal Summon or Set the monster you paid for',
            'offeringSummonOwed',
          );
        }
        this.clearOfferingWait();
        this.message('offeringPassed', { cardId: String(STARTER_IDS.ULTIMATE_OFFERING) }, 'Ultimate Offering: passed.');
        this.tryResumePendingAttack();
        return this.events;
      }
      const offeringAct =
        action.type === ActionType.NORMAL_SUMMON ||
        action.type === ActionType.SET_MONSTER ||
        action.type === ActionType.TRIBUTE_SUMMON ||
        action.type === ActionType.ACTIVATE_TRAP;
      if (!offeringAct) {
        throw new EngineError(
          this.state.zones[seat].extraNormalSummons > 0
            ? 'Ultimate Offering: Normal Summon or Set the monster you paid for'
            : 'Normal Summon or Set, or pass',
          this.state.zones[seat].extraNormalSummons > 0
            ? 'offeringSummonOwed'
            : 'offeringSummonOrPass',
        );
      }
    }

    switch (action.type) {
      case ActionType.ADVANCE_PHASE:
        this.requireTurn(seat);
        if (action.to) this.skipToPhase(action.to);
        else this.advancePhase();
        break;
      case ActionType.NORMAL_SUMMON:
        this.requireSummonWindow(seat);
        this.normalSummon(seat, action.cardUid, action.zoneIndex, false);
        break;
      case ActionType.SET_MONSTER:
        this.requireSummonWindow(seat);
        this.normalSummon(seat, action.cardUid, action.zoneIndex, true);
        break;
      case ActionType.TRIBUTE_SUMMON:
        this.requireSummonWindow(seat);
        this.tributeSummon(seat, action.cardUid, action.zoneIndex, action.tributeUids, action.faceDown);
        break;
      case ActionType.CHANGE_POSITION:
        this.requireTurnMain(seat);
        this.changePosition(seat, action.cardUid);
        break;
      case ActionType.ACTIVATE_MONSTER_EFFECT:
        this.activateMonsterEffect(seat, action.cardUid);
        break;
      case ActionType.SPECIAL_SUMMON_PROC:
        this.specialSummonProcedure(seat, action.cardUid, action.position, action.tributeUids);
        break;
      case ActionType.EQUIP_FROM_HAND:
        this.equipCocoon(seat, action.cardUid, action.targetUid);
        break;
      case ActionType.CALL_COIN:
        this.callCoin(seat, action.requestId, action.call);
        break;
      case ActionType.ANSWER_CHOICE:
        this.answerChoice(seat, action.requestId, action.accept);
        break;
      case ActionType.ORDER_CHAIN:
        this.orderChain(seat, action.requestId, action.cardUids);
        break;
      case ActionType.FLIP_SUMMON:
        this.requireTurnMain(seat);
        this.flipSummon(seat, action.cardUid, action.targetUid);
        break;
      case ActionType.SET_SPELL_TRAP:
        this.requireTurnMain(seat);
        this.setSpellTrap(seat, action.cardUid, action.zoneIndex);
        break;
      case ActionType.ACTIVATE_SPELL_FROM_HAND:
        this.requireTurnMain(seat);
        this.activateSpellFromHand(seat, action.cardUid, action.targetUids, action.position);
        break;
      case ActionType.ACTIVATE_SET_SPELL:
        this.requireTurnMain(seat);
        this.activateSetSpell(seat, action.cardUid, action.targetUids, action.position);
        break;
      case ActionType.ACTIVATE_TRAP:
        // 2002: after the Set turn a trap may be activated on either player's turn.
        this.activateSetTrap(seat, action.cardUid, action.targetUids);
        break;
      case ActionType.RESPOND_PRIORITY:
        this.respondPriority(seat, action.requestId, action.cardUid, action.targetUids, action.boost);
        break;
      case ActionType.RESOLVE_FLIP_TARGET:
        this.resolveFlipTarget(seat, action.requestId, action.targetUid);
        break;
      case ActionType.SELECT_TARGETS:
        this.selectActivationTargets(
          seat,
          action.requestId,
          action.targetUids,
          action.position,
          action.positions,
          action.abandon,
        );
        break;
      case ActionType.DISCARD:
        this.discardForHandLimit(seat, action.cardUids);
        break;
      case ActionType.DECLARE_ATTACK:
        this.requireTurnBattle(seat);
        this.declareAttack(seat, action.attackerUid, action.targetUid);
        break;
      case ActionType.DIRECT_ATTACK:
        this.requireTurnBattle(seat);
        this.directAttack(seat, action.attackerUid);
        break;
      case ActionType.SURRENDER:
        this.endGame(otherSeat(seat), 'surrender');
        break;
      case ActionType.FORFEIT:
        this.endGame(otherSeat(seat), 'forfeit');
        break;
      case ActionType.TIME_OUT:
        this.endGame(otherSeat(seat), 'timeout');
        break;
      default:
        this.message('unsupportedAction', undefined, 'Action not supported by engine yet');
        break;
    }
    this.recomputeFieldSpells();
    this.recomputeContinuousTraps();
    this.recomputeAttackLocks();
    this.flushPuppeteer();
    this.flushSummonFlip();
    this.dropStaleFlipWindow();
    this.flushBattleTriggers();
    this.flushGraveTriggers();
    this.flushLastWill();
    this.flushHandShuffles();
    this.flushPositionTriggers();
    // A position trigger can bounce a card after the shuffle above already ran.
    this.flushHandShuffles();
    this.flushQueuedFlips();
    this.tryResumePendingAttack();
    this.finishQueuedEndPass();
    return this.events;
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  private requireTurn(seat: Seat): void {
    if (this.state.turnPlayer !== seat) {
      throw new EngineError('Not your turn', 'notYourTurn');
    }
  }

  private requireTurnMain(seat: Seat): void {
    this.requireTurn(seat);
    if (this.state.phase !== EnginePhase.MAIN_1 && this.state.phase !== EnginePhase.MAIN_2) {
      throw new EngineError('Not in a Main Phase', 'notMainPhase');
    }
  }

  /**
   * Normal Summon/Set: your Main Phase, or the opponent's Battle Phase when
   * Ultimate Offering granted an extra summon (UDE 2003–04). Not the Damage Step.
   */
  private requireSummonWindow(seat: Seat): void {
    const myTurn = this.state.turnPlayer === seat;
    const phase = this.state.phase;
    const main =
      phase === EnginePhase.MAIN_1 || phase === EnginePhase.MAIN_2;
    if (myTurn && main) return;
    if (
      !myTurn &&
      phase === EnginePhase.BATTLE &&
      this.state.battleStep !== 'DAMAGE' &&
      this.state.zones[seat].extraNormalSummons > 0
    ) {
      return;
    }
    throw new EngineError('Not in a Main Phase', 'notMainPhase');
  }

  private requireTurnBattle(seat: Seat): void {
    this.requireTurn(seat);
    if (this.state.phase !== EnginePhase.BATTLE) {
      throw new EngineError('Not in the Battle Phase', 'notBattlePhase');
    }
    if (this.state.battleStep && this.state.battleStep !== 'BATTLE') {
      throw new EngineError('Not in the Battle Step', 'notBattleStep');
    }
    if (this.state.pendingAttack || this.state.chain.length > 0) {
      throw new EngineError('An attack is still resolving', 'attackResolving');
    }
  }

  // ---------------------------------------------------------------------------
  // Phase / turn machine (ported from client GameState.ts)
  // ---------------------------------------------------------------------------

  private nextPhase(): EnginePhase {
    const next = nextEnginePhase(this.state.phase, isOpeningTurn(this.state));
    if (
      next === EnginePhase.BATTLE &&
      this.state.zones[this.state.turnPlayer].skipBattlePhase
    ) {
      return EnginePhase.END;
    }
    return next;
  }

  /**
   * Jump to End Phase without entering skipped phases. Main Phase 2 only
   * exists after Battle; M1 → EP and BP → EP are legal skips.
   */
  private skipToPhase(target: EnginePhase): void {
    if (target !== EnginePhase.END) {
      throw new EngineError('You can only skip ahead to the End Phase', 'skipEndOnly');
    }
    const from = this.state.phase;
    if (
      from !== EnginePhase.MAIN_1 &&
      from !== EnginePhase.BATTLE &&
      from !== EnginePhase.MAIN_2
    ) {
      throw new EngineError('Cannot skip to the End Phase from here', 'cannotSkipEnd');
    }
    if (from === EnginePhase.BATTLE && this.state.battleStep !== 'END') {
      this.enterBattleEndStep(EnginePhase.END);
      return;
    }
    this.leaveToEndPhase();
  }

  private leaveToEndPhase(): void {
    this.state.battleStep = null;
    this.state.battleLeaveTo = null;
    this.state.phase = EnginePhase.END;
    this.noteLastWillPhase(this.state.turnPlayer);
    this.emit({
      type: EventType.PHASE_CHANGED,
      phase: EnginePhase.END,
      turnPlayer: this.state.turnPlayer,
    });
    this.onEnterEndPhase();
    this.offerOppFastTraps();
    this.finishQueuedEndPass();
  }

  /**
   * The End Phase button's follow-up pass was swallowed by a response window.
   * Once that window is gone and the turn is sitting on End, leave it.
   * A prompt that is still open keeps the request until a later action.
   */
  private finishQueuedEndPass(): void {
    if (!this.passTurnAfterEnd) return;
    if (this.state.status !== 'active') {
      this.passTurnAfterEnd = false;
      return;
    }
    if (this.state.phase !== EnginePhase.END) return;
    if (
      this.state.pendingPriority !== null ||
      this.state.pendingDiscard !== null ||
      this.state.pendingTargets !== null ||
      this.state.pendingFlipEffect !== null ||
      this.state.pendingOfferingSummon !== null ||
      this.state.pendingChoice !== null ||
      this.state.pendingCoinCall !== null ||
      this.state.pendingChainOrder !== null ||
      this.state.chain.length > 0 ||
      this.lastWillCanOffer(this.state.turnPlayer)
    ) {
      return;
    }
    this.passTurnAfterEnd = false;
    this.advancePhase();
  }

  private advancePhase(): void {
    if (this.state.phase === EnginePhase.BATTLE && this.state.battleStep !== 'END') {
      this.enterBattleEndStep(EnginePhase.MAIN_2);
      return;
    }
    const from = this.state.phase;
    this.advancePhaseStep();
    // Auto-skip only the Draw Phase (the draw still happens). Standby is the first
    // phase where a Set trap may be activated, so the turn lands there.
    this.leaveDrawPhase();
    // Main 1 → End (first turn, or Soul Exchange skipping Battle) must not
    // park the turn on End Phase. One advance both enters End and leaves it.
    // A hand over the limit still pauses there until the discard is chosen.
    // Last Will that can still Special Summon also pauses, so the picker
    // opens before the turn is gone. `to: End` uses leaveToEndPhase and does
    // not take this path, so the phase button's follow-up advance is not
    // applied twice.
    if (
      from === EnginePhase.MAIN_1 &&
      this.state.phase === EnginePhase.END &&
      this.state.status === 'active' &&
      !this.state.pendingDiscard &&
      !this.lastWillCanOffer(this.state.turnPlayer) &&
      !this.state.pendingChoice &&
      !this.state.pendingTargets
    ) {
      this.advancePhaseStep();
      this.leaveDrawPhase();
    }
    if (this.state.phase === EnginePhase.BATTLE) {
      this.state.battleStep = 'START';
      this.state.chainPasses = 0;
      const opp = otherSeat(this.state.turnPlayer);
      // "Off" skips ordinary trap prompts. A face-up Ultimate Offering is an
      // ignition the defender still has to be offered, or the AI Battle Phase
      // never stops for it.
      if (
        this.state.responseMode[opp] === 'off' &&
        this.faceUpOfferingIgnitionUids(opp).length === 0
      ) {
        this.continueAfterEmptyWindow();
        return;
      }
      this.openResponseWindow(opp);
      return;
    }
    this.offerOppFastTraps();
  }

  /**
   * End Step of the Battle Phase: both players may still activate, then the
   * turn leaves BP for `to` (Main 2 or End).
   */
  private enterBattleEndStep(to: EnginePhase): void {
    if (this.state.pendingAttack || this.state.chain.length > 0) {
      throw new EngineError('An attack is still resolving', 'attackResolving');
    }
    this.state.battleLeaveTo = to;
    this.state.battleStep = 'END';
    this.state.chainPasses = 0;
    const opp = otherSeat(this.state.turnPlayer);
    if (this.state.responseMode[opp] !== 'always') {
      this.continueAfterEmptyWindow();
      return;
    }
    this.openResponseWindow(opp);
  }

  private leaveBattlePhase(): void {
    const to = this.state.battleLeaveTo ?? EnginePhase.MAIN_2;
    this.state.battleLeaveTo = null;
    this.state.battleStep = null;
    if (to === EnginePhase.END) {
      this.leaveToEndPhase();
      return;
    }
    this.state.phase = to;
    this.noteLastWillPhase(this.state.turnPlayer);
    this.emit({
      type: EventType.PHASE_CHANGED,
      phase: to,
      turnPlayer: this.state.turnPlayer,
    });
    this.offerOppFastTraps();
  }

  /** Draw is not a phase the player sits on; keep walking until Standby or later. */
  private leaveDrawPhase(): void {
    while (this.state.status !== 'finished' && this.state.phase === EnginePhase.DRAW) {
      this.advancePhaseStep();
    }
  }

  private advancePhaseStep(): void {
    const next = this.nextPhase();
    if (this.state.phase === EnginePhase.END && next === EnginePhase.DRAW) {
      if (this.endPhaseBlocked()) return;
      // Hand-size limit: the turn cannot pass until the player discards down to the
      // limit. Humans are prompted (turn pauses); the AI discards automatically.
      const z = this.state.zones[this.state.turnPlayer];
      if (z.hand.length > HAND_SIZE_LIMIT) {
        const excess = z.hand.length - HAND_SIZE_LIMIT;
        if (this.state.seats[this.state.turnPlayer].isAI) {
          this.aiDiscardExcess(this.state.turnPlayer, excess);
        } else {
          this.state.pendingDiscard = { seat: this.state.turnPlayer, count: excess };
          this.message(
            'discardDown',
            { n: excess, limit: HAND_SIZE_LIMIT },
            `Discard ${excess} card(s) down to ${HAND_SIZE_LIMIT}.`,
          );
          return; // stay in the End Phase until the discard is done
        }
      }
      this.advanceTurn();
    }
    if (this.state.phase === EnginePhase.BATTLE && next !== EnginePhase.BATTLE) {
      this.state.battleStep = null;
      this.state.battleLeaveTo = null;
    }
    this.state.phase = next;
    this.noteLastWillPhase(this.state.turnPlayer);
    this.emit({ type: EventType.PHASE_CHANGED, phase: next, turnPlayer: this.state.turnPlayer });
    if (next === EnginePhase.END) {
      this.onEnterEndPhase();
    }
    if (next === EnginePhase.DRAW) {
      this.drawCards(this.state.turnPlayer, 1);
    }
    if (next === EnginePhase.STANDBY) {
      this.tickCastleStandby(this.state.turnPlayer);
      this.tickPumpkingStandby(this.state.turnPlayer);
      this.tickStandbyEquips(this.state.turnPlayer);
      this.mushroomStandby(this.state.turnPlayer);
      this.queueBlastJugglers(this.state.turnPlayer);
      this.openNextChoice();
    }
  }

  private advanceTurn(): void {
    // Tick lingering cards (Swords of Revealing Light, …) then the attack lock.
    // One of the outgoing player's turns has just concluded. When a counter
    // hits zero that card goes to the graveyard; expireSwords covers a lock
    // that hits zero without a counter still on the field.
    const outgoing = this.state.turnPlayer;
    this.tickLingeringTurns(outgoing);
    if (this.state.zones[outgoing].attackLockTurns > 0) {
      this.state.zones[outgoing].attackLockTurns -= 1;
      if (this.state.zones[outgoing].attackLockTurns === 0) {
        this.expireSwords(otherSeat(outgoing));
      }
    }

    // Last Will applies only during the turn it was activated, and a monster
    // sent on another turn does not satisfy it. Both seats lose the flag when
    // the turn passes, so Dark Hole on the opponent's turn cannot be used on
    // the following turn.
    for (const seat of [0, 1] as Seat[]) {
      const zone = this.state.zones[seat];
      zone.lastWillCharges = 0;
      zone.lastWillReady = false;
      zone.lastWillAsk = false;
    }

    let swapped = false;
    for (const side of [0, 1] as Seat[]) {
      for (const monster of getMonsters(this.state.zones[side])) {
        if (!monster.baseSwapped) continue;
        delete monster.baseSwapped;
        swapped = true;
      }
    }
    if (swapped) this.emitAllMonsterStats();

    this.state.turnPlayer = otherSeat(this.state.turnPlayer);
    this.state.absoluteTurnCount += 1;
    if (this.state.turnPlayer === 0) this.state.turnCount += 1;

    const z = this.state.zones[this.state.turnPlayer];
    z.hasNormalSummoned = false;
    z.extraNormalSummons = 0;
    this.state.pendingOfferingSummon = null;
    z.soulExchangeUids = [];
    z.skipBattlePhase = false;
    for (const m of getMonsters(z)) {
      m.summonedThisTurn = false;
      m.attackedThisTurn = false;
      m.declaredAttackThisTurn = false;
      m.positionChangedThisTurn = false;
      delete m.ignitionUsedThisTurn;
    }
    this.tickCocoonTurns(this.state.turnPlayer);
    this.emit({
      type: EventType.TURN_CHANGED,
      turnPlayer: this.state.turnPlayer,
      turnCount: this.state.turnCount,
      absoluteTurnCount: this.state.absoluteTurnCount,
    });
  }

  /**
   * Cocoon of Evolution counts the controller's turns after the equip turn.
   * The turn it was equipped does not count. The badge is that count.
   */
  private tickCocoonTurns(seat: Seat): void {
    for (const card of getSpellTraps(this.state.zones[seat])) {
      if (!card.faceUp || !card.equippedToUid) continue;
      if (Number(card.cardId) !== COCOON_OF_EVOLUTION) continue;
      card.turnCounter = (card.turnCounter ?? 0) + 1;
    }
  }

  /**
   * Castle of Dark Illusions: one +200/+200 pulse for each Standby still
   * owed, onto the Zombies that are on the field right now.
   */
  private tickCastleStandby(seat: Seat): void {
    for (const castle of getMonsters(this.state.zones[seat])) {
      if (!castle.faceUp || Number(castle.cardId) !== CASTLE_OF_DARK_ILLUSIONS) continue;
      const left = castle.turnCounter ?? 0;
      if (left < 1) continue;
      let n = 0;
      for (const zombie of fieldZombies(this.state, this.library)) {
        n += 1;
        const source = `castle:${castle.uid}:s${left}:${n}`;
        if (zombie.faceUp) this.addStatModifier(zombie.uid, CASTLE_PULSE, CASTLE_PULSE, source);
        else zombie.statModifiers.push({ atk: CASTLE_PULSE, def: CASTLE_PULSE, source });
      }
      castle.turnCounter = left - 1;
      if ((castle.turnCounter ?? 0) < 1) delete castle.turnCounter;
    }
  }

  /**
   * Pumpking: up to four Standby pulses while a face-up Castle is on the
   * field. The points already gained stay if Castle leaves afterwards.
   */
  private tickPumpkingStandby(seat: Seat): void {
    if (!this.faceUpCastleOnField()) return;
    for (const m of getMonsters(this.state.zones[seat])) {
      if (!m.faceUp || Number(m.cardId) !== PUMPKING) continue;
      const left = m.turnCounter ?? 0;
      if (left < 1) continue;
      this.addStatModifier(m.uid, PUMPKING_FLAT, PUMPKING_FLAT, `pumpking:${m.uid}:s${left}`);
      m.turnCounter = left - 1;
    }
  }

  /**
   * Germ Infection and Stim-Pack. The June 2002 line is "its Standby
   * Phases": the player who controls the equipped monster right now.
   * The loss is part of the Equip, so destroying the Spell gives the ATK back.
   */
  private tickStandbyEquips(seat: Seat): void {
    for (const side of [0, 1] as Seat[]) {
      for (const spell of getSpellTraps(this.state.zones[side])) {
        if (!spell.faceUp || !spell.equippedToUid) continue;
        const loss = getEquipConfig(Number(spell.cardId))?.standbyAtk;
        if (!loss) continue;
        const host = findCard(this.state, spell.equippedToUid);
        if (!host || host.location.zone !== 'monsters' || !host.instance.faceUp) continue;
        if (host.location.seat !== seat) continue;
        this.addStatModifier(host.instance.uid, loss, 0, spell.uid);
        const name = getCardData(this.library, spell.cardId).name;
        this.message(
          'equipStandby',
          { cardId: spell.cardId, n: Math.abs(loss) },
          `${name}: ATK -${Math.abs(loss)}.`,
        );
      }
    }
  }

  private faceUpCastleOnField(): boolean {
    for (const seat of [0, 1] as Seat[]) {
      for (const m of getMonsters(this.state.zones[seat])) {
        if (m.faceUp && Number(m.cardId) === CASTLE_OF_DARK_ILLUSIONS) return true;
      }
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Summons
  // ---------------------------------------------------------------------------

  private normalSummon(seat: Seat, uid: string, zoneIndex: number, set: boolean): void {
    const z = this.state.zones[seat];
    this.assertCanNormalSummon(z);

    const handIdx = z.hand.findIndex((c) => c.uid === uid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = z.hand[handIdx];
    if (kindOf(card, this.library) !== CardKind.MONSTER) throw new EngineError('Not a monster', 'notMonster');
    if (isExtraDeckCard(getCardData(this.library, card.cardId))) {
      throw new EngineError('Fusion monsters cannot be Normal Summoned', 'fusionNoNormal');
    }
    if (cannotNormalSummon(card.cardId)) {
      throw new EngineError('This monster cannot be Normal Summoned or Set', 'noNormalSummon');
    }

    const level = getLevel(card, this.library);
    if (requiredTributes(level) > 0) throw new EngineError('Requires tribute summon', 'requiresTribute');

    if (z.monsters[zoneIndex]) throw new EngineError('Monster zone occupied', 'monsterZoneOccupied');
    if (zoneIndex < 0 || zoneIndex >= z.monsters.length) throw new EngineError('Invalid zone', 'invalidZone');

    z.hand.splice(handIdx, 1);
    card.summonedThisTurn = true;
    card.position = set ? CardPosition.DEFENSE : CardPosition.ATTACK;
    card.faceUp = !set;
    z.monsters[zoneIndex] = card;
    this.markNormalSummonUsed(z);

    if (set) {
      card.setOnTurn = this.state.absoluteTurnCount;
      this.emit({ type: EventType.CARD_SET, seat, cardUid: card.uid, zone: 'monsters', zoneIndex });
    } else {
      this.emit({
        type: EventType.CARD_SUMMONED,
        seat,
        cardUid: card.uid,
        cardId: card.cardId,
        zoneIndex,
        position: CardPosition.ATTACK,
        tribute: false,
      });
      this.pendingPuppeteerUid = card.uid;
      this.checkSummonTraps(seat, card);
    }
  }

  private tributeSummon(
    seat: Seat,
    uid: string,
    zoneIndex: number,
    tributeUids: string[],
    faceDown: boolean,
  ): void {
    const z = this.state.zones[seat];
    this.assertCanNormalSummon(z);

    const handIdx = z.hand.findIndex((c) => c.uid === uid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = z.hand[handIdx];
    if (kindOf(card, this.library) !== CardKind.MONSTER) {
      throw new EngineError('Not a monster', 'notMonster');
    }
    if (isExtraDeckCard(getCardData(this.library, card.cardId))) {
      throw new EngineError('Fusion monsters cannot be Tribute Summoned', 'fusionNoTribute');
    }
    if (cannotNormalSummon(card.cardId)) {
      throw new EngineError('This monster cannot be Normal Summoned or Set', 'noNormalSummon');
    }
    const level = getLevel(card, this.library);
    const needed = requiredTributes(level);
    if (needed === 0) throw new EngineError('Does not require tributes', 'noTributesNeeded');
    if (tributeUids.length !== needed) throw new EngineError(`Needs exactly ${needed} tributes`, 'needsExactTributes', { n: needed });

    for (const tUid of tributeUids) {
      if (!this.isLegalTribute(seat, tUid)) throw new EngineError('Invalid tribute', 'invalidTribute');
    }
    // Soul Exchange tributes the opponent's monster and does not free a zone.
    // Reject before any send, or a full field destroys that monster and the
    // summon never happens.
    const own = new Set(getMonsters(z).map((m) => m.uid));
    const freed = tributeUids.filter((uid) => own.has(uid)).length;
    const open = z.monsters.filter((c) => c === null).length + freed;
    if (open < 1) throw new EngineError('No free monster zone', 'noFreeMonsterZone');
    for (const tUid of tributeUids) this.sendToGraveyard(tUid);
    if (z.soulExchangeUids.length > 0) {
      const spent = new Set(tributeUids);
      z.soulExchangeUids = z.soulExchangeUids.filter((uid) => !spent.has(uid));
    }

    const freeIdx = z.monsters[zoneIndex] ? firstFreeIndex(z.monsters) : zoneIndex;
    if (freeIdx === -1) throw new EngineError('No free monster zone', 'noFreeMonsterZone');

    z.hand.splice(handIdx, 1);
    card.summonedThisTurn = true;
    card.faceUp = !faceDown;
    card.position = faceDown ? CardPosition.DEFENSE : CardPosition.ATTACK;
    z.monsters[freeIdx] = card;
    this.markNormalSummonUsed(z);

    if (faceDown) {
      card.setOnTurn = this.state.absoluteTurnCount;
      this.emit({
        type: EventType.CARD_SET,
        seat,
        cardUid: card.uid,
        zone: 'monsters',
        zoneIndex: freeIdx,
        tribute: true,
      });
    } else {
      this.emit({
        type: EventType.CARD_SUMMONED,
        seat,
        cardUid: card.uid,
        cardId: card.cardId,
        zoneIndex: freeIdx,
        position: CardPosition.ATTACK,
        tribute: true,
      });
      this.pendingPuppeteerUid = card.uid;
      this.checkSummonTraps(seat, card);
    }
  }

  private changePosition(seat: Seat, uid: string): void {
    const z = this.state.zones[seat];
    const card = getMonsters(z).find((m) => m.uid === uid);
    if (!card) throw new EngineError('Monster not found', 'monsterNotFound');
    if (!card.faceUp) {
      this.flipSummon(seat, uid);
      return;
    }
    if (card.summonedThisTurn) throw new EngineError('Cannot change position the turn it was summoned', 'noPositionSummonTurn');
    if (card.positionChangedThisTurn) throw new EngineError('Position already changed this turn', 'positionAlreadyChanged');
    if (card.attackedThisTurn || card.declaredAttackThisTurn) {
      throw new EngineError('Monster already attacked', 'monsterAlreadyAttacked');
    }
    if (this.dragonLockActive() && getCardData(this.library, card.cardId).race === 'Dragon') {
      throw new EngineError('Dragons are locked in Defense Position (Dragon Capture Jar)', 'dragonCaptureJar');
    }

    const from = card.position;
    card.position = card.position === CardPosition.ATTACK ? CardPosition.DEFENSE : CardPosition.ATTACK;
    card.positionChangedThisTurn = true;
    this.emit({ type: EventType.POSITION_CHANGED, cardUid: card.uid, position: card.position });
    this.noteBattlePosition(card.uid, from);
  }

  private flipSummon(seat: Seat, uid: string, targetUid?: string): void {
    const z = this.state.zones[seat];
    const card = getMonsters(z).find((m) => m.uid === uid);
    if (!card) throw new EngineError('Monster not found', 'monsterNotFound');
    if (card.faceUp) throw new EngineError('Already face-up', 'alreadyFaceUp');
    if (card.summonedThisTurn) throw new EngineError('Cannot flip the turn it was set', 'noFlipSetTurn');

    const from = card.position;
    card.faceUp = true;
    card.position = CardPosition.ATTACK;
    card.positionChangedThisTurn = true;
    this.emit({ type: EventType.CARD_FLIPPED, cardUid: card.uid, cardId: card.cardId, faceUp: true });
    this.emit({ type: EventType.POSITION_CHANGED, cardUid: card.uid, position: card.position });
    this.noteBattlePosition(card.uid, from);
    this.pendingPuppeteerUid = card.uid;
    // The Flip Effect activates now. A human picks its target before Trap
    // Hole can answer. Destruction does not cancel that effect: flushSummonFlip
    // resolves it once the summon window closes, even from the Graveyard.
    this.summonFlipUid = card.uid;
    this.summonFlipTargetUid = targetUid;
    if (!this.openSummonFlipTarget(seat, card)) this.checkSummonTraps(seat, card, 'flip');
  }

  /**
   * Opens the Flip Effect target window before the summon-response window.
   * Returns true when the controller still has to pick. The AI picks later,
   * inside the effect, so its resolution stays synchronous.
   */
  private openSummonFlipTarget(seat: Seat, card: CardInstance): boolean {
    if (this.state.seats[seat].isAI) return false;
    const effects = engineEffects.getByTrigger(Number(card.cardId), EngineEffectTrigger.FLIP);
    const api = this.makeApi(seat, card);
    const name = getCardData(this.library, card.cardId).name;
    for (const effect of effects) {
      if (!effect.requiresTarget) continue;
      if (effect.canActivate && !effect.canActivate(api)) continue;
      const targets = this.enumerateFlipTargets(effect, api);
      if (targets.length === 0) continue;
      this.openFlipTargetWindow(seat, card, targets, name);
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Spells / traps
  // ---------------------------------------------------------------------------

  private setSpellTrap(seat: Seat, uid: string, zoneIndex: number): void {
    const z = this.state.zones[seat];
    const handIdx = z.hand.findIndex((c) => c.uid === uid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = z.hand[handIdx];
    const kind = kindOf(card, this.library);
    if (kind === CardKind.MONSTER) throw new EngineError('Not a spell/trap', 'notSpellTrap');

    const isFieldSpell = engineEffects.has(Number(card.cardId), EngineEffectTrigger.FIELD);
    if (!isFieldSpell) {
      const idx = z.spellTraps[zoneIndex] ? firstFreeIndex(z.spellTraps) : zoneIndex;
      if (idx === -1) throw new EngineError('No free spell/trap zone', 'noFreeSpellZone');
      z.hand.splice(handIdx, 1);
      card.faceUp = false;
      card.setOnTurn = this.state.absoluteTurnCount;
      z.spellTraps[idx] = card;
      this.emit({ type: EventType.CARD_SET, seat, cardUid: card.uid, zone: 'spellTraps', zoneIndex: idx });
      return;
    }

    // Field spells occupy the Field Zone, not a Spell/Trap slot. Setting a new
    // one replaces only this seat's field (the previous card goes to the GY);
    // the opponent's field stays until this one is activated.
    z.hand.splice(handIdx, 1);
    card.faceUp = false;
    card.setOnTurn = this.state.absoluteTurnCount;
    if (z.fieldSpell) this.sendToGraveyard(z.fieldSpell.uid);
    z.fieldSpell = card;
    this.emit({ type: EventType.CARD_SET, seat, cardUid: card.uid, zone: 'fieldSpell', zoneIndex: -1 });
  }

  private activateSpellFromHand(
    seat: Seat,
    uid: string,
    targetUids?: string[],
    position?: CardPosition,
  ): void {
    const z = this.state.zones[seat];
    const handIdx = z.hand.findIndex((c) => c.uid === uid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = z.hand[handIdx];
    if (kindOf(card, this.library) !== CardKind.SPELL) throw new EngineError('Not a spell', 'notSpell');

    // A spell needs somewhere to sit while it resolves: a field spell takes the field
    // zone, everything else a spell/trap zone. With the back row full the activation
    // is illegal — letting it through would leave the card in no zone at all, so it
    // would never reach the graveyard and could no longer be destroyed.
    const isFieldSpell = engineEffects.has(Number(card.cardId), EngineEffectTrigger.FIELD);
    const zoneIdx = isFieldSpell ? -1 : firstFreeIndex(z.spellTraps);
    if (!isFieldSpell && zoneIdx === -1) throw new EngineError('No free spell/trap zone', 'noFreeSpellZone');

    this.assertActivatable(seat, card, targetUids);

    z.hand.splice(handIdx, 1);
    card.faceUp = true;
    if (isFieldSpell) {
      // Only one field spell may be active on the whole board: activating a new one
      // sends every existing field spell (either player's) to the graveyard.
      this.clearActiveFieldSpells();
      z.fieldSpell = card;
      this.emit({ type: EventType.CARD_SET, seat, cardUid: card.uid, zone: 'fieldSpell', zoneIndex: -1 });
    } else {
      // Place into a spell/trap zone so removal logic is uniform.
      z.spellTraps[zoneIdx] = card;
    }
    this.emit({ type: EventType.SPELL_ACTIVATED, seat, cardUid: card.uid, cardId: card.cardId });
    const targets = this.fillAiCostTargets(seat, card, targetUids);
    if (this.shouldOpenTargetWindow(seat, card, targets)) {
      this.openActivationTargetWindow(seat, card, position);
      return;
    }
    this.commitActivationToChain(seat, card, targets, position);
  }

  private activateSetSpell(
    seat: Seat,
    uid: string,
    targetUids?: string[],
    position?: CardPosition,
  ): void {
    const z = this.state.zones[seat];
    const stIdx = z.spellTraps.findIndex((c) => c && c.uid === uid);
    let card: CardInstance | null = null;
    if (stIdx !== -1) card = z.spellTraps[stIdx];
    else if (z.fieldSpell && z.fieldSpell.uid === uid) card = z.fieldSpell;
    if (!card) throw new EngineError('Set spell not found', 'setSpellNotFound');
    if (kindOf(card, this.library) !== CardKind.SPELL) throw new EngineError('Not a spell', 'notSpell');
    this.assertActivatable(seat, card, targetUids);
    card.faceUp = true;
    // Activating a field (from the Field Zone or a leftover S/T set) claims the
    // only active field on the board: any other field spell is sent to the GY.
    if (engineEffects.has(Number(card.cardId), EngineEffectTrigger.FIELD)) {
      if (stIdx !== -1) z.spellTraps[stIdx] = null;
      this.clearOtherFieldSpells(card.uid);
      z.fieldSpell = card;
      if (stIdx !== -1) {
        this.emit({ type: EventType.CARD_SET, seat, cardUid: card.uid, zone: 'fieldSpell', zoneIndex: -1 });
      }
    }
    this.emit({ type: EventType.SPELL_ACTIVATED, seat, cardUid: card.uid, cardId: card.cardId });
    const targets = this.fillAiCostTargets(seat, card, targetUids);
    if (this.shouldOpenTargetWindow(seat, card, targets)) {
      this.openActivationTargetWindow(seat, card, position);
      return;
    }
    this.commitActivationToChain(seat, card, targets, position);
  }

  private activateSetTrap(seat: Seat, uid: string, targetUids?: string[]): void {
    const z = this.state.zones[seat];
    const card = z.spellTraps.find((c) => c && c.uid === uid) ?? null;
    if (!card) throw new EngineError('Set trap not found', 'setTrapNotFound');
    if (kindOf(card, this.library) !== CardKind.TRAP) throw new EngineError('Not a trap', 'notTrap');
    if (card.faceUp && Number(card.cardId) === STARTER_IDS.ULTIMATE_OFFERING) {
      this.igniteUltimateOffering(seat, card);
      return;
    }
    // A trap cannot be activated the same turn it was set (must have been set on a
    // previous turn). setOnTurn stores the absolute turn on which it was placed.
    if (card.setOnTurn === undefined || card.setOnTurn >= this.state.absoluteTurnCount) {
      throw new EngineError('A trap cannot be activated the turn it was set', 'trapSetDelay');
    }
    this.assertActivatable(seat, card, targetUids);
    if (!this.cardLegalInCurrentStep(card)) {
      throw new EngineError('This card cannot be activated in the Damage Step', 'noDamageStep');
    }
    card.faceUp = true;
    this.emit({ type: EventType.TRAP_ACTIVATED, seat, cardUid: card.uid, cardId: card.cardId });
    if (this.shouldOpenTargetWindow(seat, card, targetUids)) {
      this.openActivationTargetWindow(seat, card);
      return;
    }
    this.commitActivationToChain(seat, card, targetUids);
  }

  /**
   * Guards card activation (spell or trap): a card can only be activated if it has
   * an effect whose activation conditions are currently met. This prevents
   * activating cards that would do nothing (e.g. Stop Defense with no valid target,
   * Raigeki against an empty field, or cards with no implemented effect).
   */
  private assertActivatable(seat: Seat, card: CardInstance, targetUids?: string[]): void {
    const effects = this.activationEffects(Number(card.cardId));
    if (effects.length === 0) {
      throw new EngineError('This card cannot be activated right now', 'cannotActivateNow');
    }

    const api = this.makeApi(seat, card);
    // At least one effect must have its activation conditions met.
    const usable = effects.filter((e) => !e.canActivate || e.canActivate(api));
    if (usable.length === 0) {
      throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    }

    // Human players pick after the card is on the field. If nothing can be
    // chosen, refuse now — opening the window (or chaining with no target)
    // leaves the turn waiting and the duel cannot advance.
    // AI seats auto-pick inside execute() and never send targets.
    const isAI = this.state.seats[seat].isAI;
    if ((!targetUids || targetUids.length === 0) && this.mandatoryTargetMissing(api, usable)) {
      throw new EngineError('No legal target for this card', 'noLegalTarget');
    }
    for (const effect of usable) {
      if (!effect.requiresTarget || isAI) continue;
      if (!targetUids || targetUids.length === 0) continue;
      if (effect.canTargetSet) {
        if (!effect.canTargetSet(api, targetUids)) {
          throw new EngineError('Select valid targets for this card', 'selectValidTargets');
        }
        continue;
      }
      const target = targetUids[0];
      if (!target || (effect.canTarget && !effect.canTarget(api, target))) {
        throw new EngineError('Select a valid target for this card', 'selectValidTarget');
      }
    }
  }

  /**
   * Non-throwing counterpart of assertActivatable: whether the card has an effect
   * whose activation conditions are currently met (a valid target implicitly exists,
   * since each effect's canActivate already checks feasibility). Used to decide
   * whether to offer the "Activate" option in the UI.
   */
  private cardIsActivatable(seat: Seat, card: CardInstance): boolean {
    const effects = this.activationEffects(Number(card.cardId));
    if (effects.length === 0) return false;
    const api = this.makeApi(seat, card);
    const usable = effects.filter((e) => !e.canActivate || e.canActivate(api));
    if (usable.length === 0) return false;
    return !this.mandatoryTargetMissing(api, usable);
  }

  /**
   * True when every usable effect needs a target and none has enough legal
   * ones. A card in that state must not be offered or activated.
   */
  private mandatoryTargetMissing(api: EngineApi, usable: EngineEffect[]): boolean {
    if (usable.some((e) => !e.requiresTarget)) return false;
    const targeting = usable.filter((e) => e.requiresTarget);
    if (targeting.length === 0) return false;
    return !targeting.some((effect) => {
      const min = effect.minTargets ?? 1;
      if (min <= 0) return true;
      return this.enumerateActivationTargets(effect, api).length >= min;
    });
  }

  /**
   * Whether a prompt for this card would be useful right now. `always` mode
   * skips the gate so the player can still activate Waboku in Main Phase.
   */
  private cardIsRelevantNow(seat: Seat, card: CardInstance): boolean {
    if (this.state.responseMode[seat] === 'always') return true;
    const effects = this.activationEffects(Number(card.cardId));
    const gated = effects.filter((e) => e.relevantNow);
    if (gated.length === 0) return true;
    const api = this.makeApi(seat, card);
    return gated.some((e) => e.relevantNow!(api));
  }

  /**
   * The uids of the seat's spells/traps (in hand or set on the field) that may be
   * activated in the current phase/turn with their conditions met.
   */
  private computeActivatableUids(seat: Seat): string[] {
    const out: string[] = [];
    if (this.state.status !== 'active') return out;
    // No free activations while a response/discard window is open.
    if (
      this.state.pendingPriority ||
      this.state.pendingDiscard ||
      this.state.pendingTargets ||
      this.state.pendingChainOrder ||
      this.state.pendingChoice ||
      this.state.chain.length > 0
    ) {
      return out;
    }

    const z = this.state.zones[seat];
    const isTurn = this.state.turnPlayer === seat;
    const inMain =
      isTurn && (this.state.phase === EnginePhase.MAIN_1 || this.state.phase === EnginePhase.MAIN_2);

    if (inMain) {
      // A spell from the hand also needs a zone to occupy while it resolves, unless
      // it is a field spell (which uses the field zone instead).
      const backRowFull = firstFreeIndex(z.spellTraps) === -1;
      const thunderInDeck = z.deck.some((c) => Number(c.cardId) === THUNDER_DRAGON);
      for (const c of z.hand) {
        if (handIgnitionOf(c.cardId) === 'thunderDragon') {
          if (thunderInDeck) out.push(c.uid);
          continue;
        }
        if (kindOf(c, this.library) !== CardKind.SPELL) continue;
        const isFieldSpell = engineEffects.has(Number(c.cardId), EngineEffectTrigger.FIELD);
        if (backRowFull && !isFieldSpell) continue;
        if (this.cardIsActivatable(seat, c)) out.push(c.uid);
      }
    }
    const setCards = [...getSpellTraps(z)];
    if (z.fieldSpell) setCards.push(z.fieldSpell);
    for (const c of setCards) {
      if (c.faceUp) {
        if (
          Number(c.cardId) === STARTER_IDS.ULTIMATE_OFFERING &&
          kindOf(c, this.library) === CardKind.TRAP &&
          z.lifePoints >= 500 &&
          z.extraNormalSummons === 0 &&
          canIgniteUltimateOffering(
            this.state.turnPlayer,
            this.state.phase,
            seat,
            this.state.battleStep,
          ) &&
          canNormalOrTributeFromHand(this.state, seat, this.library)
        ) {
          out.push(c.uid);
        }
        continue;
      }
      const kind = kindOf(c, this.library);
      if (kind === CardKind.SPELL && inMain && this.cardIsActivatable(seat, c)) {
        out.push(c.uid);
      } else if (
        kind === CardKind.TRAP &&
        c.setOnTurn < this.state.absoluteTurnCount &&
        this.cardIsActivatable(seat, c) &&
        this.cardLegalInCurrentStep(c)
      ) {
        // 2002: traps are Spell Speed 2 — either player, any phase, after the Set turn.
        out.push(c.uid);
      }
    }
    return out;
  }

  private resolveActivation(
    seat: Seat,
    card: CardInstance,
    targetUids?: string[],
    position?: CardPosition,
    positions?: CardPosition[],
  ): void {
    this.chosenSummonPosition = position ?? CardPosition.ATTACK;
    this.chosenSummonPositions.clear();
    if (targetUids && positions) {
      const n = Math.min(targetUids.length, positions.length);
      for (let i = 0; i < n; i++) {
        const uid = targetUids[i];
        const pos = positions[i];
        if (!uid) continue;
        if (pos !== CardPosition.ATTACK && pos !== CardPosition.DEFENSE) continue;
        this.chosenSummonPositions.set(uid, pos);
      }
    }
    const cardId = Number(card.cardId);
    const effects = this.activationEffects(cardId);
    const api = this.makeApi(seat, card);
    try {
      for (const effect of effects) {
        if (effect.canActivate && !effect.canActivate(api)) continue;
        effect.execute(api, targetUids);
        this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: getCardData(this.library, card.cardId).name });
      }
      // A monster on the chain is a Flip Effect that destroys a known group
      // (Trap Master, Reaper of the Cards, Dragon Piper). The target was
      // chosen when the effect activated.
      if (kindOf(card, this.library) === CardKind.MONSTER) {
        for (const flip of engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP)) {
          if (flip.canActivate && !flip.canActivate(api)) continue;
          flip.execute(api, targetUids);
          this.emit({
            type: EventType.EFFECT_RESOLVED,
            cardUid: card.uid,
            detail: getCardData(this.library, card.cardId).name,
          });
        }
      }
    } finally {
      this.chosenSummonPosition = CardPosition.ATTACK;
      this.chosenSummonPositions.clear();
    }
    // One-shot spells go to the graveyard unless they are field/equip/continuous.
    // An Equip that resolved without a host must not keep the Spell/Trap slot.
    const stays =
      engineEffects.has(cardId, EngineEffectTrigger.FIELD) ||
      engineEffects.has(cardId, EngineEffectTrigger.EQUIP) ||
      engineEffects.has(cardId, EngineEffectTrigger.CONTINUOUS);
    const unattachedEquip =
      engineEffects.has(cardId, EngineEffectTrigger.EQUIP) && !card.equippedToUid;
    if (unattachedEquip || (!stays && kindOf(card, this.library) !== CardKind.MONSTER)) {
      this.sendToGraveyard(card.uid);
    }
    this.checkWin();
  }

  /**
   * Recomputes continuous field-spell ATK/DEF boosts across both fields. Field
   * modifiers are tagged with a `field:` source prefix so they can be cleared and
   * re-applied without touching equip/other modifiers. Runs after every action,
   * and again when an attack reveals a Set monster and just before damage
   * calculation: those steps often finish inside the same action, before this
   * end-of-action pass, so a terrain bonus would otherwise miss the battle.
   */
  private recomputeFieldSpells(): void {
    const monsters = [...getMonsters(this.state.zones[0]), ...getMonsters(this.state.zones[1])];
    const before = new Map<string, { atk: number; def: number }>();
    for (const m of monsters) {
      before.set(m.uid, {
        atk: getModifiedAtk(m, this.library, this.state.reverseStatMods),
        def: getModifiedDef(m, this.library, this.state.reverseStatMods),
      });
    }

    for (const m of monsters) {
      m.statModifiers = m.statModifiers.filter(
        (mod) => !mod.source.startsWith('field:') && !mod.source.startsWith('aura:'),
      );
    }

    for (const seat of [0, 1] as Seat[]) {
      const fs = this.state.zones[seat].fieldSpell;
      if (!fs || !fs.faceUp) continue;
      const cfg = getFieldSpellConfig(Number(fs.cardId));
      if (!cfg) continue;
      for (const m of monsters) {
        if (!m.faceUp) continue;
        const race = getCardData(this.library, m.cardId).race;
        if (!race) continue;
        // Terrains help some races and work against others (Umi hurts Machine and
        // Pyro, Yami hurts Fairy); no race appears in both lists.
        let sign = 0;
        if (cfg.boostedRaces.includes(race)) sign = 1;
        else if (cfg.penalizedRaces?.includes(race)) sign = -1;
        if (sign === 0) continue;
        m.statModifiers.push({
          atk: sign * cfg.atkBoost,
          def: sign * cfg.defBoost,
          source: `field:${fs.uid}`,
        });
      }
    }

    // Face-up attribute auras (Hoshiningen and the other five). They include
    // themselves. A Set copy does nothing until it is flipped face-up.
    for (const source of monsters) {
      if (!source.faceUp) continue;
      const aura = attributeAuraOf(Number(source.cardId));
      if (!aura) continue;
      for (const m of monsters) {
        if (!m.faceUp) continue;
        const delta = attributeAuraDelta(aura, getCardData(this.library, m.cardId).attribute);
        if (delta === 0) continue;
        m.statModifiers.push({ atk: delta, def: 0, source: `aura:${source.uid}` });
      }
    }

    // Muka Muka, Shadow Ghoul, and the two Battleguards. The partner, the
    // hand, and the Graveyard are the controller's, and a Set copy is off.
    for (const seat of [0, 1] as Seat[]) {
      const zones = this.state.zones[seat];
      const hand = zones.hand.length;
      const gyMonsters = zones.graveyard.filter((c) => kindOf(c, this.library) === CardKind.MONSTER).length;
      const owned = getMonsters(zones);
      const lava = owned.filter((m) => m.faceUp && Number(m.cardId) === LAVA_BATTLEGUARD).length;
      const swamp = owned.filter((m) => m.faceUp && Number(m.cardId) === SWAMP_BATTLEGUARD).length;
      for (const m of owned) {
        if (!m.faceUp) continue;
        const id = Number(m.cardId);
        if (id === MUKA_MUKA && hand > 0) {
          const bonus = hand * MUKA_PER_CARD;
          m.statModifiers.push({ atk: bonus, def: bonus, source: `aura:${m.uid}` });
        } else if (id === SHADOW_GHOUL && gyMonsters > 0) {
          m.statModifiers.push({
            atk: gyMonsters * GHOUL_PER_MONSTER,
            def: 0,
            source: `aura:${m.uid}`,
          });
        } else if (id === SWAMP_BATTLEGUARD && lava > 0) {
          m.statModifiers.push({
            atk: lava * BATTLEGUARD_PER_PARTNER,
            def: 0,
            source: `aura:${m.uid}`,
          });
        } else if (id === LAVA_BATTLEGUARD && swamp > 0) {
          m.statModifiers.push({
            atk: swamp * BATTLEGUARD_PER_PARTNER,
            def: 0,
            source: `aura:${m.uid}`,
          });
        } else if (id === PUMPKING && this.faceUpCastleOnField()) {
          m.statModifiers.push({ atk: PUMPKING_FLAT, def: PUMPKING_FLAT, source: `aura:${m.uid}` });
          if (m.turnCounter === undefined) m.turnCounter = PUMPKING_STANDBYS;
        }
      }
    }

    for (const m of monsters) {
      const prev = before.get(m.uid);
      const atk = getModifiedAtk(m, this.library, this.state.reverseStatMods);
      const def = getModifiedDef(m, this.library, this.state.reverseStatMods);
      if (prev && prev.atk === atk && prev.def === def) continue;
      this.emitStat(m);
    }
  }

  /** All effects that run when a spell/trap is activated (one-shot + persistent). */
  private activationEffects(cardId: number) {
    return [
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.ACTIVATE),
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.EQUIP),
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.FIELD),
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.CONTINUOUS),
    ];
  }

  /**
   * Mysterious Puppeteer (Expert Rules, 29 Jun 2002): a negated summon
   * never came into play, so the 500 LP do not happen. Trap Hole destroys
   * a summon that already succeeded, and the 500 still happen.
   */
  private flushPuppeteer(): void {
    const uid = this.pendingPuppeteerUid;
    if (!uid) return;
    if (
      this.resolvingChain ||
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingFlipEffect ||
      this.state.pendingDiscard ||
      this.state.chain.length > 0
    ) {
      return;
    }
    this.pendingPuppeteerUid = null;
    const negated = this.negatedSummonUids.has(uid);
    if (this.summonFlipUid !== uid) this.negatedSummonUids.delete(uid);
    if (negated) return;
    this.applyPuppeteer(uid);
  }

  /**
   * Resolves a Flip Summon effect once Trap Hole's window is gone.
   * Trap Hole does not negate: the effect still resolves if that trap
   * destroyed the monster, including from the Graveyard.
   */
  private flushSummonFlip(): void {
    const uid = this.summonFlipUid;
    if (!uid) return;
    if (
      this.resolvingChain ||
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingFlipEffect ||
      this.state.pendingDiscard ||
      this.state.chain.length > 0
    ) {
      return;
    }
    this.summonFlipUid = null;
    if (this.negatedSummonUids.has(uid)) {
      this.negatedSummonUids.delete(uid);
      this.summonFlipTargetUid = undefined;
      return;
    }
    const preset = this.summonFlipTargetUid;
    this.summonFlipTargetUid = undefined;
    const found = findCard(this.state, uid);
    if (!found) return;
    this.runFlipEffect(found.location.seat, found.instance, preset);
  }

  /**
   * A Flip Effect that names the cards it would destroy is Chain Link 1.
   * Fake Trap (Spell Speed 2) can chain when that group includes a Trap
   * the opponent controls. If the group destroys no Trap, the effect
   * resolves immediately, the same as any other Flip Effect.
   */
  private chainDestroyingFlip(
    seat: Seat,
    card: CardInstance,
    effects: EngineEffect[],
    presetTarget?: string,
  ): void {
    const effect = effects.find((e) => e.destroys);
    const name = getCardData(this.library, card.cardId).name;
    if (!effect?.destroys) return;
    const api = this.makeApi(seat, card);
    // The card was chosen when the effect activated, including a Flip Summon
    // that picks before Trap Hole's window. If that Trap chained and left,
    // do not ask for a different card. A Continuous Trap still on the field
    // stays in the legal list and is destroyed after it resolves.
    if (
      effect.requiresTarget &&
      presetTarget &&
      !this.enumerateFlipTargets(effect, api).includes(presetTarget)
    ) {
      this.message(
        'fizzled',
        { cardId: card.cardId },
        `${name} fizzled (its targets are no longer legal).`,
      );
      this.flushBattleGy();
      this.checkWin();
      return;
    }
    if (effect.canActivate && !effect.canActivate(api)) {
      this.message('flipNoTarget', { cardId: card.cardId }, `${name}: Flip Effect — no valid target`);
      return;
    }

    let targets: string[] | undefined;
    if (effect.requiresTarget) {
      const legal = this.enumerateFlipTargets(effect, api);
      if (legal.length === 0) {
        this.message('flipNoTarget', { cardId: card.cardId }, `${name}: Flip Effect — no valid target`);
        return;
      }
      const preset = presetTarget && legal.includes(presetTarget) ? presetTarget : undefined;
      if (!preset && !this.state.seats[seat].isAI) {
        this.openFlipTargetWindow(seat, card, legal, name);
        return;
      }
      const opp = otherSeat(seat);
      const target =
        preset ??
        legal.find((uid) => {
          const found = findCard(this.state, uid);
          return found?.location.seat === opp && kindOf(found.instance, this.library) === CardKind.TRAP;
        }) ??
        legal[0]!;
      targets = [target];
    }

    if (effect.destroys(api, targets).length === 0) {
      effect.execute(api, targets);
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
      this.message('flipActivated', { cardId: card.cardId }, `${name}: Flip Effect activated!`);
      this.flushBattleGy();
      this.checkWin();
      return;
    }

    // Damage calculation is already over. A Spell Speed 2 Trap such as
    // Fake Trap has to see this chain outside the Damage Step.
    if (this.state.battleStep === 'DAMAGE') this.state.battleStep = 'BATTLE';
    this.state.pendingAttack = null;
    this.message('flipActivated', { cardId: card.cardId }, `${name}: Flip Effect activated!`);
    this.pushChainLink(seat, card, targets);
    this.advanceChain();
  }

  private runFlipEffect(seat: Seat, card: CardInstance, presetTarget?: string): void {
    const cardId = Number(card.cardId);
    const effects = engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP);
    if (effects.length === 0) return;
    if (effects.some((e) => e.destroys)) {
      this.chainDestroyingFlip(seat, card, effects, presetTarget);
      return;
    }
    const name = getCardData(this.library, card.cardId).name;
    const api = this.makeApi(seat, card);
    const isAI = this.state.seats[seat].isAI;
    let resolvedAny = false;
    for (const effect of effects) {
      if (presetTarget !== undefined && effect.requiresTarget) {
        if (effect.canTarget && effect.canTarget(api, presetTarget)) {
          effect.execute(api, [presetTarget]);
          resolvedAny = true;
          this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
        }
        continue;
      }
      if (effect.canActivate && !effect.canActivate(api)) continue;
      // Effects that need a target let a human controller choose it (Set cards are
      // hidden information). The turn pauses until RESOLVE_FLIP_TARGET arrives; the
      // AI auto-picks so its resolution stays synchronous.
      // A second flip effect in the same resolution (Swords of Revealing Light can
      // flip two of them at once) cannot open a window without discarding the one
      // already open, so it auto-picks instead of being silently lost.
      if (effect.requiresTarget && isAI) {
        const targets = this.enumerateFlipTargets(effect, api);
        const best =
          Number(card.cardId) === 54652250 || Number(card.cardId) === 7089711
            ? this.bestMonsterFlipTarget(seat, targets)
            : this.bestAiFlipTarget(targets);
        if (best && (!effect.canTarget || effect.canTarget(api, best))) {
          effect.execute(api, [best]);
          resolvedAny = true;
          this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
        }
        continue;
      }
      if (effect.requiresTarget && !isAI && !this.state.pendingFlipEffect) {
        const targets = this.enumerateFlipTargets(effect, api);
        if (targets.length > 0) {
          this.openFlipTargetWindow(seat, card, targets, name);
          return;
        }
        continue; // no legal target: nothing happens
      }
      effect.execute(api);
      if (this.state.pendingTargets?.reorderDeckTop) return;
      resolvedAny = true;
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    }
    // Always surface what happened: a flip effect that finds no legal target does
    // nothing by the rules, but silent no-ops read like a bug to the player.
    this.message(
      resolvedAny ? 'flipActivated' : 'flipNoTarget',
      { cardId: card.cardId },
      resolvedAny ? `${name}: Flip Effect activated!` : `${name}: Flip Effect — no valid target`,
    );
    this.checkWin();
  }

  /** Every on-field card the given effect considers a legal target. */
  private enumerateFlipTargets(effect: EngineEffect, api: EngineApi): string[] {
    if (!effect.canTarget) return [];
    const uids: string[] = [];
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat];
      for (const c of [...z.monsters, ...z.spellTraps, z.fieldSpell, ...z.graveyard]) {
        if (c && effect.canTarget(api, c.uid)) uids.push(c.uid);
      }
    }
    return uids;
  }

  /**
   * A flip window can list a card that leaves before the chooser acts. Stop
   * Defense is the usual case: it is a face-up Spell when Armed Ninja looks
   * for a target, then it goes to the Graveyard. If every listed uid is no
   * longer a legal target, waiting for a click softlocks that player until
   * the clock forfeits them.
   *
   * Graveyard picks stay. Magician of Faith and Mask of Darkness list cards
   * that were never on the field. Treating "not on the field" as "already
   * gone" closed that window in the same action that opened it, so the Flip
   * Effect resolved as if there were no target and a battle-destroyed monster
   * never left the field.
   */
  private dropStaleFlipWindow(): void {
    const pending = this.state.pendingFlipEffect;
    if (!pending) return;
    const live = pending.targetUids.filter((uid) => this.flipTargetStillLegal(pending, uid));
    if (live.length === pending.targetUids.length) return;
    if (live.length > 0) {
      pending.targetUids = live;
      return;
    }
    this.state.pendingFlipEffect = null;
    const name = getCardData(this.library, pending.cardId).name;
    this.message(
      'flipNoTarget',
      { cardId: pending.cardId },
      `${name}: Flip Effect — no valid target`,
    );
    // The battle already queued this monster. The window was the only reason
    // flushBattleGy waited. With nothing left to choose, send it now.
    this.flushBattleGy();
  }

  /**
   * Still a card this Flip Effect can choose. A Graveyard card stays while
   * it remains a legal target. A field card that already left does not.
   */
  private flipTargetStillLegal(pending: PendingFlipEffect, uid: string): boolean {
    const found = findCard(this.state, uid);
    if (!found) return false;
    const source = findCard(this.state, pending.sourceUid)?.instance;
    const effect = engineEffects
      .getByTrigger(Number(pending.cardId), EngineEffectTrigger.FLIP)
      .find((entry) => entry.requiresTarget && entry.canTarget);
    if (!source || !effect?.canTarget) return this.flipTargetOnField(uid);
    return effect.canTarget(this.makeApi(pending.chooser, source), uid);
  }

  /** True when this uid is still a card on the board the player can click. */
  private flipTargetOnField(uid: string): boolean {
    const found = findCard(this.state, uid);
    if (!found) return false;
    const zone = found.location.zone;
    return zone === 'monsters' || zone === 'spellTraps' || zone === 'fieldSpell';
  }

  /** Opens a Flip-Effect target selection window for the flipped monster's controller. */
  private openFlipTargetWindow(
    chooser: Seat,
    source: CardInstance,
    targetUids: string[],
    name: string,
  ): void {
    this.priorityCounter += 1;
    this.state.pendingFlipEffect = {
      requestId: `flip-${this.priorityCounter}`,
      chooser,
      sourceUid: source.uid,
      cardId: source.cardId,
      prompt: `${name}: choose a target`,
      targetUids,
    };
  }

  /** Resolves a Flip Effect once the chooser has picked a target. */
  private resolveFlipTarget(seat: Seat, requestId: string, targetUid: string): void {
    const pending = this.state.pendingFlipEffect;
    if (!pending || pending.requestId !== requestId) throw new EngineError('No flip selection pending', 'noFlipPending');
    if (seat !== pending.chooser) throw new EngineError('Not your selection', 'notYourSelection');
    if (!pending.targetUids.includes(targetUid)) throw new EngineError('Invalid target', 'invalidTarget');

    this.emitTargetsChosen(seat, [targetUid], pending.sourceUid, pending.cardId);

    const cardId = Number(pending.cardId);
    const name = getCardData(this.library, pending.cardId).name;
    const source =
      findCard(this.state, pending.sourceUid)?.instance ??
      ({ uid: pending.sourceUid, cardId: pending.cardId } as CardInstance);
    this.state.pendingFlipEffect = null;

    // Flip Summon: the pick is the activation. Trap Hole still gets its
    // window, and flushSummonFlip resolves this choice afterwards.
    if (this.summonFlipUid === pending.sourceUid) {
      this.summonFlipTargetUid = targetUid;
      const found = findCard(this.state, pending.sourceUid);
      if (found?.location.zone === 'monsters' && found.instance.faceUp) {
        this.checkSummonTraps(found.location.seat, found.instance, 'flip');
      }
      return;
    }

    const flipEffects = engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP);
    if (flipEffects.some((e) => e.destroys)) {
      this.chainDestroyingFlip(pending.chooser, source, flipEffects, targetUid);
      return;
    }

    const api = this.makeApi(pending.chooser, source);
    const effect =
      engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP).find((e) => e.requiresTarget) ?? null;
    if (effect) {
      effect.execute(api, [targetUid]);
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: pending.sourceUid, detail: name });
      this.message('flipActivated', { cardId: pending.cardId }, `${name}: Flip Effect activated!`);
    }
    this.flushBattleGy();
    this.checkWin();
  }

  /**
   * Human targeting spells/traps land first, then this window opens. AI (and
   * any activation that already carried targets) resolves immediately.
   */
  private shouldOpenTargetWindow(
    seat: Seat,
    card: CardInstance,
    targetUids?: string[],
  ): boolean {
    if (this.state.seats[seat].isAI) return false;
    // A monster sent with the activation is the destroy target, not the
    // discard. Tribute to the Doomed still pays that cost first.
    if (Number(card.cardId) === TRIBUTE_TO_THE_DOOMED && !this.doomedPaid.has(card.uid)) {
      return true;
    }
    if (targetUids && targetUids.length > 0) return false;
    const api = this.makeApi(seat, card);
    return this.activationEffects(Number(card.cardId)).some(
      (e) => e.requiresTarget && (!e.canActivate || e.canActivate(api)),
    );
  }

  /** Every uid `effect.canTarget` currently accepts, anywhere on the board. */
  private enumerateActivationTargets(effect: EngineEffect, api: EngineApi): string[] {
    if (!effect.canTarget) return [];
    const uids: string[] = [];
    const seen = new Set<string>();
    const consider = (c: CardInstance | null | undefined): void => {
      if (!c || seen.has(c.uid)) return;
      if (!effect.canTarget!(api, c.uid)) return;
      seen.add(c.uid);
      uids.push(c.uid);
    };
    for (const s of [0, 1] as Seat[]) {
      const z = this.state.zones[s];
      for (const c of z.monsters) consider(c);
      for (const c of z.spellTraps) consider(c);
      consider(z.fieldSpell);
      for (const c of z.graveyard) consider(c);
      for (const c of z.banished) consider(c);
      for (const c of z.extraDeck) consider(c);
      for (const c of z.hand) consider(c);
      for (const c of z.deck) consider(c);
    }
    return uids;
  }

  private openActivationTargetWindow(
    seat: Seat,
    card: CardInstance,
    position?: CardPosition,
  ): void {
    if (Number(card.cardId) === TRIBUTE_TO_THE_DOOMED && !this.doomedPaid.has(card.uid)) {
      const hand = this.state.zones[seat].hand;
      if (hand.length === 0) {
        this.fizzleUnresolvedActivation(card);
        return;
      }
      this.openPickWindow(seat, card, hand.map((c) => c.uid), 1, 1, { discardCost: true });
      return;
    }
    const api = this.makeApi(seat, card);
    const effect = this.activationEffects(Number(card.cardId)).find(
      (e) => e.requiresTarget && (!e.canActivate || e.canActivate(api)),
    );
    if (!effect) {
      this.commitActivationToChain(seat, card, undefined, position);
      return;
    }
    const candidates = this.enumerateActivationTargets(effect, api);
    const min = effect.minTargets ?? 1;
    const max = effect.maxTargets ?? 1;
    // A mandatory target that no longer exists must not open a window or
    // join the chain. Either one pauses the attack until a pick that can
    // never arrive.
    if (min > 0 && candidates.length < min) {
      this.fizzleUnresolvedActivation(card);
      return;
    }
    const name = getCardData(this.library, card.cardId).name;
    this.priorityCounter += 1;
    const prompt = `${name}: choose a target`;
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: card.uid,
      cardId: card.cardId,
      prompt,
      candidateUids: candidates,
      min,
      max,
      ...(position !== undefined ? { position } : {}),
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt,
      candidateUids: candidates,
      min,
      max,
      sourceUid: card.uid,
      sourceCardId: card.cardId,
    });
  }

  /**
   * The card is already face-up and its effect cannot choose a target.
   * Send it to the graveyard (an equip that never attached must not keep
   * the backrow slot) and let the caller resume the turn.
   */
  private fizzleUnresolvedActivation(card: CardInstance): void {
    this.doomedPaid.delete(card.uid);
    const name = getCardData(this.library, card.cardId).name;
    this.message(
      'noLegalTarget',
      { cardId: card.cardId },
      `${name}: no legal target.`,
    );
    this.sendToGraveyard(card.uid);
  }

  /** Resolves a paused activation once the chooser has picked. */
  private selectActivationTargets(
    seat: Seat,
    requestId: string,
    targetUids: string[],
    position?: CardPosition,
    positions?: CardPosition[],
    abandon = false,
  ): void {
    const pending = this.state.pendingTargets;
    if (!pending || pending.requestId !== requestId) {
      throw new EngineError('No target selection pending', 'noTargetPending');
    }
    if (seat !== pending.chooser) throw new EngineError('Not your selection', 'notYourSelection');
    if (abandon) {
      if (pending.min > 0 && pending.candidateUids.length === 0) {
        this.state.pendingTargets = null;
        const found = findCard(this.state, pending.sourceUid);
        if (found) this.fizzleUnresolvedActivation(found.instance);
        return;
      }
      throw new EngineError('Select a valid target for this card', 'selectValidTarget');
    }
    if (targetUids.length < pending.min || targetUids.length > pending.max) {
      throw new EngineError('Select valid targets for this card', 'selectValidTargets');
    }
    if (new Set(targetUids).size !== targetUids.length) {
      throw new EngineError('Select valid targets for this card', 'selectValidTargets');
    }
    if (!targetUids.every((uid) => pending.candidateUids.includes(uid))) {
      throw new EngineError('Invalid target', 'invalidTarget');
    }

    if (pending.reorderDeckTop) {
      this.state.pendingTargets = null;
      this.placeDeckTop(pending.chooser, targetUids);
      const name = getCardData(this.library, pending.cardId).name;
      this.emit({
        type: EventType.EFFECT_RESOLVED,
        cardUid: pending.sourceUid,
        detail: name,
      });
      this.message(
        'bigEye',
        { cardId: pending.cardId, n: targetUids.length },
        `${name}: ${targetUids.length} card(s) returned to the top of the Deck.`,
      );
      this.checkWin();
      return;
    }

    if (pending.toDeckBottom) {
      this.state.pendingTargets = null;
      const placed = this.placeHandCardsUnderDeck(pending.chooser, targetUids);
      if (placed > 0) {
        this.emit({
          type: EventType.EFFECT_RESOLVED,
          cardUid: pending.sourceUid,
          detail: getCardData(this.library, pending.cardId).name,
        });
        this.message(
          'yadoKaru',
          { cardId: pending.cardId, n: placed },
          `Placed ${placed} card(s) on the bottom of the Deck.`,
        );
      } else {
        // Declining is legal (min is 0). emitTargetsChosen skips an empty
        // list, and a batch with no events never reaches the client. The
        // list was already closed, so the turn stayed on "choose a target"
        // with no way to pass.
        this.emit({
          type: EventType.TARGETS_CHOSEN,
          seat: pending.chooser,
          targetUids: [],
          sourceUid: pending.sourceUid,
          sourceCardId: pending.cardId,
        });
      }
      return;
    }

    if (pending.returnChosenToHand) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      if (uid) this.returnToHand(uid);
      this.message(
        'crassClown',
        { cardId: pending.cardId },
        'A monster returned to the hand.',
      );
      this.checkWin();
      return;
    }

    if (pending.barrelTarget) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      const source = findCard(this.state, pending.sourceUid)?.instance;
      if (uid && source) {
        this.emitTargetsChosen(
          pending.chooser,
          [uid],
          pending.sourceUid,
          pending.cardId,
        );
        this.resolveBarrelDragon(pending.chooser, source, uid);
      }
      return;
    }

    if (pending.destroyChosenMonster) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      if (uid) {
        const destroyed = this.destroyCard(uid);
        if (destroyed && Number(pending.cardId) === DREAM_CLOWN) {
          this.message(
            'dreamClown',
            { cardId: pending.cardId },
            'A monster was destroyed.',
          );
        }
      }
      this.checkWin();
      return;
    }

    if (pending.discardCost) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      if (uid) this.sendToGraveyard(uid);
      this.doomedPaid.add(pending.sourceUid);
      const spell = findCard(this.state, pending.sourceUid)?.instance;
      if (!spell) {
        this.doomedPaid.delete(pending.sourceUid);
        return;
      }
      this.openActivationTargetWindow(pending.chooser, spell, position ?? pending.position);
      return;
    }

    if (pending.tributeIgnition) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      const source = findCard(this.state, pending.sourceUid)?.instance;
      if (uid && source) this.finishTributeIgnition(pending.chooser, source, pending.tributeIgnition, uid);
      return;
    }

    if (pending.blastDestroy) {
      this.state.pendingTargets = null;
      const source = findCard(this.state, pending.sourceUid)?.instance;
      if (source) this.resolveBlast(source, targetUids);
      this.openNextChoice();
      return;
    }

    if (pending.opponentTribute) {
      this.state.pendingTargets = null;
      const uid = targetUids[0];
      if (uid) this.sendToGraveyard(uid);
      this.message(
        'sharePain',
        { cardId: pending.cardId },
        'Both players Tributed 1 monster.',
      );
      this.checkWin();
      this.resumeResolvingChain();
      return;
    }

    if (pending.fissureDestroy) {
      this.state.pendingTargets = null;
      const spell = findCard(this.state, pending.sourceUid)?.instance;
      const uid = targetUids[0];
      if (spell && uid) {
        const tied = fissureMinGroup(this.makeApi(pending.chooser, spell));
        if (tied.some((monster) => monster.uid === uid)) this.destroyCard(uid);
        this.emit({
          type: EventType.EFFECT_RESOLVED,
          cardUid: spell.uid,
          detail: getCardData(this.library, spell.cardId).name,
        });
        this.sendOneShotToGraveyard(spell);
      }
      this.checkWin();
      this.resumeResolvingChain();
      return;
    }

    if (pending.specialSummonNamed) {
      this.state.pendingTargets = null;
      const spell = findCard(this.state, pending.sourceUid)?.instance;
      const uid = targetUids[0];
      const monster = uid ? findCard(this.state, uid)?.instance : undefined;
      if (spell && monster) {
        this.finishEgotist(
          pending.chooser,
          spell,
          monster,
          position ?? pending.position ?? CardPosition.ATTACK,
        );
      } else if (spell) {
        this.sendOneShotToGraveyard(spell);
      }
      this.checkWin();
      this.resumeResolvingChain();
      return;
    }

    if (pending.specialSummonFromDeck) {
      this.state.pendingTargets = null;
      const ssUid = targetUids[0];
      const z = this.state.zones[pending.chooser];
      if (!ssUid) {
        // Declining is a legal answer (min is 0). emitTargetsChosen skips an
        // empty list, and a batch with no events never reaches the client.
        // The list is already closed, so the board stayed on "choose a
        // highlighted card" with nothing highlighted and no way to pass.
        // The charge remains; the next phase asks again.
        this.emit({
          type: EventType.TARGETS_CHOSEN,
          seat: pending.chooser,
          targetUids: [],
          sourceUid: pending.sourceUid,
          sourceCardId: pending.cardId,
        });
        this.checkWin();
        return;
      }
      this.emitTargetsChosen(pending.chooser, targetUids, pending.sourceUid, pending.cardId);
      if (this.hasFreeMonsterZone(pending.chooser)) {
        const raw = position ?? pending.position ?? CardPosition.ATTACK;
        this.specialSummon(
          ssUid,
          pending.chooser,
          raw === CardPosition.DEFENSE ? CardPosition.DEFENSE : CardPosition.ATTACK,
        );
        this.shuffleDeck(pending.chooser);
        if (z.lastWillCharges > 0) z.lastWillCharges -= 1;
        if (z.lastWillCharges > 0 && z.lastWillReady) z.lastWillAsk = true;
      } else {
        z.lastWillAsk = z.lastWillCharges > 0 && z.lastWillReady;
      }
      this.checkWin();
      return;
    }

    if (pending.addToHandFromDeck) {
      this.state.pendingTargets = null;
      const added = this.takeDeckCardsToHand(pending.chooser, targetUids);
      if (added > 0) {
        const thunder = Number(pending.cardId) === THUNDER_DRAGON;
        this.message(
          thunder ? 'thunderAdded' : 'searched',
          { cardId: pending.cardId, n: added },
          thunder
            ? `Added ${added} from the Deck.`
            : 'A monster was added from the Deck.',
        );
      }
      // Sangan and Witch already destello'd when the chain link was placed.
      // Thunder Dragon is not on that chain, so this is its only flash.
      if (!this.resolvingGraveChain) {
        this.emit({
          type: EventType.EFFECT_RESOLVED,
          cardUid: pending.sourceUid,
          detail: getCardData(this.library, pending.cardId).name,
        });
      }
      this.checkWin();
      this.resumeResolvingChain();
      return;
    }

    const found = findCard(this.state, pending.sourceUid);
    const card =
      found?.instance ??
      ({ uid: pending.sourceUid, cardId: pending.cardId } as CardInstance);
    const api = this.makeApi(pending.chooser, card);
    const effect = this.activationEffects(Number(pending.cardId)).find(
      (e) => e.requiresTarget && (!e.canActivate || e.canActivate(api)),
    );
    if (effect?.canTargetSet && !effect.canTargetSet(api, targetUids)) {
      throw new EngineError('Select valid targets for this card', 'selectValidTargets');
    }
    if (effect?.canTarget && !effect.canTargetSet) {
      for (const uid of targetUids) {
        if (!effect.canTarget(api, uid)) throw new EngineError('Invalid target', 'invalidTarget');
      }
    }

    this.state.pendingTargets = null;
    this.commitActivationToChain(
      pending.chooser,
      card,
      targetUids,
      position ?? pending.position,
      positions,
    );
  }

  // ---------------------------------------------------------------------------
  // Battle
  // ---------------------------------------------------------------------------

  private declareAttack(seat: Seat, attackerUid: string, targetUid: string): void {
    const attacker = getMonsters(this.state.zones[seat]).find((m) => m.uid === attackerUid);
    if (!attacker) throw new EngineError('Attacker not found', 'attackerNotFound');
    this.validateAttacker(attacker);

    const onField = getMonsters(this.state.zones[otherSeat(seat)]).some((m) => m.uid === targetUid);
    if (!onField) throw new EngineError('Target not found', 'targetNotFound');
    const forced = magnetismTargets(this.state, otherSeat(seat));
    if (forced.length > 0 && !forced.some((m) => m.uid === targetUid)) {
      throw new EngineError(
        'You must attack the monster equipped with Ring of Magnetism',
        'ringOfMagnetism',
      );
    }

    this.beginAttack(seat, attacker, targetUid);
  }

  /**
   * Declares an attack and opens the Battle Step window. Damage waits until
   * both players pass (or the chain resolves) and the Damage Step window closes.
   */
  private beginAttack(seat: Seat, attacker: CardInstance, targetUid: string | null): void {
    if (!this.payDarkElf(seat, attacker)) return;
    const opp = otherSeat(seat);
    this.missionStats[seat].attacks += 1;
    // Consumed at declaration: an attack answered by a trap is spent even if the
    // battle ends up cancelled, so a cancelled attack cannot be retried for free.
    // A Replay clears attackedThisTurn so the monster may declare again later
    // in this Battle Phase. declaredAttackThisTurn stays for Main Phase 2.
    attacker.attackedThisTurn = true;
    attacker.declaredAttackThisTurn = true;
    this.state.battleStep = 'BATTLE';
    this.state.chainPasses = 0;
    this.state.pendingAttack = {
      seat,
      attackerUid: attacker.uid,
      targetUid,
      stage: 'declared',
      defenderCount: getMonsters(this.state.zones[opp]).length,
    };
    // Jirai Gumo calls the coin before the attack is shown and before traps.
    if (declaresAttackWithCoin(attacker.cardId)) {
      if (this.state.seats[seat].isAI) this.resolveJirai(seat, attacker, 'heads');
      else this.openCoinCall(seat, attacker, 'jirai');
      return;
    }
    // Face-up WIND is already public, so the +1000 can play before the lunge.
    // A Set defender stays hidden until the Damage Step.
    this.previewInsectSoldiers(seat, attacker, targetUid);
    this.emit({ type: EventType.ATTACK_DECLARED, attackerUid: attacker.uid, targetUid });
    this.openResponseWindow(opp);
  }

  /** True when Insect Soldiers of the Sky would gain 1000 ATK against this defender. */
  private insectSoldiersHitsWind(attacker: CardInstance, target: CardInstance): boolean {
    if (Number(attacker.cardId) !== INSECT_SOLDIERS) return false;
    if (!target.faceUp) return false;
    const attr = (getCardData(this.library, target.cardId).attribute ?? '').toUpperCase();
    return attr === 'WIND';
  }

  /**
   * Shows the Damage Step ATK before the attack animation. The modifier itself
   * is still applied only at damage calculation.
   */
  private previewInsectSoldiers(seat: Seat, attacker: CardInstance, targetUid: string | null): void {
    if (!targetUid) return;
    const target = getMonsters(this.state.zones[otherSeat(seat)]).find((m) => m.uid === targetUid);
    if (!target || !this.insectSoldiersHitsWind(attacker, target)) return;
    this.emit({
      type: EventType.STAT_CHANGED,
      cardUid: attacker.uid,
      atk: getModifiedAtk(attacker, this.library, this.state.reverseStatMods) + INSECT_SOLDIERS_BOOST,
      def: getModifiedDef(attacker, this.library, this.state.reverseStatMods),
    });
  }

  private tossCoin(): CoinSide {
    const roll = nextRandom(this.state.rngState);
    this.state.rngState = roll.state;
    return roll.value < 0.5 ? 'heads' : 'tails';
  }

  private emitCoin(
    seat: Seat,
    card: CardInstance,
    call: CoinSide | null,
    results: CoinSide[],
  ): void {
    this.emit({
      type: EventType.COIN_TOSSED,
      seat,
      cardUid: card.uid,
      cardId: card.cardId,
      call,
      results,
    });
  }

  private openCoinCall(
    seat: Seat,
    card: CardInstance,
    kind: 'timeWizard' | 'jirai',
  ): void {
    this.priorityCounter += 1;
    const name = getCardData(this.library, card.cardId).name;
    this.state.pendingCoinCall = {
      requestId: `coin-${this.priorityCounter}`,
      chooser: seat,
      cardUid: card.uid,
      cardId: card.cardId,
      kind,
    };
    this.message('coinCall', { cardId: card.cardId }, `${name}: call Heads or Tails.`);
  }

  private openPickWindow(
    seat: Seat,
    card: CardInstance,
    candidateUids: string[],
    min: number,
    max: number,
    extra: {
      tributeIgnition?: TributeIgnition;
      blastDestroy?: boolean;
      discardCost?: boolean;
      opponentTribute?: boolean;
      barrelTarget?: boolean;
    },
  ): void {
    this.priorityCounter += 1;
    const name = getCardData(this.library, card.cardId).name;
    const prompt = `${name}: choose`;
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: card.uid,
      cardId: card.cardId,
      prompt,
      candidateUids,
      min,
      max,
      ...extra,
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt,
      candidateUids,
      min,
      max,
      sourceUid: card.uid,
      sourceCardId: card.cardId,
    });
  }

  /**
   * The AI never opens a target window. These three spells still need a
   * discard or a Tribute before the chain link is built.
   */
  private fillAiCostTargets(
    seat: Seat,
    card: CardInstance,
    targetUids?: string[],
  ): string[] | undefined {
    if (!this.state.seats[seat].isAI) return targetUids;
    const id = Number(card.cardId);
    if (id === TRIBUTE_TO_THE_DOOMED) {
      if (!this.doomedPaid.has(card.uid)) {
        const discardUid = aiLowValueDiscard(this.state, this.library, seat);
        const discard = discardUid
          ? this.state.zones[seat].hand.find((c) => c.uid === discardUid)
          : undefined;
        if (!discard) return [];
        this.sendToGraveyard(discard.uid);
        this.doomedPaid.add(card.uid);
      }
      if (targetUids && targetUids.length > 0) return targetUids;
      const victim = this.bestDoomedVictim(seat);
      return victim ? [victim] : [];
    }
    if (targetUids && targetUids.length > 0) return targetUids;
    if (id === SHARE_THE_PAIN) {
      const uid = aiPreferRecruiterTribute(this.state, this.library, seat);
      return uid ? [uid] : [];
    }
    if (id === CHEERFUL_COFFIN) return this.coffinDiscards(seat);
    return targetUids;
  }

  private doomedVictimUids(): string[] {
    const out: string[] = [];
    for (const seat of [0, 1] as Seat[]) {
      for (const monster of getMonsters(this.state.zones[seat])) {
        if (this.lordOfDBlocksTarget(monster.uid)) continue;
        out.push(monster.uid);
      }
    }
    return out;
  }

  private atkOfUid(uid: string): number {
    const card = findCard(this.state, uid)?.instance;
    if (!card) return 0;
    return getModifiedAtk(card, this.library, this.state.reverseStatMods);
  }

  private weakestOf(uids: string[]): string | null {
    let best: string | null = null;
    let bestAtk = Infinity;
    for (const uid of uids) {
      const atk = this.atkOfUid(uid);
      if (atk < bestAtk) {
        bestAtk = atk;
        best = uid;
      }
    }
    return best;
  }

  private strongestOf(uids: string[]): string | null {
    let best: string | null = null;
    let bestAtk = -1;
    for (const uid of uids) {
      const atk = this.atkOfUid(uid);
      if (atk > bestAtk) {
        bestAtk = atk;
        best = uid;
      }
    }
    return best;
  }

  /**
   * Cannon Soldier tributes a recruiter, then a monster that would return
   * to the opponent, then a Soul Exchange selection, then the weakest
   * other body.
   */
  private cannonTribute(uids: string[], sourceUid: string): string | null {
    const others = uids.filter((uid) => uid !== sourceUid);
    const recruiter = others.find((uid) => {
      const id = Number(findCard(this.state, uid)?.instance.cardId);
      return id === 26202165 || id === 78010363;
    });
    if (recruiter) return recruiter;
    const borrowed = others.find((uid) => {
      const card = findCard(this.state, uid)?.instance;
      return card?.controlReturnTo !== undefined;
    });
    if (borrowed) return borrowed;
    const exchanged = this.strongestExchanged(others, sourceUid);
    if (exchanged) return exchanged;
    return this.weakestOf(others) ?? (uids.includes(sourceUid) ? sourceUid : null);
  }

  /** Soul Exchange tributes sit on the opponent's side. Prefer the strongest. */
  private strongestExchanged(uids: string[], sourceUid: string): string | null {
    const home = findCard(this.state, sourceUid)?.location.seat;
    const exchanged = uids.filter((uid) => {
      const found = findCard(this.state, uid);
      return found != null && found.location.seat !== home;
    });
    return exchanged.length > 0 ? this.strongestOf(exchanged) : null;
  }

  /** Prefer the opponent's strongest monster. */
  private bestDoomedVictim(seat: Seat): string | null {
    const theirs: string[] = [];
    const ours: string[] = [];
    for (const uid of this.doomedVictimUids()) {
      const found = findCard(this.state, uid);
      if (!found) continue;
      if (found.location.seat === seat) ours.push(uid);
      else theirs.push(uid);
    }
    const pool = theirs.length > 0 ? theirs : ours;
    return this.strongestOf(pool);
  }

  /** Shadow Ghoul fuel: junk monsters, never a piece or a recruiter. */
  private coffinDiscards(seat: Seat): string[] {
    const keep = new Set(['26202165', '78010363', '40640057', '31560081']);
    const junk = this.state.zones[seat].hand.filter((card) => {
      if (kindOf(card, this.library) !== CardKind.MONSTER) return false;
      if (EXODIA_PIECE_SET.has(card.cardId) || keep.has(card.cardId)) return false;
      const atk = getCardData(this.library, card.cardId).atk ?? 0;
      return atk <= 1000;
    });
    return junk.slice(0, 3).map((card) => card.uid);
  }

  private igniteTribute(seat: Seat, card: CardInstance, kind: TributeIgnition): void {
    const targets = tributeCandidateUids(this.state, this.library, seat, card.uid, kind);
    if (targets.length === 0) {
      throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    }
    if (this.state.seats[seat].isAI || targets.length === 1) {
      const uid =
        kind === 'turtle'
          ? this.strongestOf(targets)
          : kind === 'cannon'
            ? this.cannonTribute(targets, card.uid)
            : this.strongestExchanged(targets, card.uid) ??
              this.weakestOf(targets.filter((id) => id !== card.uid)) ??
              targets[0];
      if (uid) this.finishTributeIgnition(seat, card, kind, uid);
      return;
    }
    this.openPickWindow(seat, card, targets, 1, 1, { tributeIgnition: kind });
  }

  /**
   * Tribute, then the printed effect. Cannon and Turtle may Tribute
   * themselves. Aile's 700 ATK lasts until the End Phase.
   */
  private finishTributeIgnition(
    seat: Seat,
    card: CardInstance,
    kind: TributeIgnition,
    uid: string,
  ): void {
    const victim = findCard(this.state, uid)?.instance;
    if (!victim) return;
    const atk = getModifiedAtk(victim, this.library, this.state.reverseStatMods);
    const name = getCardData(this.library, card.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.sendToGraveyard(uid);
    const marks = this.state.zones[seat].soulExchangeUids;
    if (marks.length > 0) {
      this.state.zones[seat].soulExchangeUids = marks.filter((id) => id !== uid);
    }
    if (kind === 'cannon') {
      this.changeLifePoints(otherSeat(seat), -500);
      this.message('cannonSoldier', { cardId: card.cardId, n: 500 }, `${name}: 500 damage.`);
    } else if (kind === 'turtle') {
      const damage = Math.floor(atk / 2);
      if (damage > 0) this.changeLifePoints(otherSeat(seat), -damage);
      this.message('catapultTurtle', { cardId: card.cardId, n: damage }, `${name}: ${damage} damage.`);
    } else {
      const source = findCard(this.state, card.uid)?.instance;
      if (source?.faceUp) {
        const n = source.statModifiers.length;
        this.addStatModifier(source.uid, 700, 0, `untilEnd:${source.uid}:aile:${n}`);
      }
      this.message('aileBoost', { cardId: card.cardId }, `${name}: ATK +700 until the End Phase.`);
    }
    this.checkWin();
  }

  private jugglerVictimUids(sourceUid: string): string[] {
    const out: string[] = [];
    for (const seat of [0, 1] as Seat[]) {
      for (const monster of getMonsters(this.state.zones[seat])) {
        if (monster.uid === sourceUid || !monster.faceUp) continue;
        if (getModifiedAtk(monster, this.library, this.state.reverseStatMods) > 1000) continue;
        if (this.lordOfDBlocksTarget(monster.uid)) continue;
        out.push(monster.uid);
      }
    }
    return out;
  }

  private resolveBlast(card: CardInstance, uids: string[]): void {
    const name = getCardData(this.library, card.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.sendToGraveyard(card.uid);
    let destroyed = 0;
    for (const uid of uids) {
      const found = findCard(this.state, uid)?.instance;
      if (!found?.faceUp) continue;
      if (getModifiedAtk(found, this.library, this.state.reverseStatMods) > 1000) continue;
      if (this.lordOfDBlocksTarget(uid)) continue;
      if (this.destroyCard(uid)) destroyed += 1;
    }
    this.message(
      destroyed > 0 ? 'blastJuggler' : 'blastNoTarget',
      { cardId: card.cardId, n: destroyed },
      destroyed > 0 ? `${name} destroyed ${destroyed} monster(s).` : `${name}: no monster to destroy.`,
    );
    this.checkWin();
  }

  /** Face-up Mushroom Man #2: the controller loses 300 LP on their Standby. */
  private mushroomStandby(seat: Seat): void {
    if (this.state.status !== 'active') return;
    const name = getCardData(this.library, String(MUSHROOM_MAN_2)).name;
    for (const monster of getMonsters(this.state.zones[seat])) {
      if (!monster.faceUp || Number(monster.cardId) !== MUSHROOM_MAN_2) continue;
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: monster.uid, detail: name });
      this.changeLifePoints(seat, -300);
      this.message('mushroomDrain', { cardId: monster.cardId }, `${name}: lose 300 Life Points.`);
    }
    this.checkWin();
  }

  private queueBlastJugglers(seat: Seat): void {
    if (this.state.status !== 'active') return;
    for (const monster of getMonsters(this.state.zones[seat])) {
      if (!monster.faceUp || Number(monster.cardId) !== BLAST_JUGGLER) continue;
      if (this.jugglerVictimUids(monster.uid).length < 2) continue;
      this.choiceJobs.push({ kind: 'juggler', seat, uid: monster.uid });
    }
  }

  private queueMushroomGive(seat: Seat): void {
    if (this.state.status !== 'active') return;
    if (this.state.zones[seat].lifePoints < 500) return;
    if (firstFreeIndex(this.state.zones[otherSeat(seat)].monsters) === -1) return;
    for (const monster of getMonsters(this.state.zones[seat])) {
      if (!monster.faceUp || Number(monster.cardId) !== MUSHROOM_MAN_2) continue;
      this.choiceJobs.push({ kind: 'mushroom', seat, uid: monster.uid });
    }
  }

  private openChoice(seat: Seat, card: CardInstance, kind: PendingChoice['kind']): void {
    this.priorityCounter += 1;
    this.state.pendingChoice = {
      requestId: `choice-${this.priorityCounter}`,
      chooser: seat,
      cardUid: card.uid,
      cardId: card.cardId,
      kind,
    };
    const name = getCardData(this.library, card.cardId).name;
    this.message(
      kind === 'blastJuggler' ? 'blastAsk' : 'mushroomAsk',
      { cardId: card.cardId },
      kind === 'blastJuggler'
        ? `${name}: Tribute it to destroy 2 face-up monsters with 1000 or less ATK?`
        : `${name}: pay 500 Life Points to give control of it to your opponent?`,
    );
  }

  /** Opens the next yes/no. The AI never sees the prompt. */
  private openNextChoice(): void {
    if (this.state.status !== 'active') {
      this.choiceJobs = [];
      return;
    }
    if (this.state.pendingChoice || this.state.pendingTargets) return;
    while (this.choiceJobs.length > 0) {
      const job = this.choiceJobs.shift()!;
      const found = findCard(this.state, job.uid);
      const card = found?.instance;
      if (!found || !card?.faceUp || found.location.zone !== 'monsters' || found.location.seat !== job.seat) {
        continue;
      }
      if (job.kind === 'juggler') {
        const victims = this.jugglerVictimUids(card.uid);
        if (victims.length < 2) continue;
        if (this.state.seats[job.seat].isAI) {
          const picked = this.aiBlastVictims(job.seat, victims);
          if (!picked) continue;
          this.resolveBlast(card, picked);
          continue;
        }
        this.openChoice(job.seat, card, 'blastJuggler');
        return;
      }
      if (this.state.zones[job.seat].lifePoints < 500) continue;
      if (firstFreeIndex(this.state.zones[otherSeat(job.seat)].monsters) === -1) continue;
      if (this.state.seats[job.seat].isAI) continue;
      this.openChoice(job.seat, card, 'mushroomGive');
      return;
    }
  }

  private answerChoice(seat: Seat, requestId: string, accept: boolean): void {
    const pending = this.state.pendingChoice;
    if (!pending || pending.requestId !== requestId) {
      throw new EngineError('Answer the prompt first', 'waitingTarget');
    }
    if (seat !== pending.chooser) throw new EngineError('Not your selection', 'notYourSelection');
    this.state.pendingChoice = null;
    const found = findCard(this.state, pending.cardUid);
    const card = found?.instance;
    if (!accept || !found || !card?.faceUp || found.location.zone !== 'monsters') {
      this.openNextChoice();
      this.resumeEndPhase();
      return;
    }
    if (pending.kind === 'blastJuggler') {
      const victims = this.jugglerVictimUids(card.uid);
      if (victims.length < 2) {
        this.message('blastNoTarget', { cardId: card.cardId }, 'Not enough monsters.');
        this.openNextChoice();
        this.resumeEndPhase();
        return;
      }
      this.openPickWindow(seat, card, victims, 2, 2, { blastDestroy: true });
      return;
    }
    if (this.state.zones[seat].lifePoints < 500) {
      this.openNextChoice();
      this.resumeEndPhase();
      return;
    }
    if (firstFreeIndex(this.state.zones[otherSeat(seat)].monsters) === -1) {
      this.openNextChoice();
      this.resumeEndPhase();
      return;
    }
    const name = getCardData(this.library, card.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.changeLifePoints(seat, -500);
    this.checkWin();
    if (this.state.status !== 'active') return;
    if (this.shiftControl(card.uid, otherSeat(seat))) {
      this.message('mushroomGive', { cardId: card.cardId }, `${name}: control shifts to the opponent.`);
    }
    this.openNextChoice();
    this.resumeEndPhase();
  }

  /**
   * The End Phase button already asked to pass the turn. The prompt paused
   * that second step; finishing the prompt (Yes or No) completes it.
   * A decline that changes nothing still emits one empty message, or the
   * client keeps the prompt (it only refreshes when events arrive).
   */
  private resumeEndPhase(): void {
    const leaving =
      this.state.phase === EnginePhase.END &&
      this.state.status === 'active' &&
      !this.endPhaseBlocked();
    if (!leaving && this.events.length === 0) {
      this.emit({ type: EventType.MESSAGE, text: '' });
    }
    if (leaving) this.advancePhase();
  }

  private endPhaseBlocked(): boolean {
    return (
      this.state.pendingChoice !== null ||
      this.state.pendingTargets !== null ||
      this.state.pendingDiscard !== null ||
      this.state.pendingPriority !== null ||
      this.state.pendingCoinCall !== null ||
      this.state.pendingFlipEffect !== null ||
      this.state.pendingChainOrder !== null ||
      this.state.pendingOfferingSummon !== null ||
      this.state.chain.length > 0
    );
  }

  /**
   * Permanent control change. Borrowed monsters (Change of Heart) still
   * go home at the End Phase; this one stays.
   */
  private shiftControl(uid: string, toSeat: Seat): boolean {
    const found = findCard(this.state, uid);
    if (!found || found.location.zone !== 'monsters') return false;
    const fromSeat = found.location.seat;
    if (fromSeat === toSeat) return false;
    const fromIndex = found.location.index;
    const idx = firstFreeIndex(this.state.zones[toSeat].monsters);
    if (idx === -1) return false;
    const removed = this.removeFromField(uid);
    if (!removed) return false;
    const card = removed.card;
    delete card.controlReturnTo;
    card.summonedThisTurn = false;
    card.attackedThisTurn = false;
    card.declaredAttackThisTurn = false;
    card.positionChangedThisTurn = false;
    this.state.zones[toSeat].monsters[idx] = card;
    this.emit({
      type: EventType.CARD_MOVED,
      cardUid: uid,
      from: { seat: fromSeat, zone: 'monsters', index: fromIndex },
      to: { seat: toSeat, zone: 'monsters', index: idx },
    });
    return true;
  }

  /**
   * Share the Pain already Tributed one of yours. The opponent Tributes
   * one of theirs now. A human picks; the AI Tributes its weakest.
   */
  private resolveShareThePain(link: ChainLink, spell: CardInstance): boolean {
    const name = getCardData(this.library, spell.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: spell.uid, detail: name });
    const still = findCard(this.state, spell.uid);
    if (still && still.location.zone !== 'graveyard') this.sendOneShotToGraveyard(spell);
    const opp = otherSeat(link.controller);
    const monsters = getMonsters(this.state.zones[opp]);
    if (monsters.length === 0) {
      this.message('shareNoMonster', { cardId: spell.cardId }, `${name}: the opponent has no monster.`);
      this.checkWin();
      return false;
    }
    if (this.state.seats[opp].isAI) {
      const uid = this.weakestOf(monsters.map((monster) => monster.uid));
      if (uid) this.sendToGraveyard(uid);
      this.message('sharePain', { cardId: spell.cardId }, `${name}: both players Tributed 1 monster.`);
      this.checkWin();
      return false;
    }
    this.openPickWindow(
      opp,
      spell,
      monsters.map((monster) => monster.uid),
      1,
      1,
      { opponentTribute: true },
    );
    return true;
  }

  private activateMonsterEffect(seat: Seat, uid: string): void {
    this.requireTurnMain(seat);
    if (
      this.state.chain.length > 0 ||
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingCoinCall
    ) {
      throw new EngineError('This card cannot be activated right now', 'cannotActivateNow');
    }
    const inHand = this.state.zones[seat].hand.find((c) => c.uid === uid);
    if (inHand && handIgnitionOf(inHand.cardId)) {
      this.activateThunderDragon(seat, inHand);
      return;
    }
    const card = getMonsters(this.state.zones[seat]).find((m) => m.uid === uid);
    if (!card || !card.faceUp) throw new EngineError('Monster not found', 'monsterNotFound');
    const tribute = tributeIgnitionOf(card.cardId);
    if (tribute) {
      this.igniteTribute(seat, card, tribute);
      return;
    }
    const kind = coinIgnitionOf(card.cardId);
    if (!kind) throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    if (card.ignitionUsedThisTurn) {
      throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    }
    if (kind === 'barrel') {
      this.igniteBarrelDragon(seat, card);
      return;
    }
    if (this.state.seats[seat].isAI) {
      card.ignitionUsedThisTurn = true;
      this.resolveTimeWizard(seat, card, 'heads');
      return;
    }
    this.openCoinCall(seat, card, 'timeWizard');
  }

  /**
   * Discard Thunder Dragon, then add 1 or 2 copies from the Deck.
   * The discard is the cost: it happens before the choice, and it is
   * not a chain link. Zero copies in the Deck means it cannot start.
   */
  private activateThunderDragon(seat: Seat, card: CardInstance): void {
    const copies = this.state.zones[seat].deck.filter(
      (c) => Number(c.cardId) === THUNDER_DRAGON,
    );
    if (copies.length === 0) {
      throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    }
    const sourceUid = card.uid;
    const cardId = card.cardId;
    this.sendToGraveyard(sourceUid);
    const max = Math.min(2, copies.length);
    const uids = copies.map((c) => c.uid);
    if (this.state.seats[seat].isAI) {
      const added = this.takeDeckCardsToHand(seat, uids.slice(0, max));
      this.message(
        'thunderAdded',
        { cardId, n: added },
        `Added ${added} from the Deck.`,
      );
      this.emit({
        type: EventType.EFFECT_RESOLVED,
        cardUid: sourceUid,
        detail: getCardData(this.library, cardId).name,
      });
      this.checkWin();
      return;
    }
    this.openDeckAddPrompt(seat, sourceUid, cardId, uids, 1, max);
  }

  private callCoin(seat: Seat, requestId: string, call: CoinSide): void {
    const pending = this.state.pendingCoinCall;
    if (!pending || pending.requestId !== requestId) {
      throw new EngineError('Call the coin first', 'waitingCoin');
    }
    if (seat !== pending.chooser) throw new EngineError('Not your selection', 'notYourSelection');
    if (call !== 'heads' && call !== 'tails') {
      throw new EngineError('Call the coin first', 'waitingCoin');
    }
    const found = findCard(this.state, pending.cardUid);
    this.state.pendingCoinCall = null;
    if (!found || found.location.zone !== 'monsters') {
      this.state.pendingAttack = null;
      return;
    }
    const card = found.instance;
    if (pending.kind === 'jirai') {
      this.resolveJirai(seat, card, call);
      return;
    }
    card.ignitionUsedThisTurn = true;
    this.resolveTimeWizard(seat, card, call);
  }

  /**
   * Right call: destroy every monster the opponent controls.
   * Wrong call: destroy every monster you control, then lose LP equal to
   * half the total ATK of the face-up ones (face-down ATK is not counted).
   */
  private resolveTimeWizard(seat: Seat, card: CardInstance, call: CoinSide): void {
    const result = this.tossCoin();
    this.emitCoin(seat, card, call, [result]);
    const right = call === result;
    const victims = getMonsters(this.state.zones[right ? otherSeat(seat) : seat]);
    let atk = 0;
    if (!right) {
      for (const m of victims) {
        if (m.faceUp) atk += getModifiedAtk(m, this.library, this.state.reverseStatMods);
      }
    }
    for (const m of victims) this.destroyCard(m.uid);
    if (!right) {
      const damage = Math.floor(atk / 2);
      if (damage > 0) this.changeLifePoints(seat, -damage);
    }
    const name = getCardData(this.library, card.cardId).name;
    this.message(
      right ? 'timeWizardRight' : 'timeWizardWrong',
      { cardId: card.cardId },
      right
        ? `${name}: called it right. The opponent's monsters are destroyed.`
        : `${name}: called it wrong. Your monsters are destroyed.`,
    );
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.checkWin();
  }

  /**
   * Select 1 opposing monster, then toss 3 coins. 2 or 3 heads destroys
   * that monster. c81480460.lua targets before TossCoin.
   */
  private igniteBarrelDragon(seat: Seat, card: CardInstance): void {
    const victims = getMonsters(this.state.zones[otherSeat(seat)]);
    if (victims.length === 0) {
      throw new EngineError('Activation conditions are not met', 'conditionsNotMet');
    }
    card.ignitionUsedThisTurn = true;
    if (this.state.seats[seat].isAI) {
      const best = [...victims].sort(
        (a, b) =>
          getModifiedAtk(b, this.library, this.state.reverseStatMods) -
          getModifiedAtk(a, this.library, this.state.reverseStatMods),
      )[0];
      if (best) this.resolveBarrelDragon(seat, card, best.uid);
      return;
    }
    this.openPickWindow(
      seat,
      card,
      victims.map((m) => m.uid),
      1,
      1,
      { barrelTarget: true },
    );
  }

  /** Coins land after the monster is chosen. A miss leaves it on the field. */
  private resolveBarrelDragon(seat: Seat, card: CardInstance, targetUid: string): void {
    const results: CoinSide[] = [this.tossCoin(), this.tossCoin(), this.tossCoin()];
    this.emitCoin(seat, card, null, results);
    const heads = results.filter((side) => side === 'heads').length;
    const name = getCardData(this.library, card.cardId).name;
    const victim = findCard(this.state, targetUid);
    const still =
      victim != null &&
      victim.location.seat === otherSeat(seat) &&
      victim.location.zone === 'monsters';
    const destroyed = heads >= 2 && still ? this.destroyCard(targetUid) : false;
    this.message(
      destroyed ? 'barrelHit' : 'barrelMiss',
      { cardId: card.cardId, heads },
      destroyed
        ? `${name}: ${heads} heads. That monster is destroyed.`
        : `${name}: ${heads} heads. Nothing is destroyed.`,
    );
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.checkWin();
  }

  /** A wrong call sets LP to ceil(current / 2). The attack still happens. */
  private resolveJirai(seat: Seat, card: CardInstance, call: CoinSide): void {
    const result = this.tossCoin();
    this.emitCoin(seat, card, call, [result]);
    const name = getCardData(this.library, card.cardId).name;
    const right = call === result;
    if (!right) {
      const lp = this.state.zones[seat].lifePoints;
      const next = Math.ceil(lp / 2);
      if (next !== lp) this.changeLifePoints(seat, next - lp);
    }
    this.message(
      right ? 'jiraiRight' : 'jiraiWrong',
      { cardId: card.cardId },
      right
        ? `${name}: called it right. The attack continues.`
        : `${name}: called it wrong. Life Points are halved.`,
    );
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.checkWin();
    if (this.state.status !== 'active') {
      this.state.pendingAttack = null;
      return;
    }
    const pending = this.state.pendingAttack;
    if (!pending || pending.attackerUid !== card.uid) return;
    this.emit({
      type: EventType.ATTACK_DECLARED,
      attackerUid: card.uid,
      targetUid: pending.targetUid,
    });
    this.openResponseWindow(otherSeat(seat));
  }

  /**
   * Advances a paused attack through Battle Step → Damage Step → calculation.
   * Returns early whenever a window, chain, or Ultimate Offering summon is open.
   */
  private proceedPendingAttack(): void {
    const pending = this.state.pendingAttack;
    if (!pending) return;
    if (this.state.status !== 'active') {
      this.state.pendingAttack = null;
      return;
    }
    if (
      this.state.pendingCoinCall ||
      this.state.pendingChoice ||
      this.state.pendingChainOrder ||
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingOfferingSummon !== null ||
      this.state.chain.length > 0
    ) {
      return;
    }

    const attacker = getMonsters(this.state.zones[pending.seat]).find(
      (m) => m.uid === pending.attackerUid,
    );
    if (!attacker || !attacker.faceUp || attacker.position !== CardPosition.ATTACK) {
      this.state.pendingAttack = null;
      this.state.battleStep = 'BATTLE';
      this.cancelAttack('attackerGone');
      return;
    }

    if (pending.stage === 'declared' && this.maybeReplay(pending, attacker)) {
      return;
    }

    if (pending.stage === 'declared') {
      pending.stage = 'preDamage';
      this.state.battleStep = 'DAMAGE';
      if (pending.targetUid) {
        const target = getMonsters(this.state.zones[otherSeat(pending.seat)]).find(
          (m) => m.uid === pending.targetUid,
        );
        if (!target) {
          this.state.pendingAttack = null;
          this.state.battleStep = 'BATTLE';
          this.cancelAttack('targetGone');
          return;
        }
        if (!target.faceUp) {
          target.faceUp = true;
          pending.revealedByAttack = true;
          this.emit({
            type: EventType.CARD_FLIPPED,
            cardUid: target.uid,
            cardId: target.cardId,
            faceUp: true,
          });
        }
      }
      // The Set monster is face-up before anyone answers the Damage Step.
      // Field ATK/DEF has to be on the card for that window and for a
      // calculation that continues in this same action.
      this.recomputeFieldSpells();
      this.openResponseWindow(otherSeat(pending.seat));
      return;
    }

    this.resolveBattle(pending.seat, pending.attackerUid, pending.targetUid);
    this.state.pendingAttack = null;
    if (this.maybeLeaveBattleForMaiden()) return;
    this.state.battleStep = 'BATTLE';
  }

  /**
   * UDE-era Replay: the defending player's monster count changed, or the
   * attack target left the field, during the Battle Step. Returns play to
   * the Battle Step and frees the attacker to declare again (or pass).
   */
  private maybeReplay(pending: PendingAttack, attacker: CardInstance): boolean {
    const defenders = getMonsters(this.state.zones[otherSeat(pending.seat)]);
    const countChanged = defenders.length !== pending.defenderCount;
    const targetGone =
      pending.targetUid !== null &&
      !defenders.some((m) => m.uid === pending.targetUid);
    if (!countChanged && !targetGone) return false;

    // Pre-May 2006: the monster may declare again later this Battle Phase.
    // The declaration still blocks a manual position change in Main Phase 2.
    attacker.attackedThisTurn = false;
    this.emit({
      type: EventType.ATTACK_REPLAY,
      attackerUid: pending.attackerUid,
      targetUid: pending.targetUid,
    });
    this.state.pendingAttack = null;
    this.state.battleStep = 'BATTLE';
    return true;
  }

  /**
   * After Ultimate Offering's summon window closes, continue a paused attack
   * (Replay or Damage Step) or the Battle Phase step that was waiting.
   * Mandatory Graveyard triggers are still unordered here: the Battle Phase
   * window waits until that chain is placed.
   */
  private tryResumePendingAttack(): void {
    if (this.state.pendingOfferingSummon !== null) return;
    if (this.state.pendingCoinCall || this.state.pendingChoice) return;
    if (
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingFlipEffect ||
      this.state.pendingChainOrder ||
      this.state.chain.length > 0
    ) {
      return;
    }
    if (this.resumeOfferingWindow) {
      this.resumeOfferingWindow = false;
      if (this.reofferBattleIgnition()) return;
    }
    this.continueAfterEmptyWindow();
  }

  /**
   * After a paid Ultimate Offering summon on the opponent's Battle Phase,
   * open the Battle Step window again (another payment, or a trap) before
   * damage or the next attack.
   */
  private reofferBattleIgnition(): boolean {
    if (this.state.status !== 'active') return false;
    if (this.state.phase !== EnginePhase.BATTLE) return false;
    if (this.state.battleStep === 'DAMAGE') return false;
    const seat = otherSeat(this.state.turnPlayer);
    this.state.chainPasses = 0;
    this.state.chainTriggerUid = null;
    this.openResponseWindow(seat);
    return true;
  }

  /**
   * Carries out a declared attack, either straight after the declaration or once a
   * trap-response window closes. Because a trap may have changed the board in
   * between, the attack is re-checked here and cancelled when it can no longer
   * happen: the attacker is gone or no longer in Attack Position, or its target left
   * the field.
   */
  private resolveBattle(seat: Seat, attackerUid: string, targetUid: string | null): void {
    const attacker = getMonsters(this.state.zones[seat]).find((m) => m.uid === attackerUid);
    if (!attacker || !attacker.faceUp || attacker.position !== CardPosition.ATTACK) {
      this.cancelAttack('attackerGone');
      return;
    }

    if (targetUid === null) {
      this.resolveDirectAttack(seat, attacker);
      return;
    }

    const target = getMonsters(this.state.zones[otherSeat(seat)]).find((m) => m.uid === targetUid);
    if (!target) {
      this.cancelAttack('targetGone');
      return;
    }

    this.resolveMonsterBattle(seat, attacker, target);
  }

  private cancelAttack(reason: AttackCancelReason): void {
    const text = {
      attackerGone: 'the attacker can no longer attack',
      targetGone: 'the target left the field',
      oppHasMonster: 'the opponent now controls a monster',
    }[reason];
    this.message(
      'attackCancelled',
      { reason },
      `The attack was cancelled: ${text}.`,
    );
    this.checkWin();
  }

  /** Damage calculation between an attacker and the monster it attacked. */
  private resolveMonsterBattle(seat: Seat, attacker: CardInstance, target: CardInstance): void {
    const opp = otherSeat(seat);
    const attackerUid = attacker.uid;
    const targetUid = target.uid;

    // Face-down defender is turned face-up in the Damage Step (see
    // proceedPendingAttack). The Flip Effect waits until after calculation.
    const revealedByAttack = !!this.state.pendingAttack?.revealedByAttack || !target.faceUp;
    if (!target.faceUp) {
      target.faceUp = true;
      this.emit({ type: EventType.CARD_FLIPPED, cardUid: target.uid, cardId: target.cardId, faceUp: true });
    }
    // Damage Step chains can remove the field after the reveal. Recalculate
    // before reading ATK/DEF so the battle uses the terrain that is still up.
    this.recomputeFieldSpells();
    this.applyInsectSoldiersBoost(attacker, target);

    const atk = getModifiedAtk(attacker, this.library, this.state.reverseStatMods);
    const defending = target.position === CardPosition.DEFENSE;
    const targetValue = defending
      ? getModifiedDef(target, this.library, this.state.reverseStatMods)
      : getModifiedAtk(target, this.library, this.state.reverseStatMods);

    let attackerDestroyed = false;
    let targetDestroyed = false;
    let damage = 0;
    let damageSeat: Seat | null = null;

    if (defending) {
      if (atk > targetValue) {
        targetDestroyed = true;
      } else if (atk < targetValue) {
        damage = targetValue - atk;
        damageSeat = seat;
      }
    } else {
      if (atk > targetValue) {
        targetDestroyed = true;
        damage = atk - targetValue;
        damageSeat = opp;
      } else if (atk < targetValue) {
        attackerDestroyed = true;
        damage = targetValue - atk;
        damageSeat = seat;
      } else {
        attackerDestroyed = true;
        targetDestroyed = true;
      }
    }

    if (
      damage > 0 &&
      damageSeat !== null &&
      !this.state.battleDamageImmune[damageSeat] &&
      this.zeroDamageSeat !== damageSeat
    ) {
      this.changeLifePoints(damageSeat, -damage);
      const attackerDealt = damageSeat === opp;
      this.noteBattleDamage(attackerDealt ? attacker : target, attackerDealt ? seat : opp);
    } else if (damage > 0 && damageSeat !== null && this.zeroDamageSeat === damageSeat) {
      damage = 0;
      this.zeroDamageSeat = null;
      this.message('kuribohDamage', { cardId: String(KURIBOH) }, 'Kuriboh: battle damage becomes 0.');
    } else if (damage > 0 && damageSeat !== null) {
      damage = 0;
      this.message('wabokuDamage', { cardId: String(STARTER_IDS.WABOKU) }, 'Waboku: battle damage becomes 0.');
    }

    if (attackerDestroyed && this.state.battleDestroyImmune[seat]) {
      attackerDestroyed = false;
      this.message(
        'wabokuAttacker',
        { cardId: String(STARTER_IDS.WABOKU) },
        'Waboku: the attacking monster is not destroyed by battle this turn.',
      );
    }
    if (targetDestroyed && this.state.battleDestroyImmune[opp]) {
      targetDestroyed = false;
      this.message(
        'wabokuDefender',
        { cardId: String(STARTER_IDS.WABOKU) },
        'Waboku: the defending monster is not destroyed by battle this turn.',
      );
    }

    // SDY-034 returns the attacker only after damage is calculated normally.
    // A monster destroyed by that battle goes to the Graveyard. c13945283.lua
    // skips the bounce when the attacker is STATUS_BATTLE_DESTROYED.
    const bounceAttacker =
      Number(target.cardId) === STARTER_IDS.WALL_OF_ILLUSION && !attackerDestroyed;

    this.emit({
      type: EventType.BATTLE_RESOLVED,
      attackerUid,
      targetUid,
      attackerDestroyed,
      targetDestroyed,
      damageSeat: damage > 0 ? damageSeat : null,
      damage,
    });

    const silentStats = new Set<string>();
    if (attackerDestroyed) silentStats.add(attackerUid);
    if (targetDestroyed) silentStats.add(targetUid);
    this.stripDamageStepBoosts(silentStats);

    if (!attackerDestroyed) this.applyAttackLocks(opp, attacker, target);
    if (bounceAttacker) {
      // The destello keys off EFFECT_RESOLVED, same as other trigger monsters.
      this.emit({
        type: EventType.EFFECT_RESOLVED,
        cardUid: target.uid,
        detail: getCardData(this.library, target.cardId).name,
      });
      this.returnToHand(attacker.uid);
    } else if (attackerDestroyed) this.queueBattleDestroy(attacker.uid);
    if (targetDestroyed) this.queueBattleDestroy(target.uid);

    if (revealedByAttack) this.runFlipEffect(opp, target);
    this.flushBattleGy();
    this.checkWin();
  }

  /** Marks a monster to leave for the GY after the current Flip Effect, if any. */
  private queueBattleDestroy(uid: string): void {
    if (!this.pendingBattleGy.includes(uid)) this.pendingBattleGy.push(uid);
  }

  /**
   * Sends queued battle-destroyed cards to the GY. Skips the flush while a Flip
   * Effect target window is open so the source stays on the field for the choice.
   */
  private flushBattleGy(): void {
    if (this.state.pendingFlipEffect) return;
    if (this.state.chain.length > 0 || this.resolvingChain) return;
    const uids = this.pendingBattleGy;
    this.pendingBattleGy = [];
    for (const uid of uids) {
      const found = findCard(this.state, uid);
      if (found?.location.zone !== 'monsters') continue;
      if (Number(found.instance.cardId) === UNHAPPY_MAIDEN) this.noteUnhappyMaiden(found.instance);
      this.destroyCard(uid);
    }
  }

  private directAttack(seat: Seat, attackerUid: string): void {
    const attacker = getMonsters(this.state.zones[seat]).find((m) => m.uid === attackerUid);
    if (!attacker) throw new EngineError('Attacker not found', 'attackerNotFound');
    this.validateAttacker(attacker);

    const opp = otherSeat(seat);
    if (
      getMonsters(this.state.zones[opp]).length > 0 &&
      !cardAttacksDirectly(attacker.cardId)
    ) {
      throw new EngineError('Opponent controls monsters; cannot attack directly', 'cannotDirectAttack');
    }
    if (magnetismTargets(this.state, opp).length > 0) {
      throw new EngineError(
        'You must attack the monster equipped with Ring of Magnetism',
        'ringOfMagnetism',
      );
    }

    this.beginAttack(seat, attacker, null);
  }

  /** Life-point damage for an unblocked attack, once no response is pending. */
  private resolveDirectAttack(seat: Seat, attacker: CardInstance): void {
    const opp = otherSeat(seat);
    // A response may have put a monster back in the way, in which case there is
    // nothing to hit directly.
    if (
      getMonsters(this.state.zones[opp]).length > 0 &&
      !cardAttacksDirectly(attacker.cardId)
    ) {
      this.cancelAttack('oppHasMonster');
      return;
    }

    const atk = getModifiedAtk(attacker, this.library, this.state.reverseStatMods);
    const zeroed = this.state.battleDamageImmune[opp] || this.zeroDamageSeat === opp;
    const dealt = zeroed ? 0 : atk;
    if (atk > 0 && this.zeroDamageSeat === opp) {
      this.zeroDamageSeat = null;
      this.message('kuribohDamage', { cardId: String(KURIBOH) }, 'Kuriboh: battle damage becomes 0.');
    } else if (atk > 0 && dealt === 0) {
      this.message('wabokuDamage', { cardId: String(STARTER_IDS.WABOKU) }, 'Waboku: battle damage becomes 0.');
    }
    this.stripDamageStepBoosts();
    this.emit({
      type: EventType.BATTLE_RESOLVED,
      attackerUid: attacker.uid,
      targetUid: null,
      attackerDestroyed: false,
      targetDestroyed: false,
      damageSeat: dealt > 0 ? opp : null,
      damage: dealt,
    });
    if (dealt > 0) {
      this.changeLifePoints(opp, -dealt);
      this.noteBattleDamage(attacker, seat);
    }
    this.checkWin();
  }

  private validateAttacker(attacker: CardInstance): void {
    if (!attacker.faceUp) throw new EngineError('Attacker is face-down', 'attackerFaceDown');
    if (attacker.position !== CardPosition.ATTACK) throw new EngineError('Attacker not in attack position', 'attackerNotAttackPos');
    if (attacker.attackedThisTurn) throw new EngineError('Already attacked this turn', 'alreadyAttacked');
    if (isOpeningTurn(this.state)) {
      throw new EngineError('Cannot attack on the very first turn', 'noFirstTurnAttack');
    }
    if (this.state.zones[this.state.turnPlayer].attackLockTurns > 0) {
      throw new EngineError('You cannot attack (Swords of Revealing Light)', 'swordsOfRevealingLight');
    }
    const lock = attackLockCode(this.state, this.state.turnPlayer, attacker);
    if (lock === 'paralyzingPotion') {
      throw new EngineError('That monster cannot attack (Paralyzing Potion)', 'paralyzingPotion');
    }
    if (lock === 'electricLizard') {
      throw new EngineError('That monster cannot attack this turn (Electric Lizard)', 'electricLizard');
    }
    if (lock === 'darkElfCost') {
      throw new EngineError('Dark Elf needs 1000 Life Points to attack', 'darkElfCost');
    }
  }

  /** Dark Elf's attack cost. Paying to 0 loses the duel before the attack starts. */
  private payDarkElf(seat: Seat, attacker: CardInstance): boolean {
    if (Number(attacker.cardId) !== DARK_ELF) return true;
    this.changeLifePoints(seat, -DARK_ELF_COST);
    const name = getCardData(this.library, attacker.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: attacker.uid, detail: name });
    this.message('darkElfPaid', { cardId: attacker.cardId }, `${name}: pay 1000 Life Points.`);
    this.checkWin();
    return this.state.status === 'active';
  }

  /**
   * Electric Lizard and Steel Scorpion apply after damage calculation, even
   * if the defender is destroyed by that battle. A destroyed attacker leaves
   * the field, so the mark would not stick.
   */
  private applyAttackLocks(defenderSeat: Seat, attacker: CardInstance, target: CardInstance): void {
    const id = Number(target.cardId);
    const race = (getCardData(this.library, attacker.cardId).race ?? '').toLowerCase();
    const name = getCardData(this.library, target.cardId).name;
    if (id === ELECTRIC_LIZARD && race !== 'zombie') {
      attacker.lizardLockTurn = this.state.absoluteTurnCount + 2;
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: target.uid, detail: name });
      this.message(
        'electricLizardLock',
        { cardId: attacker.cardId },
        `${getCardData(this.library, attacker.cardId).name} cannot attack on its next turn.`,
      );
    }
    if (id === STEEL_SCORPION && race !== 'machine') {
      if (attacker.doomEndsLeft === undefined) {
        attacker.doomEndsLeft = STEEL_SCORPION_ENDS;
        attacker.doomSeat = defenderSeat;
      }
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: target.uid, detail: name });
      this.message(
        'steelScorpionMark',
        { cardId: attacker.cardId },
        `${getCardData(this.library, attacker.cardId).name} is destroyed at the 3rd End Phase.`,
      );
    }
  }

  private noteUnhappyMaiden(card: CardInstance): void {
    this.maidenEndsBattle = true;
    const name = getCardData(this.library, card.cardId).name;
    this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: card.uid, detail: name });
    this.message('unhappyMaiden', { cardId: card.cardId }, `${name}: the Battle Phase ends.`);
  }

  /** Leaves the Battle Phase once the attack that destroyed the Maiden is over. */
  private maybeLeaveBattleForMaiden(): boolean {
    if (!this.maidenEndsBattle) return false;
    if (this.state.pendingAttack) return false;
    if (
      this.state.pendingFlipEffect ||
      this.state.pendingPriority ||
      this.state.pendingTargets ||
      this.state.pendingCoinCall ||
      this.state.pendingChoice ||
      this.state.chain.length > 0 ||
      this.resolvingChain
    ) {
      return false;
    }
    if (this.state.status !== 'active' || this.state.phase !== EnginePhase.BATTLE) {
      this.maidenEndsBattle = false;
      return false;
    }
    this.maidenEndsBattle = false;
    // The Battle Phase ends. The turn still plays Main Phase 2.
    this.enterBattleEndStep(EnginePhase.MAIN_2);
    return true;
  }

  /** Steel Scorpion counts the attacker's End Phases, including the attack turn. */
  private tickSteelScorpion(): void {
    const seat = this.state.turnPlayer;
    const monsters = [
      ...getMonsters(this.state.zones[0]),
      ...getMonsters(this.state.zones[1]),
    ];
    for (const m of monsters) {
      if (m.doomEndsLeft === undefined || m.doomSeat === undefined) continue;
      if (!m.faceUp) {
        delete m.doomEndsLeft;
        delete m.doomSeat;
        continue;
      }
      if (seat === m.doomSeat) continue;
      m.doomEndsLeft -= 1;
      if (m.doomEndsLeft > 0) continue;
      const name = getCardData(this.library, m.cardId).name;
      delete m.doomEndsLeft;
      delete m.doomSeat;
      this.emit({ type: EventType.EFFECT_RESOLVED, cardUid: m.uid, detail: name });
      this.message('steelScorpionDestroy', { cardId: m.cardId }, `${name} is destroyed.`);
      this.destroyCard(m.uid);
    }
  }

  // ---------------------------------------------------------------------------
  // State mutation primitives (also exposed to effects via EngineApi)
  // ---------------------------------------------------------------------------

  /** True when Fake Trap is destroyed instead of this Trap. */
  private sparedByReplacement(uid: string): boolean {
    const found = findCard(this.state, uid);
    if (!found) return false;
    const current = this.resolvingIndex >= 0 ? this.resolvingLinks[this.resolvingIndex] : undefined;
    return (
      this.fakeTrapSeat !== null &&
      this.fakeTrapSourceUid !== null &&
      current?.sourceUid === this.fakeTrapSourceUid &&
      found.location.seat === this.fakeTrapSeat &&
      found.location.zone === 'spellTraps' &&
      kindOf(found.instance, this.library) === CardKind.TRAP
    );
  }

  private destroyCard(uid: string): boolean {
    const found = findCard(this.state, uid);
    if (!found) return false;
    if (this.sparedByReplacement(uid)) {
      if (!found.instance.faceUp && !this.sparedFaceDown.some((c) => c.uid === uid)) {
        this.sparedFaceDown.push({ uid, cardId: found.instance.cardId });
      }
      return false;
    }
    this.emit({ type: EventType.CARD_DESTROYED, cardUid: uid });
    // Attached equips follow the host; sendToGraveyard handles that.
    this.sendToGraveyard(uid);
    return true;
  }

  /**
   * Sends every Equip Spell attached to `uid` to the graveyard. An Equip only exists
   * while its host is on the field, so a host that leaves for good — tributed,
   * destroyed, bounced or banished — takes its equips with it. Called before the host
   * is removed so the equips can still clean up their stat modifiers on it.
   * Control changes are deliberately excluded: there the equip stays attached.
   */
  private dropEquipsOf(uid: string): void {
    const found = findCard(this.state, uid);
    if (!found) return;
    for (const equipUid of [...found.instance.equippedUids]) this.sendToGraveyard(equipUid);
  }

  private removeFromField(uid: string): { card: CardInstance; seat: Seat } | null {
    const found = findCard(this.state, uid);
    if (!found) return null;
    const { location, instance } = found;
    delete instance.faceUpEffectUsed;
    delete instance.ignitionUsedThisTurn;
    delete instance.baseSwapped;
    delete instance.lizardLockTurn;
    delete instance.doomEndsLeft;
    delete instance.doomSeat;
    const z = this.state.zones[location.seat];
    if (location.zone === 'monsters' && location.index >= 0) z.monsters[location.index] = null;
    else if (location.zone === 'spellTraps' && location.index >= 0) z.spellTraps[location.index] = null;
    else if (location.zone === 'fieldSpell') z.fieldSpell = null;
    else if (location.zone === 'hand') z.hand.splice(location.index, 1);
    else if (location.zone === 'graveyard') z.graveyard.splice(location.index, 1);
    else if (location.zone === 'deck') z.deck.splice(location.index, 1);
    else if (location.zone === 'banished') z.banished.splice(location.index, 1);
    else return null;

    // Detach equip relationships.
    if (instance.equippedToUid) {
      const host = findCard(this.state, instance.equippedToUid);
      if (host) {
        host.instance.equippedUids = host.instance.equippedUids.filter((u) => u !== uid);
        host.instance.statModifiers = host.instance.statModifiers.filter((m) => m.source !== uid);
        this.emitStat(host.instance);
      }
      instance.equippedToUid = null;
    }
    return { card: instance, seat: location.seat };
  }

  private sendToGraveyard(uid: string): void {
    const before = findCard(this.state, uid);
    const fromMonsters =
      before?.location.zone === 'monsters' ? before.location.seat : null;
    this.dropEquipsOf(uid);
    const removed = this.removeFromField(uid);
    if (!removed) return;
    const { card, seat } = removed;
    const home = card.owner ?? seat;
    card.faceUp = true;
    card.statModifiers = [];
    delete card.atkFinal;
    card.equippedUids = [];
    card.position = CardPosition.ATTACK;
    delete card.turnCounter;
    this.state.zones[home].graveyard.push(card);
    this.emit({ type: EventType.CARD_TO_GRAVEYARD, cardUid: uid, seat: home });
    // Last Will: your own monster, from your Monster Zone, into your Graveyard.
    // A monster you stole goes home to its owner and does not qualify.
    // A negated summon never came into play, so it does not arm Last Will.
    if (
      fromMonsters !== null &&
      before &&
      fromMonsters === before.instance.owner &&
      !this.leavingFromNegatedSummon(uid)
    ) {
      this.markLastWillReady(fromMonsters);
    }
    this.noteGraveTrigger(
      card,
      home,
      before?.location.zone ?? null,
      before?.location.seat ?? seat,
    );
  }

  private returnToHand(uid: string): void {
    this.dropEquipsOf(uid);
    const fromGraveyard = findCard(this.state, uid)?.location.zone === 'graveyard';
    const removed = this.removeFromField(uid);
    if (!removed) return;
    const { card, seat } = removed;
    card.faceUp = true;
    card.statModifiers = [];
    delete card.atkFinal;
    card.equippedUids = [];
    card.position = CardPosition.ATTACK;
    delete card.turnCounter;
    delete card.properlySummoned;
    card.setOnTurn = -1;
    card.summonedThisTurn = false;
    card.attackedThisTurn = false;
    card.declaredAttackThisTurn = false;
    card.positionChangedThisTurn = false;
    const data = getCardData(this.library, card.cardId);
    const home = card.owner ?? seat;
    // Fusion Monsters cannot exist in the hand: a bounce sends them to the Extra.
    if (/fusion/i.test(data.type) || /fusion/i.test(data.humanReadableCardType ?? '')) {
      this.state.zones[home].extraDeck.push(card);
      this.emit({
        type: EventType.CARD_MOVED,
        cardUid: uid,
        from: { seat, zone: 'monsters', index: -1 },
        to: { seat: home, zone: 'extraDeck', index: -1 },
      });
      this.message(
        'returnedToExtra',
        { cardId: card.cardId },
        `${data.name} returned to the Extra Deck.`,
      );
    } else {
      this.state.zones[home].hand.push(card);
      this.emit({ type: EventType.CARD_TO_HAND, cardUid: uid, seat: home });
      this.handShuffleSeats.add(home);
      // The Graveyard is public. Magician of Faith and Mask of Darkness
      // add that card to a hidden hand, so both players still have to see it.
      if (fromGraveyard) {
        const shown = [{ uid: card.uid, cardId: card.cardId }];
        this.rememberCards(home, shown, true);
        this.emit({
          type: EventType.PEEK_CARDS,
          seat: home,
          cards: shown,
          reveal: true,
          search: true,
        });
      }
    }
    // A piece bouncing into a 4-piece Exodia hand wins immediately.
    this.checkExodia(home);
  }

  private banishCard(uid: string, faceDown = false): void {
    this.dropEquipsOf(uid);
    const removed = this.removeFromField(uid);
    if (!removed) return;
    const { card, seat } = removed;
    const home = card.owner ?? seat;
    card.faceUp = !faceDown;
    card.statModifiers = [];
    delete card.atkFinal;
    card.equippedUids = [];
    delete card.turnCounter;
    this.state.zones[home].banished.push(card);
    this.emit({ type: EventType.CARD_BANISHED, cardUid: uid, seat: home, faceDown });
  }

  private banishFromGraveyard(seat: Seat, count: number, preferStrongest: boolean): void {
    const gy = this.state.zones[seat].graveyard;
    const monsters = gy.filter((c) => kindOf(c, this.library) === CardKind.MONSTER);
    monsters.sort((a, b) =>
      preferStrongest
        ? getModifiedAtk(b, this.library) - getModifiedAtk(a, this.library)
        : getModifiedAtk(a, this.library) - getModifiedAtk(b, this.library),
    );
    for (const card of monsters.slice(0, count)) {
      const idx = gy.findIndex((c) => c.uid === card.uid);
      if (idx >= 0) {
        gy.splice(idx, 1);
        card.faceUp = true;
        this.state.zones[seat].banished.push(card);
        this.emit({ type: EventType.CARD_BANISHED, cardUid: card.uid, seat, faceDown: false });
      }
    }
  }

  private changeLifePoints(seat: Seat, delta: number): void {
    const z = this.state.zones[seat];
    z.lifePoints = Math.max(0, z.lifePoints + delta);
    this.emit({ type: EventType.LIFE_POINTS_CHANGED, seat, lifePoints: z.lifePoints, delta });
  }

  /**
   * Moves `count` cards from deck to hand. Emits one CARD_DRAWN per card so the
   * client can play them sequentially (never overlap 2+ draws, even for Pot of
   * Greed). Do not collapse this into a single "drew N" event.
   */
  private drawCards(seat: Seat, count: number): void {
    const z = this.state.zones[seat];
    for (let i = 0; i < count; i++) {
      const card = z.deck.shift();
      if (!card) {
        this.endGame(otherSeat(seat), 'deck-out');
        return;
      }
      z.hand.push(card);
      this.emit({ type: EventType.CARD_DRAWN, seat, cardUid: card.uid, cardId: card.cardId });
    }
    this.checkExodia(seat);
  }

  private addStatModifier(uid: string, atk: number, def: number, sourceUid: string): void {
    const found = findCard(this.state, uid);
    if (!found) return;
    found.instance.statModifiers.push({ atk, def, source: sourceUid });
    this.emitStat(found.instance);
  }

  /** Sanga, Kazejin, and Suijin: the attacker's ATK for this damage calculation. */
  private setAtkFinal(uid: string, atk: number): void {
    const found = findCard(this.state, uid);
    if (!found) return;
    found.instance.atkFinal = atk;
    this.emitStat(found.instance);
  }

  private emitStat(card: CardInstance): void {
    this.emit({
      type: EventType.STAT_CHANGED,
      cardUid: card.uid,
      atk: getModifiedAtk(card, this.library, this.state.reverseStatMods),
      def: getModifiedDef(card, this.library, this.state.reverseStatMods),
    });
  }

  private checkWin(): void {
    if (this.state.status === 'finished') return;
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].lifePoints <= 0) {
        this.endGame(otherSeat(seat), 'life-points');
        return;
      }
    }
  }

  /**
   * Tournament round clock. The duel stops where it stands.
   * Higher Life Points win. A tie breaks on cards left in the Deck,
   * then cards in hand, then seat 0, so the bracket always has a winner.
   */
  finishRoundTime(): GameEvent[] {
    this.events = [];
    if (this.state.status === 'finished') return this.events;
    this.endGame(this.roundTimeWinner(), 'round-time');
    return this.events;
  }

  private roundTimeWinner(): Seat {
    const score = (seat: Seat): [number, number, number] => {
      const zone = this.state.zones[seat];
      return [zone.lifePoints, zone.deck.length, zone.hand.length];
    };
    const mine = score(0);
    const theirs = score(1);
    for (let i = 0; i < mine.length; i++) {
      if (mine[i] !== theirs[i]) return mine[i]! > theirs[i]! ? 0 : 1;
    }
    return 0;
  }

  private endGame(winner: Seat, reason: string): void {
    if (this.state.status === 'finished') return;
    this.state.status = 'finished';
    this.state.winner = winner;
    this.state.winReason = reason;
    this.emit({ type: EventType.GAME_OVER, winner, reason });
  }

  private message(
    code: DuelMessageCode,
    vars: Record<string, string | number> | undefined,
    text: string,
  ): void {
    this.emit({
      type: EventType.MESSAGE,
      text,
      code,
      ...(vars ? { vars } : {}),
    });
  }

  private emit(event: GameEvent): void {
    this.events.push(event);
    this.noteMissionEvent(event);
  }

  private noteMissionEvent(event: GameEvent): void {
    switch (event.type) {
      case EventType.CARD_DRAWN:
        this.missionStats[event.seat].cardsDrawn += 1;
        break;
      case EventType.CARD_SUMMONED:
        this.missionStats[event.seat].summons += 1;
        if (event.tribute) this.missionStats[event.seat].tributes += 1;
        break;
      case EventType.CARD_SET:
        if (event.tribute) {
          this.missionStats[event.seat].summons += 1;
          this.missionStats[event.seat].tributes += 1;
        }
        break;
      case EventType.SPELL_ACTIVATED:
        this.missionStats[event.seat].spells += 1;
        break;
      case EventType.TRAP_ACTIVATED:
        this.missionStats[event.seat].traps += 1;
        break;
      case EventType.BATTLE_RESOLVED:
        if (event.damage > 0 && event.damageSeat !== null) {
          this.addBattleDamage(otherSeat(event.damageSeat), event.damage);
        }
        break;
      default:
        break;
    }
  }

  private addBattleDamage(seat: Seat, damage: number): void {
    const acc = this.missionStats[seat];
    const turn = this.state.absoluteTurnCount;
    if (acc.damageTurn !== turn) {
      acc.damageTurn = turn;
      acc.damageThisTurn = 0;
    }
    acc.damageThisTurn += damage;
    if (acc.damageThisTurn > acc.maxBattleDamageTurn) {
      acc.maxBattleDamageTurn = acc.damageThisTurn;
    }
  }

  private hasFreeMonsterZone(seat: Seat): boolean {
    return firstFreeIndex(this.state.zones[seat].monsters) !== -1;
  }

  /** Special-summons a monster found anywhere (e.g. a graveyard) onto a seat's field. */
  private specialSummon(
    uid: string,
    toSeat: Seat,
    position: CardPosition,
    tributeUids?: string[],
  ): void {
    const idx = firstFreeIndex(this.state.zones[toSeat].monsters);
    if (idx === -1) throw new EngineError('No free monster zone', 'noFreeMonsterZone');
    const removed = this.removeFromField(uid);
    if (!removed) return;
    const card = removed.card;
    card.faceUp = true;
    card.position = position;
    card.summonedThisTurn = true;
    card.attackedThisTurn = false;
    card.declaredAttackThisTurn = false;
    card.positionChangedThisTurn = false;
    card.statModifiers = [];
    delete card.atkFinal;
    card.equippedUids = [];
    delete card.controlReturnTo;
    this.state.zones[toSeat].monsters[idx] = card;
    this.emit({
      type: EventType.CARD_SUMMONED,
      seat: toSeat,
      cardUid: card.uid,
      cardId: card.cardId,
      zoneIndex: idx,
      position,
      tribute: false,
      ...(tributeUids && tributeUids.length > 0 ? { tributeUids } : {}),
    });
    this.checkSummonTraps(toSeat, card, 'special');
  }

  /** Moves a monster onto another seat's field until that seat's End Phase. */
  private takeControl(uid: string, toSeat: Seat): void {
    const found = findCard(this.state, uid);
    if (!found || found.location.zone !== 'monsters') throw new EngineError('Target is not a monster', 'targetNotMonster');
    const owner = found.location.seat;
    if (owner === toSeat) return;
    const fromIndex = found.location.index;
    const idx = firstFreeIndex(this.state.zones[toSeat].monsters);
    if (idx === -1) throw new EngineError('No free monster zone', 'noFreeMonsterZone');
    const removed = this.removeFromField(uid);
    if (!removed) return;
    const card = removed.card;
    card.controlReturnTo = owner;
    card.summonedThisTurn = false;
    card.attackedThisTurn = false;
    card.declaredAttackThisTurn = false;
    card.positionChangedThisTurn = false;
    this.state.zones[toSeat].monsters[idx] = card;
    this.emit({
      type: EventType.CARD_MOVED,
      cardUid: uid,
      from: { seat: owner, zone: 'monsters', index: fromIndex },
      to: { seat: toSeat, zone: 'monsters', index: idx },
    });
  }

  /** Returns any temporarily-controlled monsters to their owners (End Phase). */
  private returnBorrowedMonsters(): void {
    for (const seat of [0, 1] as Seat[]) {
      const z = this.state.zones[seat];
      for (let i = 0; i < z.monsters.length; i++) {
        const card = z.monsters[i];
        if (!card || card.controlReturnTo === undefined) continue;
        const owner = card.controlReturnTo;
        delete card.controlReturnTo;
        if (owner === seat) continue;
        z.monsters[i] = null;
        card.attackedThisTurn = false;
        card.declaredAttackThisTurn = false;
        card.positionChangedThisTurn = false;
        const idx = firstFreeIndex(this.state.zones[owner].monsters);
        if (idx === -1) {
          // No room to return: the monster is sent to its owner's graveyard.
          card.faceUp = true;
          card.statModifiers = [];
          delete card.atkFinal;
          card.equippedUids = [];
          card.position = CardPosition.ATTACK;
          this.state.zones[card.owner ?? owner].graveyard.push(card);
          this.emit({
            type: EventType.CARD_TO_GRAVEYARD,
            cardUid: card.uid,
            seat: card.owner ?? owner,
          });
          continue;
        }
        this.state.zones[owner].monsters[idx] = card;
        this.emit({
          type: EventType.CARD_MOVED,
          cardUid: card.uid,
          from: { seat, zone: 'monsters', index: i },
          to: { seat: owner, zone: 'monsters', index: idx },
        });
      }
    }
  }

  private setAttackLock(seat: Seat, turns: number): void {
    this.state.zones[seat].attackLockTurns = Math.max(this.state.zones[seat].attackLockTurns, turns);
  }

  /** Remaining turns shown on the source card while it stays on the field. */
  private setTurnCounter(source: CardInstance, turns: number): void {
    source.turnCounter = Math.max(0, turns);
  }

  /**
   * Decrement face-up lingering cards whose clock ticks on `outgoing`'s turn
   * end. Cards that reach 0 are sent to the graveyard.
   */
  private tickLingeringTurns(outgoing: Seat): void {
    const expire: string[] = [];
    for (const controller of [0, 1] as Seat[]) {
      const cards = [
        ...getSpellTraps(this.state.zones[controller]),
        this.state.zones[controller].fieldSpell,
      ];
      for (const c of cards) {
        if (!c?.faceUp || !c.turnCounter || c.turnCounter < 1) continue;
        const spec = lingeringSpecFor(c.cardId);
        if (!spec) continue;
        const tickSeat =
          spec.tickOn === 'opponent' ? otherSeat(controller) : controller;
        if (tickSeat !== outgoing) continue;
        c.turnCounter -= 1;
        if (c.turnCounter <= 0) expire.push(c.uid);
      }
    }
    for (const uid of expire) this.sendToGraveyard(uid);
  }

  /**
   * Flips a face-down monster face-up by card effect (Stop Defense, Swords of
   * Revealing Light). Meeting the Flip condition this way triggers the monster's Flip
   * Effect, and it is the monster's *own* controller who resolves it — which is what
   * makes flipping an opponent's Man-Eater Bug or Hane-Hane a bad idea.
   */
  private flipFaceUp(uid: string): void {
    const found = findCard(this.state, uid);
    if (!found || found.location.zone !== 'monsters') return;
    const m = found.instance;
    if (m.faceUp) return;
    m.faceUp = true;
    this.emit({ type: EventType.CARD_FLIPPED, cardUid: m.uid, cardId: m.cardId, faceUp: true });
    // Swords of Revealing Light (and Stop Defense) flip during their own
    // resolution. A Flip Effect that starts a chain here would nest inside
    // that link. Queue it: after this chain finishes, flushQueuedFlips runs
    // Trap Master / Reaper as a new chain, which is the 2002 timing.
    if (
      this.flipChoiceOpen() ||
      this.resolvingChain ||
      this.state.chain.length > 0 ||
      this.state.pendingPriority !== null
    ) {
      this.queueFlipEffect(found.location.seat, m);
      return;
    }
    this.runFlipEffect(found.location.seat, m);
  }

  /** True while a human Flip decision is already on screen. */
  private flipChoiceOpen(): boolean {
    return this.state.pendingFlipEffect !== null || this.state.pendingTargets !== null;
  }

  /** Remember a Flip Effect that cannot open a second window yet. */
  private queueFlipEffect(seat: Seat, card: CardInstance): void {
    if (engineEffects.getByTrigger(Number(card.cardId), EngineEffectTrigger.FLIP).length === 0) return;
    if (this.queuedFlips.some((job) => job.uid === card.uid)) return;
    this.queuedFlips.push({ uid: card.uid, seat });
  }

  /**
   * Resolve Flip Effects that waited for another window, or for the chain
   * that flipped them (Swords of Revealing Light) to finish. One at a time,
   * in the order the monsters were flipped.
   */
  private flushQueuedFlips(): void {
    while (
      this.queuedFlips.length > 0 &&
      this.state.status === 'active' &&
      !this.flipChoiceOpen() &&
      !this.resolvingChain &&
      this.state.chain.length === 0 &&
      this.state.pendingPriority === null
    ) {
      const job = this.queuedFlips.shift();
      if (!job) return;
      const found = findCard(this.state, job.uid);
      if (!found) continue;
      this.runFlipEffect(found.location.seat, found.instance);
    }
  }

  /** Sends every field spell on the board (either side) to the graveyard, enforcing
   * the rule that only one field spell may be active at a time. */
  private clearActiveFieldSpells(): void {
    this.clearOtherFieldSpells(null);
  }

  /**
   * Sends every field spell except `keepUid` to the graveyard. Passing null
   * clears the whole board (used when a new field is activated from the hand).
   */
  private clearOtherFieldSpells(keepUid: string | null): void {
    for (const seat of [0, 1] as Seat[]) {
      const fs = this.state.zones[seat].fieldSpell;
      if (fs && fs.uid !== keepUid) this.sendToGraveyard(fs.uid);
    }
  }

  /** True while a face-up Swords of Revealing Light on the opposing field locks `seat`. */
  private swordsLocking(seat: Seat): boolean {
    return getSpellTraps(this.state.zones[otherSeat(seat)]).some(
      (c) => c.faceUp && Number(c.cardId) === 72302403,
    );
  }

  /**
   * Upkeep for Swords of Revealing Light: the lock runs for three of the victim's
   * turns, but only while the card imposing it is still face-up on the field. Lifting
   * it here covers every way that card can leave (destroyed by an effect, De-Spell,
   * control change) without each removal path needing to know about the lock.
   */
  private recomputeAttackLocks(): void {
    for (const seat of [0, 1] as Seat[]) {
      if (this.state.zones[seat].attackLockTurns > 0 && !this.swordsLocking(seat)) {
        this.state.zones[seat].attackLockTurns = 0;
      }
    }
  }

  /** Sends any face-up Swords of Revealing Light on a seat's field to the graveyard. */
  private expireSwords(seat: Seat): void {
    for (const c of getSpellTraps(this.state.zones[seat])) {
      if (c.faceUp && Number(c.cardId) === 72302403) this.sendToGraveyard(c.uid);
    }
  }

  /** True while any face-up Dragon Capture Jar sits on either field. */
  private dragonLockActive(): boolean {
    return ([0, 1] as Seat[]).some((s) =>
      getSpellTraps(this.state.zones[s]).some((c) => c.faceUp && Number(c.cardId) === 50045299),
    );
  }

  /** Continuous trap upkeep: locks Dragons in Defense while Dragon Capture Jar is up. */
  private recomputeContinuousTraps(): void {
    if (!this.dragonLockActive()) return;
    for (const seat of [0, 1] as Seat[]) {
      for (const m of getMonsters(this.state.zones[seat])) {
        if (
          m.faceUp &&
          getCardData(this.library, m.cardId).race === 'Dragon' &&
          m.position !== CardPosition.DEFENSE
        ) {
          const from = m.position;
          m.position = CardPosition.DEFENSE;
          this.emit({ type: EventType.POSITION_CHANGED, cardUid: m.uid, position: m.position });
          this.noteBattlePosition(m.uid, from);
        }
      }
    }
  }

  /** Queues a position trigger when this monster's printed text cares. */
  private noteBattlePosition(uid: string, from: CardPosition): void {
    const found = findCard(this.state, uid);
    if (!found || found.location.zone !== 'monsters') return;
    const card = found.instance;
    const kind = positionTriggerOf(card.cardId, from, card.position);
    if (!kind) return;
    if (this.positionJobs.some((job) => job.uid === uid && job.kind === kind)) return;
    this.positionJobs.push({
      uid,
      cardId: card.cardId,
      seat: found.location.seat,
      kind,
    });
  }

  private positionTriggerBlocked(): boolean {
    return (
      this.state.status !== 'active' ||
      this.resolvingChain ||
      this.state.pendingPriority !== null ||
      this.state.pendingTargets !== null ||
      this.state.pendingFlipEffect !== null ||
      this.state.pendingDiscard !== null ||
      this.state.pendingCoinCall !== null ||
      this.state.pendingChoice !== null ||
      this.state.pendingChainOrder !== null ||
      this.state.pendingOfferingSummon !== null ||
      this.state.chain.length > 0
    );
  }

  /** Resolves queued position triggers once the turn is idle. */
  private flushPositionTriggers(): void {
    if (this.positionJobs.length === 0 || this.positionTriggerBlocked()) return;
    const jobs = this.positionJobs.splice(0);
    for (let i = 0; i < jobs.length; i++) {
      const opened = this.resolvePositionTrigger(jobs[i]!);
      if (opened || this.positionTriggerBlocked()) {
        this.positionJobs.unshift(...jobs.slice(i + 1));
        return;
      }
    }
  }

  /**
   * Returns true when the player still has to choose. Mandatory effects
   * with no legal monster do nothing. Yado Karu is optional.
   */
  private resolvePositionTrigger(job: {
    uid: string;
    cardId: string;
    seat: Seat;
    kind: PositionTrigger;
  }): boolean {
    if (job.kind === 'tainted') {
      this.flashPositionSource(job.uid, job.cardId);
      if (this.shuffleDeck(job.seat)) {
        this.emit({ type: EventType.DECK_SHUFFLED, seat: job.seat });
      }
      this.message(
        'taintedWisdom',
        { cardId: job.cardId },
        'The Deck was shuffled.',
      );
      return false;
    }
    if (job.kind === 'yado') {
      const hand = this.state.zones[job.seat].hand.map((card) => card.uid);
      if (hand.length === 0) return false;
      if (this.state.seats[job.seat].isAI) return false;
      this.openPositionWindow(job, hand, 'bottom');
      return true;
    }
    const targets = this.opponentMonsterUids(job.seat);
    if (targets.length === 0) {
      this.flashPositionSource(job.uid, job.cardId);
      if (job.kind === 'crass') {
        this.message('crassNoTarget', { cardId: job.cardId }, 'No monster to return.');
      } else {
        this.message('dreamNoTarget', { cardId: job.cardId }, 'No monster to destroy.');
      }
      return false;
    }
    // One legal monster is not a choice. The AI also picks when there are several.
    if (this.state.seats[job.seat].isAI || targets.length === 1) {
      const uid = this.pickStrongestMonster(targets);
      this.flashPositionSource(job.uid, job.cardId);
      if (job.kind === 'crass') {
        if (uid) this.returnToHand(uid);
        this.message('crassClown', { cardId: job.cardId }, 'A monster returned to the hand.');
      } else if (uid && this.destroyCard(uid)) {
        this.message('dreamClown', { cardId: job.cardId }, 'A monster was destroyed.');
      }
      this.checkWin();
      return false;
    }
    this.flashPositionSource(job.uid, job.cardId);
    this.openPositionWindow(job, targets, job.kind === 'crass' ? 'return' : 'destroy');
    return true;
  }

  private flashPositionSource(uid: string, cardId: string): void {
    this.emit({
      type: EventType.EFFECT_RESOLVED,
      cardUid: uid,
      detail: getCardData(this.library, cardId).name,
    });
  }

  private opponentMonsterUids(seat: Seat): string[] {
    const api = this.makeApi(seat, { uid: 'pos', cardId: String(CRASS_CLOWN) } as CardInstance);
    return getMonsters(this.state.zones[otherSeat(seat)])
      .filter((monster) => !isDragonProtected(api, monster.uid))
      .map((monster) => monster.uid);
  }

  /** Face-up monsters by ATK, then a face-down one if that is all there is. */
  private pickStrongestMonster(uids: string[]): string | undefined {
    let best: string | undefined;
    let bestAtk = -1;
    let faceDown: string | undefined;
    for (const uid of uids) {
      const found = findCard(this.state, uid);
      if (!found) continue;
      if (!found.instance.faceUp) {
        faceDown ??= uid;
        continue;
      }
      const atk = getModifiedAtk(found.instance, this.library, this.state.reverseStatMods);
      if (atk > bestAtk) {
        bestAtk = atk;
        best = uid;
      }
    }
    return best ?? faceDown;
  }

  private openPositionWindow(
    job: { uid: string; cardId: string; seat: Seat },
    candidateUids: string[],
    mode: 'return' | 'destroy' | 'bottom',
  ): void {
    this.priorityCounter += 1;
    const name = getCardData(this.library, job.cardId).name;
    const min = mode === 'bottom' ? 0 : 1;
    const max = mode === 'bottom' ? candidateUids.length : 1;
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: job.seat,
      sourceUid: job.uid,
      cardId: job.cardId,
      prompt: `${name}: choose`,
      candidateUids,
      min,
      max,
      ...(mode === 'return' ? { returnChosenToHand: true } : {}),
      ...(mode === 'destroy' ? { destroyChosenMonster: true } : {}),
      ...(mode === 'bottom' ? { toDeckBottom: true } : {}),
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat: job.seat,
      prompt: this.state.pendingTargets.prompt,
      candidateUids,
      min,
      max,
      sourceUid: job.uid,
      sourceCardId: job.cardId,
    });
  }

  /** Yado Karu: first chosen sits highest among the returned cards. */
  /**
   * Big Eye. The human orders the top of their Deck. The AI puts the
   * card it wants next on top. June 2002 text is the 5 cards, not a
   * chosen count.
   */
  private orderDeckTop(seat: Seat, source: CardInstance, maxCount: number): void {
    const deck = this.state.zones[seat].deck;
    const count = Math.min(maxCount, deck.length);
    if (count <= 0) return;
    const top = deck.slice(0, count);
    if (this.state.seats[seat].isAI) {
      this.placeDeckTop(seat, this.bigEyeOrder(seat, top));
      return;
    }
    this.priorityCounter += 1;
    this.state.pendingTargets = {
      requestId: `deck-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: source.uid,
      cardId: source.cardId,
      prompt: 'Put the cards back. The first one is the top of your Deck.',
      candidateUids: top.map((card) => card.uid),
      min: count,
      max: count,
      reorderDeckTop: true,
    };
  }

  /** First uid becomes the top of the Deck (the next card drawn). */
  private placeDeckTop(seat: Seat, orderedUids: string[]): void {
    const deck = this.state.zones[seat].deck;
    const byUid = new Map(deck.map((card) => [card.uid, card]));
    const top = orderedUids.map((uid) => byUid.get(uid)).filter((card): card is CardInstance => !!card);
    const chosen = new Set(top.map((card) => card.uid));
    const rest = deck.filter((card) => !chosen.has(card.uid));
    this.state.zones[seat].deck = [...top, ...rest];
    const cards = top.map((card) => ({ uid: card.uid, cardId: card.cardId }));
    this.rememberCards(seat, cards, false);
    this.emit({ type: EventType.PEEK_CARDS, seat, cards });
  }

  /**
   * Shield & Sword. Face-up monsters exchange printed ATK and DEF until
   * this turn ends. A monster already switched stays switched.
   */
  private swapPrintedStats(source: CardInstance): void {
    let any = false;
    for (const side of [0, 1] as Seat[]) {
      for (const monster of getMonsters(this.state.zones[side])) {
        if (!monster.faceUp || monster.baseSwapped) continue;
        monster.baseSwapped = true;
        any = true;
        this.emitStat(monster);
      }
    }
    if (!any) return;
    const name = getCardData(this.library, source.cardId).name;
    this.message(
      'shieldSword',
      { cardId: source.cardId },
      `${name}: original ATK and DEF are switched until the end of this turn.`,
    );
  }

  private placeHandCardsUnderDeck(seat: Seat, uids: string[]): number {
    const hand = this.state.zones[seat].hand;
    const deck = this.state.zones[seat].deck;
    let placed = 0;
    for (const uid of uids) {
      const idx = hand.findIndex((card) => card.uid === uid);
      if (idx < 0) continue;
      const [card] = hand.splice(idx, 1);
      if (!card) continue;
      card.faceUp = false;
      card.position = CardPosition.ATTACK;
      deck.push(card);
      this.emit({
        type: EventType.CARD_MOVED,
        cardUid: card.uid,
        from: { seat, zone: 'hand' },
        to: { seat, zone: 'deck', index: deck.length - 1 },
      });
      placed += 1;
    }
    return placed;
  }

  /**
   * When a monster is summoned face-up, the opponent (then the summoner) may
   * start a chain. Trap Hole still gates itself on `responseTrigger`.
   */
  private checkSummonTraps(
    summonerSeat: Seat,
    summoned: CardInstance,
    kind: 'normal' | 'flip' | 'special' = 'normal',
  ): void {
    this.summonKind = kind;
    if (!summoned.faceUp) return;
    if (this.resolvingChain) return;
    if (this.state.pendingFlipEffect || this.state.pendingTargets) return;
    if (this.state.chain.length > 0 || this.state.pendingPriority) return;

    // Field-spell boosts normally settle at the end of the action, but Trap Hole
    // reads ATK the moment the summon succeeds (900 + 200 field = 1100).
    this.recomputeFieldSpells();
    this.state.chainTriggerUid = summoned.uid;
    this.state.chainPasses = 0;
    this.openResponseWindow(otherSeat(summonerSeat));
  }

  /**
   * 2002: once a trap has sat a turn, its owner may activate it on the opponent's
   * turn. Standby is the first legal phase. Battle Phase uses Start / End Step
   * windows instead of this once-per-phase prompt. Only `always` mode opens
   * these empty phase windows.
   */
  private offerOppFastTraps(): void {
    if (this.state.status !== 'active') return;
    if (
      this.state.pendingPriority ||
      this.state.pendingDiscard ||
      this.state.pendingFlipEffect ||
      this.state.pendingTargets ||
      this.state.pendingChoice ||
      this.state.chain.length > 0
    ) {
      return;
    }
    const phase = this.state.phase;
    if (
      phase !== EnginePhase.STANDBY &&
      phase !== EnginePhase.MAIN_1 &&
      phase !== EnginePhase.MAIN_2
    ) {
      return;
    }
    const opp = otherSeat(this.state.turnPlayer);
    if (this.state.responseMode[opp] !== 'always') return;
    const key = `${this.state.absoluteTurnCount}:${phase}`;
    if (this.fastTrapWindowKey === key) return;
    this.fastTrapWindowKey = key;
    // One seat only (the opponent). A pass or an empty list closes the window.
    this.state.chainPasses = 1;
    this.openResponseWindow(opp);
  }

  private spellSpeedOf(card: CardInstance): SpellSpeed {
    const id = Number(card.cardId);
    if (COUNTER_TRAP_IDS.has(id)) return 3;
    if (QUICK_MONSTER_IDS.has(id)) return 2;
    return kindOf(card, this.library) === CardKind.TRAP ? 2 : 1;
  }

  private cardHasDamageStepEffect(card: CardInstance): boolean {
    return this.activationEffects(Number(card.cardId)).some((e) => e.damageStep);
  }

  /**
   * Attack declared on a Set monster that has not been flipped yet. Damage
   * Step cards (and anything that changes ATK/DEF) wait until that reveal.
   */
  private setAttackStillHidden(): boolean {
    const pending = this.state.pendingAttack;
    if (!pending || pending.stage !== 'declared' || !pending.targetUid) return false;
    const target = getMonsters(this.state.zones[otherSeat(pending.seat)]).find(
      (m) => m.uid === pending.targetUid,
    );
    return !!target && !target.faceUp;
  }

  private cardLegalInCurrentStep(card: CardInstance): boolean {
    if (this.setAttackStillHidden() && this.cardHasDamageStepEffect(card)) return false;
    if (this.state.battleStep !== 'DAMAGE') return true;
    if (this.cardHasDamageStepEffect(card)) return true;
    // Spell Speed 3 chains to a Damage Step activation (Castle Walls,
    // Reinforcements, Reverse Trap). Horn of Heaven still fails
    // canActivate: this window is not a summon.
    if (COUNTER_TRAP_IDS.has(Number(card.cardId))) return true;
    // Waboku is asked at declaration and does not open the Damage Step.
    // A boost played there changes the battle, so Minimal and Auto ask to chain it.
    return this.wabokuChainsToDamageBoost(card);
  }

  /** Waboku may answer a Damage Step ATK/DEF modifier already on the chain. */
  private wabokuChainsToDamageBoost(card: CardInstance): boolean {
    if (Number(card.cardId) !== STARTER_IDS.WABOKU) return false;
    const last = this.state.chain[this.state.chain.length - 1];
    if (!last) return false;
    const id = Number(last.cardId);
    return (
      id === STARTER_IDS.REINFORCEMENTS ||
      id === STARTER_IDS.CASTLE_WALLS ||
      id === STARTER_IDS.REVERSE_TRAP
    );
  }

  /**
   * Set traps this seat may flip as the next chain link (or as CL1 of a window).
   * Spells (Spell Speed 1) never appear here: they start a chain only in the
   * controller's Main Phase via ACTIVATE_SPELL_FROM_HAND / ACTIVATE_SET_SPELL.
   *
   * Face-up Ultimate Offering is not a chain link. On the opponent's Battle
   * Phase its ignition is listed in the same window so the defender can pay
   * 500 before an attack or before damage. It is omitted mid-chain and in
   * the Damage Step.
   */
  private eligibleChainResponses(seat: Seat): string[] {
    const last = this.state.chain[this.state.chain.length - 1];
    const onChain = new Set(this.state.chain.map((l) => l.sourceUid));
    const eligible: string[] = [];
    for (const t of getSpellTraps(this.state.zones[seat])) {
      if (kindOf(t, this.library) !== CardKind.TRAP) continue;
      if (t.faceUp || t.setOnTurn >= this.state.absoluteTurnCount) continue;
      if (onChain.has(t.uid)) continue;
      if (last && this.spellSpeedOf(t) < last.spellSpeed) continue;
      if (!this.cardLegalInCurrentStep(t)) continue;
      const usable = this.withResponseTrigger(this.state.chainTriggerUid, () =>
        this.cardIsActivatable(seat, t) && this.cardIsRelevantNow(seat, t),
      );
      if (usable) eligible.push(t.uid);
    }
    for (const uid of this.faceUpOfferingIgnitionUids(seat)) {
      if (!eligible.includes(uid)) eligible.push(uid);
    }
    for (const uid of this.quickMonsterUids(seat)) {
      if (!eligible.includes(uid)) eligible.push(uid);
    }
    return eligible;
  }

  /** Kuriboh in hand, and a guardian that is being attacked. */
  private quickMonsterUids(seat: Seat): string[] {
    if (this.state.battleStep !== 'DAMAGE') return [];
    const uids: string[] = [];
    const z = this.state.zones[seat];
    for (const c of z.hand) {
      if (Number(c.cardId) !== KURIBOH) continue;
      if (this.cardIsActivatable(seat, c) && this.cardIsRelevantNow(seat, c)) uids.push(c.uid);
    }
    const attack = this.state.pendingAttack;
    for (const m of getMonsters(z)) {
      if (!QUICK_MONSTER_IDS.has(Number(m.cardId)) || Number(m.cardId) === KURIBOH) continue;
      if (!m.faceUp || m.faceUpEffectUsed) continue;
      if (this.state.turnPlayer === seat) continue;
      if (attack?.targetUid !== m.uid) continue;
      if (this.cardIsActivatable(seat, m) && this.cardIsRelevantNow(seat, m)) uids.push(m.uid);
    }
    return uids;
  }

  /**
   * Face-up Ultimate Offering this seat may pay right now: opponent's Battle
   * Phase, not the Damage Step, not while a chain is building, with 500 LP
   * and a monster that can be Normal Summoned or Tribute Summoned.
   */
  private faceUpOfferingIgnitionUids(seat: Seat): string[] {
    if (this.state.chain.length > 0 || this.state.chainTriggerUid) return [];
    if (this.state.phase !== EnginePhase.BATTLE) return [];
    if (this.state.turnPlayer === seat) return [];
    const z = this.state.zones[seat];
    if (z.lifePoints < 500) return [];
    if (z.extraNormalSummons > 0) return [];
    if (!canNormalOrTributeFromHand(this.state, seat, this.library)) return [];
    if (
      !canIgniteUltimateOffering(
        this.state.turnPlayer,
        this.state.phase,
        seat,
        this.state.battleStep,
      )
    ) {
      return [];
    }
    const uids: string[] = [];
    for (const c of getSpellTraps(z)) {
      if (!c.faceUp || Number(c.cardId) !== STARTER_IDS.ULTIMATE_OFFERING) continue;
      uids.push(c.uid);
    }
    return uids;
  }

  private chainWindowPrompt(): { prompt: string; promptCode: DuelPromptCode } {
    const last = this.state.chain[this.state.chain.length - 1];
    if (last) {
      return {
        prompt: `Chain to ${getCardData(this.library, last.cardId).name}?`,
        promptCode: 'chainTo',
      };
    }
    if (this.state.chainTriggerUid) {
      return {
        prompt: 'Activate a Trap in response to the summon?',
        promptCode: 'trapOnSummon',
      };
    }
    if (this.state.pendingAttack?.stage === 'declared') {
      return {
        prompt: 'Activate a Trap in response to the attack?',
        promptCode: 'trapOnAttack',
      };
    }
    if (this.state.battleStep === 'DAMAGE') {
      return {
        prompt: 'Activate a Trap before damage calculation?',
        promptCode: 'trapOnDamage',
      };
    }
    return { prompt: 'Activate a Trap?', promptCode: 'trapActivate' };
  }

  /**
   * Minimal mode pauses only when a Set trap answers the play that just
   * happened. `relevantNow` is that answer (Trap Hole on a qualifying summon,
   * Waboku and the battle boosts on an attack, Dragon Capture Jar while the
   * opponent has a face-up Dragon, and a Counter Trap when the opponent
   * activated or summoned something it can negate). A Set Ultimate Offering
   * does, on the opponent's Battle Phase: once at the start, and again when
   * an attack is declared. Not the Damage Step, not a summon, and not while
   * a chain is already open.
   *
   * Battle Phase entry stays quiet except for a Set Just Desserts and a Set
   * Ultimate Offering: the defender is asked once, and the prompt lists only
   * those cards (plus a face-up Ultimate Offering). Waboku and the battle
   * boosts still wait for an attack. A face-up Ultimate Offering ignition
   * still pauses. An attack on a face-up monster asks once, at declaration.
   * An attack on a Set monster waits for the flip when the only answer is a
   * Damage Step card, then asks once with ATK and DEF visible. If Waboku is
   * also Set, that card is asked at declaration, and a remaining boost is
   * asked again after the flip. Activating that boost reopens Waboku, in
   * Minimal and Auto, so it can be chained. A Set Robbin' Goblin asks at
   * that declaration when the rebound would deal battle damage, not after
   * the flip: it is not a Damage Step card.
   */
  private minimalWindowWorthOpening(seat: Seat, uids: string[]): boolean {
    if (uids.some((uid) => this.isFaceUpOffering(seat, uid))) return true;
    if (
      this.setOfferingOpensMinimal(seat) &&
      uids.some((uid) => this.isSetOffering(seat, uid))
    ) {
      return true;
    }
    // A Graveyard trigger is Chain Link 1. A trap that can be activated now
    // may chain to it, even when that trap is not answering a summon or attack.
    if (uids.length > 0 && this.state.chain.some((link) => link.triggerKind)) return true;

    if (
      this.defenderBattleStart(seat) &&
      uids.some((uid) => this.isSetJustDesserts(seat, uid))
    ) {
      return true;
    }

    if (
      this.attackerBattleStart(seat) &&
      uids.some((uid) => this.isSetRobbinGoblin(seat, uid))
    ) {
      return true;
    }

    // Kuriboh in hand and a guardian under attack are not Set traps, but
    // their eligibility already ran `relevantNow` (damage incoming / targeted).
    if (
      this.state.battleStep === 'DAMAGE' &&
      uids.some((uid) => this.isQuickMonsterResponse(seat, uid))
    ) {
      return true;
    }

    const answers = uids.some((uid) => this.cardAnswersPlay(seat, uid));
    if (!answers) return false;

    if (this.state.chain.length > 0) return true;

    if (this.state.battleStep === 'DAMAGE') {
      return !!this.state.pendingAttack?.revealedByAttack;
    }

    if (this.state.chainTriggerUid || this.state.pendingAttack) return true;

    return false;
  }

  /**
   * A Set trap whose effect declares `relevantNow`. Eligibility already ran
   * that gate, so being in the window list means the gate currently passes.
   * Robbin' Goblin is the exception: Minimal opens it at the start of your
   * Battle Phase (`attackerBattleStart`), and when the opponent attacks a
   * Set Defense monster of yours. Your own attack does not open a window
   * alone; it may sit in a window another card already opened.
   */
  private cardAnswersPlay(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c?.uid === uid);
    if (!card || card.faceUp) return false;
    if (this.isSetRobbinGoblin(seat, uid)) return this.robbinGoblinAnswersSetDefense(seat);
    return this.activationEffects(Number(card.cardId)).some((effect) => effect.relevantNow);
  }

  /**
   * Offers priority starting at `first`. Empty legal lists count as a pass.
   * Two consecutive passes resolve the chain or close an empty window.
   */
  private openResponseWindow(first: Seat): void {
    this.offerChainPriority(first);
  }

  private offerChainPriority(first: Seat): void {
    if (this.state.status !== 'active') return;
    if (this.state.pendingPriority || this.state.pendingTargets) return;

    let seat = first;
    for (let n = 0; n < 2; n++) {
      if (this.state.chainPasses >= 2) break;
      if (this.state.chain.length >= MAX_CHAIN_LINKS) break;
      let uids = this.eligibleChainResponses(seat);
      if (uids.length === 0) {
        this.state.chainPasses += 1;
        seat = otherSeat(seat);
        continue;
      }
      if (this.state.seats[seat].isAI) {
        const before = this.state.chain.length;
        this.aiRespondChain(seat, uids);
        if (this.state.pendingTargets || this.state.pendingPriority) return;
        if (this.state.chain.length > before) {
          this.advanceChain();
          return;
        }
        this.noteBattlePass(uids);
        this.state.chainPasses += 1;
        seat = otherSeat(seat);
        continue;
      }
      if (this.state.responseMode[seat] === 'off') {
        uids = uids.filter((uid) => this.isFaceUpOffering(seat, uid));
        if (uids.length === 0) {
          this.state.chainPasses += 1;
          seat = otherSeat(seat);
          continue;
        }
      }
      if (
        this.state.responseMode[seat] === 'minimal' &&
        this.defenderBattleStart(seat)
      ) {
        uids = uids.filter(
          (uid) =>
            this.isFaceUpOffering(seat, uid) ||
            this.isSetJustDesserts(seat, uid) ||
            this.isSetOffering(seat, uid),
        );
      }
      if (
        this.state.responseMode[seat] === 'minimal' &&
        this.attackerBattleStart(seat)
      ) {
        uids = uids.filter((uid) => this.isSetRobbinGoblin(seat, uid));
      }
      if (
        this.state.responseMode[seat] === 'minimal' &&
        !this.minimalWindowWorthOpening(seat, uids)
      ) {
        this.state.chainPasses += 1;
        seat = otherSeat(seat);
        continue;
      }
      this.openHumanPriority(seat, uids);
      return;
    }

    if (this.state.chain.length > 0) {
      this.resolveChain();
      return;
    }
    this.continueAfterEmptyWindow();
  }

  private openHumanPriority(responder: Seat, activatableUids: string[]): void {
    this.priorityCounter += 1;
    const requestId = `pri-${this.priorityCounter}`;
    const { prompt, promptCode } = this.chainWindowPrompt();
    const last = this.state.chain[this.state.chain.length - 1];
    this.state.pendingPriority = {
      requestId,
      responder,
      prompt,
      promptCode,
      activatableUids,
      triggerUid: this.state.chainTriggerUid,
      ...(last ? { chainLink: last.link } : {}),
    };
    this.emit({
      type: EventType.REQUEST_PRIORITY,
      requestId,
      seat: responder,
      prompt,
      promptCode,
      activatableUids,
    });
  }

  private isQuickMonsterResponse(seat: Seat, uid: string): boolean {
    const z = this.state.zones[seat];
    const card =
      z.hand.find((c) => c.uid === uid) ??
      getMonsters(z).find((c) => c.uid === uid);
    return !!card && QUICK_MONSTER_IDS.has(Number(card.cardId));
  }

  private aiRespondChain(responder: Seat, activatableUids: string[]): void {
    const emptyFast =
      this.state.chain.length === 0 &&
      !this.state.chainTriggerUid &&
      !this.state.pendingAttack;
    if (emptyFast) return;
    const attack = this.state.pendingAttack;
    if (attack && this.state.battleStep === 'DAMAGE' && attack.targetUid) {
      for (const uid of activatableUids) {
        const monster = getMonsters(this.state.zones[responder]).find((m) => m.uid === uid);
        if (!monster || attack.targetUid !== monster.uid) continue;
        const id = Number(monster.cardId);
        if (id !== SANGA && id !== KAZEJIN && id !== SUIJIN) continue;
        const attacker = findCard(this.state, attack.attackerUid)?.instance;
        if (!attacker || getModifiedAtk(attacker, this.library, this.state.reverseStatMods) <= 0) {
          continue;
        }
        const atk = getModifiedAtk(attacker, this.library, this.state.reverseStatMods);
        const defending = monster.position === CardPosition.DEFENSE;
        const bar = defending
          ? getModifiedDef(monster, this.library, this.state.reverseStatMods)
          : getModifiedAtk(monster, this.library, this.state.reverseStatMods);
        const dies = defending ? atk > bar : atk >= bar;
        if (!dies) continue;
        try {
          this.activateQuickMonster(responder, monster);
          return;
        } catch {
          // The guardian can no longer answer. Try a trap instead.
        }
      }
    }
    if (this.state.battleStep === 'DAMAGE') {
      for (const uid of activatableUids) {
        const card = this.state.zones[responder].hand.find((c) => c.uid === uid);
        if (!card || Number(card.cardId) !== KURIBOH) continue;
        if (!shouldDropKuriboh(this.state, this.library, responder)) continue;
        try {
          this.activateQuickMonster(responder, card);
          return;
        } catch {
          // Kuriboh can no longer answer.
        }
      }
    }
    for (const choice of planAiChainResponses(
      this.state, this.library, responder, activatableUids,
    )) {
      const targets = choice.targets ?? this.aiChainTargets(responder, choice.uid);
      try {
        this.withResponseTrigger(this.state.chainTriggerUid, () =>
          this.activateSetTrap(responder, choice.uid, targets),
        );
        return;
      } catch {
        // This trap can no longer be activated — try the next candidate
        // instead of passing the whole window.
      }
    }
  }

  private aiChainTargets(seat: Seat, cardUid: string): string[] | undefined {
    const found = findCard(this.state, cardUid);
    if (!found) return undefined;
    const cardId = Number(found.instance.cardId);
    if (cardId === TWO_PRONGED_ID) {
      return twoProngedTradeForAi(this.state, this.library, seat) ?? undefined;
    }
    const triggerUid = this.state.chainTriggerUid;
    if (!triggerUid) return undefined;
    const api = this.makeApi(seat, found.instance);
    const effect = this.activationEffects(cardId).find(
      (e) => e.requiresTarget && (!e.canActivate || e.canActivate(api)),
    );
    if (effect?.canTarget && effect.canTarget(api, triggerUid)) return [triggerUid];
    return undefined;
  }

  /** A pass during an attack means those Sets declined a battle trap. */
  private noteBattlePass(activatableUids: readonly string[]): void {
    if (!this.state.pendingAttack) return;
    if (!this.state.battlePassedUids) this.state.battlePassedUids = [];
    const seen = new Set(this.state.battlePassedUids);
    for (const id of activatableUids) {
      if (seen.has(id)) continue;
      this.state.battlePassedUids.push(id);
      seen.add(id);
    }
  }

  /** Peek or public reveal. Only these ids may later be read while face-down. */
  private rememberCards(
    seat: Seat,
    cards: { uid: string; cardId: string }[],
    reveal: boolean,
  ): void {
    if (!this.state.aiKnownCards) this.state.aiKnownCards = [[], []];
    const seats: Seat[] = reveal ? [0, 1] : [seat];
    for (const viewer of seats) {
      const list = this.state.aiKnownCards[viewer];
      for (const card of cards) {
        if (!card.cardId) continue;
        const hit = list.find((known) => known.uid === card.uid);
        if (hit) hit.cardId = card.cardId;
        else list.push({ uid: card.uid, cardId: card.cardId });
      }
    }
  }

  /**
   * A pass on a summon of face-up ATK >= 1000 means every offered Set declined
   * Trap Hole. Attack windows and small summons do not count. The search clone
   * records this too, then gets thrown away; the live list is what a real pass writes.
   */
  private noteTrapHolePass(activatableUids: readonly string[]): void {
    if (this.state.pendingAttack) return;
    const uid = this.state.chainTriggerUid;
    if (!uid) return;
    const found = findCard(this.state, uid);
    if (!found?.instance.faceUp) return;
    if (getModifiedAtk(found.instance, this.library, this.state.reverseStatMods) < 1000) {
      return;
    }
    if (!this.state.trapHolePassedUids) this.state.trapHolePassedUids = [];
    const seen = new Set(this.state.trapHolePassedUids);
    for (const id of activatableUids) {
      if (seen.has(id)) continue;
      this.state.trapHolePassedUids.push(id);
      seen.add(id);
    }
  }

  /** Handles a player's answer to an open trap-response / chain window. */
  private findQuickMonster(seat: Seat, uid: string): CardInstance | null {
    const z = this.state.zones[seat];
    const card =
      z.hand.find((c) => c.uid === uid) ??
      getMonsters(z).find((c) => c.uid === uid) ??
      null;
    if (!card || !QUICK_MONSTER_IDS.has(Number(card.cardId))) return null;
    return card;
  }

  /** Kuriboh discards itself. A guardian stays and marks its once-while-face-up effect. */
  private activateQuickMonster(seat: Seat, card: CardInstance, boost?: 'atk' | 'def'): void {
    this.assertActivatable(seat, card);
    if (!this.cardLegalInCurrentStep(card)) {
      throw new EngineError('This card cannot be activated in the Damage Step', 'noDamageStep');
    }
    if (Number(card.cardId) === KURIBOH) {
      this.sendToGraveyard(card.uid);
    } else {
      card.faceUpEffectUsed = true;
      this.emit({
        type: EventType.EFFECT_RESOLVED,
        cardUid: card.uid,
        detail: getCardData(this.library, card.cardId).name,
      });
    }
    this.commitActivationToChain(seat, card, undefined, undefined, undefined, boost);
  }

  /** Counter Trap costs are paid when the card joins the chain. */
  private payCounterCost(seat: Seat, card: CardInstance, targetUids?: string[]): void {
    const id = Number(card.cardId);
    const lp = this.state.zones[seat].lifePoints;
    if (id === SOLEMN_JUDGMENT) {
      const pay = Math.floor(lp / 2);
      if (pay > 0) this.changeLifePoints(seat, -pay);
      return;
    }
    if (id === SEVEN_TOOLS) {
      if (lp < 1000) throw new EngineError('Not enough Life Points', 'notEnoughLifePoints');
      this.changeLifePoints(seat, -1000);
      return;
    }
    if (id === MAGIC_JAMMER) {
      const hand = this.state.zones[seat].hand.filter((c) => c.uid !== card.uid);
      const chosen = targetUids?.map((uid) => hand.find((c) => c.uid === uid)).find(Boolean);
      const picked = this.state.seats[seat].isAI
        ? aiLowValueDiscard(this.state, this.library, seat)
        : undefined;
      const discard = chosen ?? (picked ? hand.find((c) => c.uid === picked) : undefined);
      if (!discard) throw new EngineError('Discard 1 card', 'discardRequired');
      this.sendToGraveyard(discard.uid);
      return;
    }
    if (id === SHARE_THE_PAIN) {
      const mine = getMonsters(this.state.zones[seat]);
      const chosen = targetUids ? mine.find((monster) => targetUids.includes(monster.uid)) : undefined;
      const tribute =
        chosen ??
        (this.state.seats[seat].isAI ? mine.reduce((best, monster) =>
          getModifiedAtk(monster, this.library, false) < getModifiedAtk(best, this.library, false) ? monster : best,
        ) : undefined);
      if (!tribute) throw new EngineError('Tribute 1 monster', 'tributeRequired');
      this.sendToGraveyard(tribute.uid);
      return;
    }
    if (id === HORN_OF_HEAVEN) {
      const mine = getMonsters(this.state.zones[seat]);
      const chosen = targetUids
        ? mine.find((m) => targetUids.includes(m.uid))
        : undefined;
      const tribute =
        chosen ??
        (this.state.seats[seat].isAI
          ? mine.reduce((best, m) => (getModifiedAtk(m, this.library, false) < getModifiedAtk(best, this.library, false) ? m : best))
          : undefined);
      if (!tribute) throw new EngineError('Tribute 1 monster', 'tributeRequired');
      this.sendToGraveyard(tribute.uid);
    }
  }

  /**
   * Insect Soldiers of the Sky gains 1000 ATK for this damage calculation
   * when the defender is WIND. The Set defender was just turned face-up.
   */
  private applyInsectSoldiersBoost(attacker: CardInstance, target: CardInstance): void {
    if (!this.insectSoldiersHitsWind(attacker, target)) return;
    attacker.statModifiers.push({ atk: INSECT_SOLDIERS_BOOST, def: 0, source: `step:${attacker.uid}` });
    // A face-up WIND was already counted up before the lunge. A monster that
    // was Set is revealed in this same batch, so the count plays after the flip.
    if (this.state.pendingAttack?.revealedByAttack) this.emitStat(attacker);
  }

  /** Damage-calculation ATK changes last only through this battle. */
  private stripDamageStepBoosts(silentUids?: ReadonlySet<string>): void {
    for (const seat of [0, 1] as Seat[]) {
      for (const m of getMonsters(this.state.zones[seat])) {
        const hadFinal = m.atkFinal !== undefined;
        if (hadFinal) delete m.atkFinal;
        const before = m.statModifiers.length;
        m.statModifiers = m.statModifiers.filter((mod) => !mod.source.startsWith('step:'));
        if (silentUids?.has(m.uid)) continue;
        if (hadFinal || m.statModifiers.length !== before) this.emitStat(m);
      }
    }
  }

  /**
   * A face-down trap this seat can flip by hand during an open window.
   * `relevantNow` decides which cards the prompt lists. It does not decide
   * which Set trap a tap may activate.
   */
  private manualSetTrapAnswer(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c?.uid === uid) ?? null;
    if (!card || card.faceUp) return false;
    if (kindOf(card, this.library) !== CardKind.TRAP) return false;
    if (card.setOnTurn === undefined || card.setOnTurn >= this.state.absoluteTurnCount) return false;
    if (!this.cardLegalInCurrentStep(card)) return false;
    return this.withResponseTrigger(this.state.chainTriggerUid, () =>
      this.cardIsActivatable(seat, card),
    );
  }

  private respondPriority(
    seat: Seat,
    requestId: string,
    cardUid: string | null,
    targetUids?: string[],
    boost?: 'atk' | 'def',
  ): void {
    const pending = this.state.pendingPriority;
    if (!pending || pending.requestId !== requestId) throw new EngineError('No pending response', 'noPendingResponse');
    if (seat !== pending.responder) throw new EngineError('It is not your response to make', 'notYourResponse');
    if (cardUid !== null && !pending.activatableUids.includes(cardUid)) {
      // Auto and Minimal leave some Set traps off the question. Robbin'
      // Goblin on the opponent's Battle Phase is one. Tapping that card is
      // still a legal answer; the prompt list is not the only way to flip it.
      if (!this.manualSetTrapAnswer(seat, cardUid)) {
        throw new EngineError('That card cannot be activated now', 'cardCannotActivateNow');
      }
    }

    this.state.pendingPriority = null;

    if (cardUid === null) {
      this.noteTrapHolePass(pending.activatableUids);
      this.noteBattlePass(pending.activatableUids);
      // A public "No response" only exists when this seat was offered a legal
      // Set card. Saying it out loud tells the opponent that pass was a choice.
      // It also carries the snapshot: a pass that only leaves the Battle Step
      // emits nothing else, and an empty batch never reaches the client, so
      // the phase strip stays locked on the old prompt.
      this.message('noResponse', undefined, 'No response.');
      this.state.chainPasses += 1;
      this.offerChainPriority(otherSeat(seat));
      return;
    }

    const targets = targetUids?.length ? targetUids : undefined;
    try {
      this.withResponseTrigger(this.state.chainTriggerUid, () => {
        const quick = this.findQuickMonster(seat, cardUid);
        if (quick) this.activateQuickMonster(seat, quick, boost);
        else this.activateSetTrap(seat, cardUid, targets);
      });
    } catch (err) {
      this.state.pendingPriority = pending;
      throw err;
    }
  }

  private commitActivationToChain(
    seat: Seat,
    card: CardInstance,
    targetUids?: string[],
    position?: CardPosition,
    positions?: CardPosition[],
    boost?: 'atk' | 'def',
  ): void {
    this.doomedPaid.delete(card.uid);
    this.pushChainLink(seat, card, targetUids, position, positions, boost);
    this.advanceChain();
  }

  /** Uids the effect already chose to destroy when this card joins the chain. */
  private destroyUidsFor(seat: Seat, card: CardInstance, targetUids?: string[]): string[] {
    const api = this.makeApi(seat, card);
    const cardId = Number(card.cardId);
    const effects = [
      ...this.activationEffects(cardId),
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP),
    ];
    const uids = new Set<string>();
    for (const effect of effects) {
      if (!effect.destroys) continue;
      for (const uid of effect.destroys(api, targetUids)) uids.add(uid);
    }
    return [...uids];
  }

  /** The opponent looks at face-down traps Fake Trap saved. */
  private flushFakeTrapConfirm(): void {
    const cards = this.sparedFaceDown;
    this.sparedFaceDown = [];
    if (cards.length === 0 || this.fakeTrapSeat === null) return;
    const viewer = otherSeat(this.fakeTrapSeat);
    this.rememberCards(viewer, cards, false);
    this.emit({ type: EventType.PEEK_CARDS, seat: viewer, cards });
    this.message(
      'fakeTrapConfirm',
      { cardId: String(FAKE_TRAP) },
      'Fake Trap: face-down cards were confirmed.',
    );
  }

  private pushChainLink(
    seat: Seat,
    card: CardInstance,
    targetUids?: string[],
    position?: CardPosition,
    positions?: CardPosition[],
    boost?: 'atk' | 'def',
  ): void {
    if (this.state.chain.length >= MAX_CHAIN_LINKS) {
      throw new EngineError('Chain is too long', 'chainTooLong');
    }
    const speed = this.spellSpeedOf(card);
    const last = this.state.chain[this.state.chain.length - 1];
    if (last && speed < last.spellSpeed) {
      throw new EngineError('Cannot chain a slower effect', 'cannotChainSlower');
    }
    if (last && speed < 2) {
      throw new EngineError('Spell Speed 1 cannot be chained', 'spellSpeed1NoChain');
    }
    this.payCounterCost(seat, card, targetUids);
    this.state.chain.push({
      link: this.state.chain.length + 1,
      controller: seat,
      sourceUid: card.uid,
      cardId: card.cardId,
      spellSpeed: speed,
      targetUids: targetUids ?? [],
      destroyUids: this.destroyUidsFor(seat, card, targetUids),
      ...(position !== undefined ? { position } : {}),
      ...(positions && positions.length > 0 ? { positions } : {}),
      ...(boost ? { boost } : {}),
      triggerUid: this.state.chainTriggerUid,
    });
    this.state.chainPasses = 0;
    if (targetUids && targetUids.length > 0) {
      this.emitTargetsChosen(seat, targetUids, card.uid, card.cardId);
    }
    if (this.state.chain.length >= 2) {
      this.emit({
        type: EventType.CHAIN_LINK_ADDED,
        links: this.state.chain.map(chainLinkView),
      });
    }
  }

  /** Public pick. The renderer marks these cards before the effect moves them. */
  private emitTargetsChosen(
    seat: Seat,
    targetUids: string[],
    sourceUid: string,
    sourceCardId: string,
  ): void {
    if (targetUids.length === 0) return;
    this.emit({
      type: EventType.TARGETS_CHOSEN,
      seat,
      targetUids: [...targetUids],
      sourceUid,
      sourceCardId,
    });
  }

  private advanceChain(): void {
    if (this.state.status !== 'active') return;
    if (this.state.pendingTargets || this.state.pendingFlipEffect) return;
    if (this.state.chain.length === 0) {
      this.continueAfterEmptyWindow();
      return;
    }
    if (this.state.chain.length >= MAX_CHAIN_LINKS || this.state.chainPasses >= 2) {
      this.resolveChain();
      return;
    }
    const last = this.state.chain[this.state.chain.length - 1];
    this.offerChainPriority(otherSeat(last.controller));
  }

  private continueAfterEmptyWindow(): void {
    this.state.chainPasses = 0;
    if (this.state.pendingOfferingSummon !== null) return;
    if (this.state.pendingCoinCall || this.state.pendingChoice) return;
    if (this.state.pendingAttack) {
      this.proceedPendingAttack();
      return;
    }
    this.state.chainTriggerUid = null;
    if (this.state.battleStep === 'START') {
      this.state.battleStep = 'BATTLE';
      return;
    }
    if (this.state.battleStep === 'END') {
      this.leaveBattlePhase();
    }
  }

  /** Continues a chain that paused on a Deck search. */
  private resumeResolvingChain(): void {
    if (!this.resolvingChain) return;
    this.resolvingIndex += 1;
    this.resolveChain();
  }

  private resolveChain(): void {
    if (!this.resolvingChain) {
      const links = [...this.state.chain].reverse();
      this.state.chain = [];
      this.state.chainPasses = 0;
      this.resolvingChain = true;
      this.resolvingLinks = links;
      this.resolvingIndex = 0;
      if (links.length >= 2) {
        this.emit({ type: EventType.CHAIN_RESOLVING, links: links.map(chainLinkView) });
      }
      this.resolvingGraveChain = this.graveChainPending;
      this.graveChainPending = false;
      this.chainOfferingSeats = [];
      this.fakeTrapSeat = null;
      this.fakeTrapSourceUid = null;
      this.sparedFaceDown = [];
    }

    let paused = false;
    try {
      while (this.resolvingIndex < this.resolvingLinks.length) {
        if (this.state.status !== 'active') break;
        const link = this.resolvingLinks[this.resolvingIndex]!;
        if (this.resolvingLinks.length >= 2) {
          const found = findCard(this.state, link.sourceUid);
          const card =
            found?.instance ??
            ({ uid: link.sourceUid, cardId: link.cardId } as CardInstance);
          const legal = !!link.triggerKind || (!link.negated && this.linkStillLegal(link, card));
          this.emit({
            type: EventType.CHAIN_LINK_RESOLVING,
            ...chainLinkView(link),
            fizzled: !legal,
          });
        }
        if (link.triggerKind) {
          if (this.resolveGraveChainLink(link)) {
            paused = true;
            return;
          }
        } else if (this.resolveSpellTrapLink(link)) {
          paused = true;
          return;
        }
        this.resolvingIndex += 1;
      }
    } finally {
      if (!paused) this.finishResolvingChain();
    }
  }

  private resolveSpellTrapLink(link: ChainLink): boolean {
    if (link.negated) {
      // A negated activation is destroyed, including a Continuous Trap
      // such as Dragon Capture Jar. sendOneShotToGraveyard would leave
      // those face-up, which is only correct after they resolve.
      const foundNeg = findCard(this.state, link.sourceUid);
      if (foundNeg) this.destroyCard(foundNeg.instance.uid);
      this.message(
        'activationNegated',
        { cardId: link.cardId },
        `${getCardData(this.library, link.cardId).name} was negated.`,
      );
      return false;
    }
    const found = findCard(this.state, link.sourceUid);
    const card =
      found?.instance ??
      ({ uid: link.sourceUid, cardId: link.cardId } as CardInstance);
    if (Number(link.cardId) === SHARE_THE_PAIN) return this.resolveShareThePain(link, card);
    const name = getCardData(this.library, link.cardId).name;
    if (!this.linkStillLegal(link, card)) {
      this.message(
        'fizzled',
        { cardId: link.cardId },
        `${name} fizzled (its targets are no longer legal).`,
      );
      this.sendOneShotToGraveyard(card);
      return false;
    }
    if (Number(link.cardId) === ELEGANT_EGOTIST) {
      return this.resolveElegantEgotist(link, card);
    }
    if (Number(link.cardId) === FISSURE && this.pauseFissureTie(link, card)) return true;
    this.withResponseTrigger(link.triggerUid, () => {
      this.resolveActivation(
        link.controller,
        card,
        link.targetUids.length > 0 ? link.targetUids : undefined,
        link.position,
        link.positions,
      );
    });
    if (this.fakeTrapSourceUid && link.sourceUid === this.fakeTrapSourceUid) {
      this.flushFakeTrapConfirm();
    }
    if (Number(link.cardId) === STARTER_IDS.ULTIMATE_OFFERING) {
      this.chainOfferingSeats.push(link.controller);
    }
    return false;
  }

  /**
   * Fissure chooses at resolution, and only when the lowest ATK is shared.
   * c66788016.lua selects inside the operation, after the chain. A unique
   * minimum stays on the automatic path. The AI keeps the leftmost monster.
   */
  private pauseFissureTie(link: ChainLink, spell: CardInstance): boolean {
    const seat = link.controller;
    if (this.state.seats[seat].isAI) return false;
    const tied = fissureMinGroup(this.makeApi(seat, spell));
    if (tied.length < 2) return false;
    const name = getCardData(this.library, spell.cardId).name;
    this.priorityCounter += 1;
    const candidateUids = tied.map((monster) => monster.uid);
    const prompt = `${name}: the lowest ATK is tied — choose one`;
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: spell.uid,
      cardId: spell.cardId,
      prompt,
      candidateUids,
      min: 1,
      max: 1,
      fissureDestroy: true,
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt,
      candidateUids,
      min: 1,
      max: 1,
      sourceUid: spell.uid,
      sourceCardId: spell.cardId,
    });
    return true;
  }

  /**
   * Elegant Egotist. The June 2002 text lets you Special Summon Harpie Lady
   * or Harpie Lady Sisters from your hand or Deck while you control a
   * face-up Harpie Lady anywhere on the field. The choice happens now,
   * after the chain, and the summon itself is optional. c90219263.lua
   * checks either side of the field, and so does the album text.
   */
  private resolveElegantEgotist(link: ChainLink, spell: CardInstance): boolean {
    const seat = link.controller;
    const name = getCardData(this.library, spell.cardId).name;
    const candidates = this.egotistCandidates(seat);
    if (this.egotistBlocked(seat, spell, candidates.length > 0)) return false;
    if (this.state.seats[seat].isAI) {
      const best = candidates.reduce((left, right) =>
        (getCardData(this.library, left.cardId).atk ?? 0) >=
        (getCardData(this.library, right.cardId).atk ?? 0)
          ? left
          : right,
      );
      this.finishEgotist(seat, spell, best, CardPosition.ATTACK);
      return false;
    }
    this.priorityCounter += 1;
    const candidateUids = candidates.map((card) => card.uid);
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: spell.uid,
      cardId: String(ELEGANT_EGOTIST),
      prompt: `${name}: Special Summon Harpie Lady or Harpie Lady Sisters?`,
      candidateUids,
      min: 0,
      max: 1,
      specialSummonNamed: true,
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt: this.state.pendingTargets.prompt,
      candidateUids,
      min: 0,
      max: 1,
      sourceUid: spell.uid,
      sourceCardId: String(ELEGANT_EGOTIST),
    });
    return true;
  }

  private finishEgotist(
    seat: Seat,
    spell: CardInstance,
    monster: CardInstance,
    position: CardPosition,
  ): void {
    const found = findCard(this.state, monster.uid);
    const id = Number(monster.cardId);
    const legalZone = found?.location.zone === 'hand' || found?.location.zone === 'deck';
    const legalName = id === HARPIE_LADY || id === HARPIE_LADY_SISTERS;
    if (!found || !legalZone || !legalName) {
      this.message(
        'egotistNoMonster',
        { cardId: spell.cardId },
        `${getCardData(this.library, spell.cardId).name}: no Harpie Lady or Harpie Lady Sisters in your hand or Deck.`,
      );
      this.sendOneShotToGraveyard(spell);
      return;
    }
    if (this.egotistBlocked(seat, spell, true)) return;
    const fromDeck = found.location.zone === 'deck';
    if (cannotNormalSummon(monster.cardId)) monster.properlySummoned = true;
    this.specialSummon(monster.uid, seat, position);
    if (fromDeck && this.shuffleDeck(seat)) {
      this.emit({ type: EventType.DECK_SHUFFLED, seat });
    }
    const summoned = getCardData(this.library, monster.cardId).name;
    this.message('specialSummoned', { cardId: monster.cardId }, `${summoned} was Special Summoned.`);
    this.sendOneShotToGraveyard(spell);
  }

  /**
   * The spell already resolved. A failed check sends it to the Graveyard.
   * `hasMonster` is false when the hand and Deck have neither name.
   */
  private egotistBlocked(seat: Seat, spell: CardInstance, hasMonster: boolean): boolean {
    const name = getCardData(this.library, spell.cardId).name;
    if (!this.controlsFaceUpHarpieLady(seat)) {
      this.message(
        'egotistNoLady',
        { cardId: spell.cardId },
        `${name}: there is no face-up Harpie Lady on the field.`,
      );
      this.sendOneShotToGraveyard(spell);
      return true;
    }
    if (!this.hasFreeMonsterZone(seat)) {
      this.message(
        'egotistNoZone',
        { cardId: spell.cardId },
        `${name}: no free monster zone.`,
      );
      this.sendOneShotToGraveyard(spell);
      return true;
    }
    if (!hasMonster) {
      this.message(
        'egotistNoMonster',
        { cardId: spell.cardId },
        `${name}: no Harpie Lady or Harpie Lady Sisters in your hand or Deck.`,
      );
      this.sendOneShotToGraveyard(spell);
      return true;
    }
    return false;
  }

  private egotistCandidates(seat: Seat): CardInstance[] {
    const zone = this.state.zones[seat];
    return [...zone.hand, ...zone.deck].filter((card) => {
      const id = Number(card.cardId);
      return id === HARPIE_LADY || id === HARPIE_LADY_SISTERS;
    });
  }

  private controlsFaceUpHarpieLady(_seat: Seat): boolean {
    return ([0, 1] as Seat[]).some((seat) =>
      getMonsters(this.state.zones[seat]).some(
        (monster) => monster.faceUp && Number(monster.cardId) === HARPIE_LADY,
      ),
    );
  }

  /**
   * June 2002: treat Cocoon of Evolution as an Equip Magic Card on a face-up
   * Petit Moth. That moth's ATK and DEF become Cocoon's (0/2000).
   */
  private equipCocoon(seat: Seat, cocoonUid: string, hostUid: string): void {
    this.requireTurnMain(seat);
    const zone = this.state.zones[seat];
    const handIdx = zone.hand.findIndex((card) => card.uid === cocoonUid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = zone.hand[handIdx]!;
    if (Number(card.cardId) !== COCOON_OF_EVOLUTION) {
      throw new EngineError('Not a spell', 'notSpell');
    }
    const host = findCard(this.state, hostUid);
    if (
      !host ||
      host.location.zone !== 'monsters' ||
      !host.instance.faceUp ||
      Number(host.instance.cardId) !== PETIT_MOTH
    ) {
      throw new EngineError('Invalid target', 'invalidTarget');
    }
    const zoneIdx = firstFreeIndex(zone.spellTraps);
    if (zoneIdx === -1) throw new EngineError('No free spell/trap zone', 'noFreeSpellZone');
    zone.hand.splice(handIdx, 1);
    card.faceUp = true;
    card.turnCounter = 0;
    zone.spellTraps[zoneIdx] = card;
    card.equippedToUid = host.instance.uid;
    host.instance.equippedUids.push(card.uid);
    const cocoon = getCardData(this.library, String(COCOON_OF_EVOLUTION));
    const moth = getCardData(this.library, String(PETIT_MOTH));
    this.addStatModifier(
      host.instance.uid,
      (cocoon.atk ?? 0) - (moth.atk ?? 0),
      (cocoon.def ?? 0) - (moth.def ?? 0),
      card.uid,
    );
    this.emit({ type: EventType.SPELL_ACTIVATED, seat, cardUid: card.uid, cardId: card.cardId });
    this.message(
      'cocoonEquipped',
      { cardId: String(PETIT_MOTH) },
      'Petit Moth\'s ATK and DEF became the same as Cocoon of Evolution.',
    );
  }

  /**
   * Gate Guardian tributes the three guardians. Larvae Moth and Great Moth
   * tribute your Petit Moth on the 2nd or 4th of your turns after Cocoon.
   * Neither spends the Normal Summon.
   */
  private specialSummonProcedure(
    seat: Seat,
    uid: string,
    position: CardPosition,
    tributeUids: string[],
  ): void {
    this.requireTurnMain(seat);
    const zone = this.state.zones[seat];
    const handIdx = zone.hand.findIndex((card) => card.uid === uid);
    if (handIdx === -1) throw new EngineError('Card not in hand', 'cardNotInHand');
    const card = zone.hand[handIdx]!;
    const evolveTurn = mothEvolveTurn(card.cardId);
    if (evolveTurn !== null) {
      this.evolveMoth(seat, card, position, tributeUids, evolveTurn);
      return;
    }
    if (Number(card.cardId) !== GATE_GUARDIAN) {
      throw new EngineError('This monster cannot be Normal Summoned or Set', 'noNormalSummon');
    }
    const monsters = getMonsters(zone);
    if (!isGateTributeSet(monsters, tributeUids)) {
      throw new EngineError('Invalid tribute', 'invalidTribute');
    }
    const open = zone.monsters.filter((monster) => monster === null).length;
    if (open + tributeUids.length < 1) {
      throw new EngineError('No free monster zone', 'noFreeMonsterZone');
    }
    for (const tributeUid of tributeUids) this.sendToGraveyard(tributeUid);
    card.properlySummoned = true;
    this.specialSummon(card.uid, seat, position, tributeUids);
    const name = getCardData(this.library, card.cardId).name;
    this.message('specialSummoned', { cardId: card.cardId }, `${name} was Special Summoned.`);
  }

  /**
   * Larvae Moth on exactly the 2nd of your turns, Great Moth on the 4th.
   * Tribute your Petit Moth that is still equipped with your Cocoon.
   * c87756343.lua and c14141448.lua; the printed turn is exact, not "or later".
   */
  private evolveMoth(
    seat: Seat,
    card: CardInstance,
    position: CardPosition,
    tributeUids: string[],
    turn: number,
  ): void {
    const zone = this.state.zones[seat];
    if (tributeUids.length !== 1) throw new EngineError('Invalid tribute', 'invalidTribute');
    const legal = evolveTribute(this.state, seat, turn);
    if (!legal || legal.moth.uid !== tributeUids[0]) {
      throw new EngineError('Invalid tribute', 'invalidTribute');
    }
    const open = zone.monsters.filter((monster) => monster === null).length;
    if (open + 1 < 1) throw new EngineError('No free monster zone', 'noFreeMonsterZone');
    const cocoonUid = legal.cocoon.uid;
    const mothUid = legal.moth.uid;
    this.sendToGraveyard(mothUid);
    card.properlySummoned = true;
    this.specialSummon(card.uid, seat, position, [cocoonUid, mothUid]);
    const name = getCardData(this.library, card.cardId).name;
    this.message('specialSummoned', { cardId: card.cardId }, `${name} was Special Summoned.`);
  }

  private finishResolvingChain(): void {
    const grave = this.resolvingGraveChain;
    const offering = this.chainOfferingSeats;
    this.resolvingChain = false;
    this.resolvingGraveChain = false;
    this.resolvingLinks = [];
    this.resolvingIndex = -1;
    this.chainOfferingSeats = [];
    this.fakeTrapSeat = null;
    this.fakeTrapSourceUid = null;
    this.sparedFaceDown = [];
    this.state.chainTriggerUid = null;
    for (const seat of offering) this.offerOfferingSummon(seat);
    if (grave) {
      this.flushGraveTriggers();
      return;
    }
    // Stop Defense is still a face-up Spell when it flips Armed Ninja, so the
    // window lists it. The one-shot then goes to the Graveyard in this same
    // chain. Drop targets that already left before the turn waits on a click.
    this.dropStaleFlipWindow();
    this.proceedAfterChain();
  }

  private linkStillLegal(
    link: { controller: Seat; cardId: string; targetUids: string[]; triggerUid: string | null },
    card: CardInstance,
  ): boolean {
    const api = this.makeApi(link.controller, card);
    const cardId = Number(link.cardId);
    // Flip Effects on the chain (Trap Master, Reaper of the Cards) keep the
    // card chosen at activation. activationEffects alone skipped them, so a
    // chained Waboku left the field and the effect destroyed a different Trap.
    const targeted = [
      ...this.activationEffects(cardId),
      ...engineEffects.getByTrigger(cardId, EngineEffectTrigger.FLIP),
    ].filter((e) => e.requiresTarget && !e.costSelection);
    if (targeted.length === 0) return true;
    if (link.targetUids.length === 0) return true;
    // canActivate died with the last legal card. That is a fizzle, not a
    // resolution that skips the effect and still sends the Spell away.
    const needing = targeted.find((e) => !e.canActivate || e.canActivate(api));
    if (!needing) return false;
    return this.withResponseTrigger(link.triggerUid, () => {
      if (needing.canTargetSet) return !!needing.canTargetSet(api, link.targetUids);
      if (!needing.canTarget) return true;
      return link.targetUids.every((uid) => needing.canTarget!(api, uid));
    });
  }

  private sendOneShotToGraveyard(card: CardInstance): void {
    const cardId = Number(card.cardId);
    // Field and Continuous stay face-up. An Equip that never attached does not.
    const stays =
      engineEffects.has(cardId, EngineEffectTrigger.FIELD) ||
      engineEffects.has(cardId, EngineEffectTrigger.CONTINUOUS);
    if (!stays && kindOf(card, this.library) !== CardKind.MONSTER) this.sendToGraveyard(card.uid);
  }

  private proceedAfterChain(): void {
    this.flushBattleGy();
    if (this.maybeLeaveBattleForMaiden()) return;
    if (this.state.pendingTargets || this.state.pendingFlipEffect) return;
    if (this.state.pendingOfferingSummon !== null) return;
    if (this.state.pendingCoinCall || this.state.pendingChoice) return;
    if (this.state.pendingAttack) {
      this.proceedPendingAttack();
      return;
    }
    if (this.state.battleStep === 'START') {
      this.state.battleStep = 'BATTLE';
      return;
    }
    if (this.state.battleStep === 'END') {
      this.leaveBattlePhase();
    }
  }

  /** True while a human player owes a decision; used to pause AI auto-play. */
  hasPendingResponse(): boolean {
    return (
      this.state.pendingPriority !== null ||
      this.state.pendingDiscard !== null ||
      this.state.pendingFlipEffect !== null ||
      this.state.pendingTargets !== null ||
      this.state.pendingOfferingSummon !== null ||
      this.state.pendingCoinCall !== null ||
      this.state.pendingChoice !== null ||
      this.state.pendingChainOrder !== null ||
      this.state.chain.length > 0
    );
  }

  /** Shuffle a seat's Main Deck. Returns false when there is nothing to mix. */
  private shuffleDeck(seat: Seat): boolean {
    const z = this.state.zones[seat];
    if (z.deck.length < 2) return false;
    const shuffled = shuffle(z.deck, this.state.rngState);
    this.state.rngState = shuffled.rngState;
    z.deck = shuffled.items;
    return true;
  }

  /** Shuffle a seat's hand (courtesy button, or after a bounce). */
  private shuffleHandFor(seat: Seat): void {
    const z = this.state.zones[seat];
    if (z.hand.length < 2) return;
    const shuffled = shuffle(z.hand, this.state.rngState);
    this.state.rngState = shuffled.rngState;
    z.hand = shuffled.items;
    this.emit({
      type: EventType.HAND_SHUFFLED,
      seat,
      order: z.hand.map((c) => c.uid),
    });
  }

  /** One shuffle per seat after every bounce in the current action. */
  private flushHandShuffles(): void {
    for (const seat of [0, 1] as Seat[]) {
      if (!this.handShuffleSeats.has(seat)) continue;
      this.shuffleHandFor(seat);
    }
    this.handShuffleSeats.clear();
  }

  /** AI variant of the hand-size discard: sheds the lowest-value excess. */
  private aiDiscardExcess(seat: Seat, excess: number): void {
    const z = this.state.zones[seat];
    for (let i = 0; i < excess && z.hand.length > HAND_SIZE_LIMIT; i++) {
      const uid = pickAiDiscardUid(z.hand, this.library, z.lifePoints, z.extraDeck);
      if (!uid) break;
      this.sendToGraveyard(uid);
    }
  }

  /** Handles a human player's chosen discards to satisfy the hand-size limit. */
  private discardForHandLimit(seat: Seat, cardUids: string[]): void {
    const pending = this.state.pendingDiscard;
    if (!pending || pending.seat !== seat) throw new EngineError('You do not owe a discard', 'noDiscardOwed');
    const z = this.state.zones[seat];
    for (const uid of cardUids) {
      if (!z.hand.some((c) => c.uid === uid)) throw new EngineError('That card is not in your hand', 'cardNotYourHand');
    }
    for (const uid of cardUids) this.sendToGraveyard(uid);

    if (z.hand.length > HAND_SIZE_LIMIT) {
      // Still over the limit: keep the window open with the remaining count.
      this.state.pendingDiscard = { seat, count: z.hand.length - HAND_SIZE_LIMIT };
      return;
    }
    // Requirement met: close the window and let the turn pass to the next player.
    this.state.pendingDiscard = null;
    this.advancePhase();
  }

  private checkExodia(seat: Seat): void {
    if (this.state.status === 'finished') return;
    const handIds = new Set(this.state.zones[seat].hand.map((c) => c.cardId));
    if (EXODIA_PIECE_IDS.every((id) => handIds.has(id))) {
      this.endGame(seat, 'exodia');
    }
  }

  // --- Fusion summon (Polymerization) -----------------------------------------

  private cardIdByName(name: string): string | null {
    if (!this.nameToId) {
      this.nameToId = new Map();
      for (const [id, data] of this.library) {
        if (!this.nameToId.has(data.name)) this.nameToId.set(data.name, id);
      }
    }
    return this.nameToId.get(name) ?? null;
  }

  private parseFusionMaterials(desc: string): string[] {
    const matches = desc.match(/"([^"]+)"/g);
    if (!matches) return [];
    // Quoted words that are not cards (Black Skull Dragon's "Archfiend" line)
    // are not materials. Two quotes of the same name still count as two.
    return matches
      .map((m) => m.replace(/"/g, ''))
      .filter((name) => this.cardIdByName(name) !== null);
  }

  private fusionRecipesFor(seat: Seat): { fusion: CardInstance; materialIds: string[] }[] {
    const out: { fusion: CardInstance; materialIds: string[] }[] = [];
    for (const fusion of this.state.zones[seat].extraDeck) {
      const names = this.parseFusionMaterials(getCardData(this.library, fusion.cardId).desc ?? '');
      if (names.length < 2) continue;
      const ids: string[] = [];
      let ok = true;
      for (const n of names) {
        const id = this.cardIdByName(n);
        if (!id) {
          ok = false;
          break;
        }
        ids.push(id);
      }
      if (ok) out.push({ fusion, materialIds: ids });
    }
    return out;
  }

  private findMaterialUid(seat: Seat, cardId: string, used: Set<string>): string | null {
    const z = this.state.zones[seat];
    const inHand = z.hand.find((c) => c.cardId === cardId && !used.has(c.uid));
    if (inHand) return inHand.uid;
    const onField = getMonsters(z).find((m) => m.cardId === cardId && !used.has(m.uid));
    return onField ? onField.uid : null;
  }

  private findFusionPlay(seat: Seat, fusionUid?: string): { fusionUid: string; materialUids: string[] } | null {
    const z = this.state.zones[seat];
    for (const recipe of this.fusionRecipesFor(seat)) {
      if (fusionUid && recipe.fusion.uid !== fusionUid) continue;
      const used = new Set<string>();
      const uids: string[] = [];
      let ok = true;
      for (const matId of recipe.materialIds) {
        const uid = this.findMaterialUid(seat, matId, used);
        if (!uid) {
          ok = false;
          break;
        }
        used.add(uid);
        uids.push(uid);
      }
      if (!ok) continue;
      const freeSlots = z.monsters.filter((m) => m === null).length;
      const fieldMats = uids.filter((uid) => z.monsters.some((m) => m?.uid === uid)).length;
      if (freeSlots + fieldMats < 1) continue;
      return { fusionUid: recipe.fusion.uid, materialUids: uids };
    }
    return null;
  }

  private canFusionSummon(seat: Seat, fusionUid?: string): boolean {
    return this.findFusionPlay(seat, fusionUid) !== null;
  }

  private fusionSummon(seat: Seat, fusionUid?: string): boolean {
    const play = this.findFusionPlay(seat, fusionUid);
    if (!play) return false;
    for (const uid of play.materialUids) this.sendToGraveyard(uid);

    const extra = this.state.zones[seat].extraDeck;
    const fIdx = extra.findIndex((c) => c.uid === play.fusionUid);
    if (fIdx === -1) return false;
    const zoneIdx = firstFreeIndex(this.state.zones[seat].monsters);
    if (zoneIdx === -1) return false;
    const [fusion] = extra.splice(fIdx, 1);
    const position = this.chosenSummonPosition;
    fusion.faceUp = true;
    fusion.position = position;
    fusion.summonedThisTurn = true;
    fusion.attackedThisTurn = false;
    fusion.declaredAttackThisTurn = false;
    fusion.positionChangedThisTurn = false;
    this.state.zones[seat].monsters[zoneIdx] = fusion;
    this.emit({
      type: EventType.CARD_SUMMONED,
      seat,
      cardUid: fusion.uid,
      cardId: fusion.cardId,
      zoneIndex: zoneIdx,
      position,
      tribute: false,
    });
    this.missionStats[seat].fusions += 1;
    this.checkSummonTraps(seat, fusion, 'special');
    return true;
  }

  /**
   * Runs `fn` with `uid` exposed to effects as the trap-response trigger. Restores the
   * previous value afterwards so nested windows (a response that starts a battle) do
   * not leak their trigger into each other.
   */
  private withResponseTrigger<T>(uid: string | null, fn: () => T): T {
    const previous = this.responseTriggerUid;
    this.responseTriggerUid = uid;
    try {
      return fn();
    } finally {
      this.responseTriggerUid = previous;
    }
  }

  private makeApi(controller: Seat, source: CardInstance): EngineApi {
    const triggerUid = this.responseTriggerUid ?? this.state.chainTriggerUid;
    const trigger = triggerUid ? findCard(this.state, triggerUid) : null;
    return {
      state: this.state,
      library: this.library,
      controller,
      source,
      responseTrigger: trigger?.instance ?? null,
      summonPosition: this.chosenSummonPosition,
      summonPositionFor: (uid) => this.chosenSummonPositions.get(uid) ?? this.chosenSummonPosition,
      emit: (e) => this.emit(e),
      destroyCard: (uid) => this.destroyCard(uid),
      sendToGraveyard: (uid) => this.sendToGraveyard(uid),
      banishCard: (uid, faceDown) => this.banishCard(uid, faceDown),
      changeLifePoints: (seat, delta) => this.changeLifePoints(seat, delta),
      drawCards: (seat, count) => this.drawCards(seat, count),
      addStatModifier: (uid, atk, def, sourceUid) => this.addStatModifier(uid, atk, def, sourceUid),
      setAtkFinal: (uid, atk) => this.setAtkFinal(uid, atk),
      banishFromGraveyard: (seat, count, preferStrongest) =>
        this.banishFromGraveyard(seat, count, preferStrongest),
      returnToHand: (uid) => this.returnToHand(uid),
      specialSummon: (uid, toSeat, position) => this.specialSummon(uid, toSeat, position),
      takeControl: (uid, toSeat) => this.takeControl(uid, toSeat),
      setAttackLock: (seat, turns) => this.setAttackLock(seat, turns),
      setTurnCounter: (turns) => this.setTurnCounter(source, turns),
      flipFaceUp: (uid) => this.flipFaceUp(uid),
      noteBattlePosition: (uid, from) => this.noteBattlePosition(uid, from),
      hasFreeMonsterZone: (seat) => this.hasFreeMonsterZone(seat),
      fusionSummon: (seat, fusionUid) => this.fusionSummon(seat, fusionUid),
      canFusionSummon: (seat, fusionUid) => this.canFusionSummon(seat, fusionUid),
      grantExtraNormalSummon: () => {
        this.state.zones[controller].extraNormalSummons += 1;
      },
      armLastWill: () => {
        const z = this.state.zones[controller];
        z.lastWillCharges += 1;
        if (z.lastWillReady) z.lastWillAsk = true;
      },
      setBattleDamageImmune: () => {
        this.state.battleDamageImmune[controller] = true;
      },
      setBattleDestroyImmune: () => {
        this.state.battleDestroyImmune[controller] = true;
      },
      setReverseStatMods: () => {
        this.state.reverseStatMods = true;
        this.emitAllMonsterStats();
        this.message(
          'reverseTrap',
          { cardId: String(STARTER_IDS.REVERSE_TRAP) },
          'Reverse Trap: ATK and DEF modifiers are inverted until the End Phase.',
        );
      },
      markSoulExchange: (uid) => {
        const marks = this.state.zones[controller].soulExchangeUids;
        if (!marks.includes(uid)) marks.push(uid);
      },
      skipBattlePhase: () => {
        this.state.zones[controller].skipBattlePhase = true;
      },
      discardHand: (seat) => this.discardHand(seat),
      peekCards: (cards, opts) => {
        this.rememberCards(controller, cards, opts?.reveal === true);
        this.emit({
          type: EventType.PEEK_CARDS,
          seat: controller,
          cards,
          ...(opts?.reveal ? { reveal: true } : {}),
        });
      },
      orderDeckTop: (maxCount) => this.orderDeckTop(controller, source, maxCount),
      swapPrintedStats: () => this.swapPrintedStats(source),
      isDragonProtected: (uid) => this.lordOfDBlocksTarget(uid),
      isPendingBattleDestroy: (uid) => this.pendingBattleGy.includes(uid),
      negatePreviousLink: () => {
        const victim = this.resolvingLinks[this.resolvingIndex + 1];
        if (victim) victim.negated = true;
      },
      hasPreviousLink: () => this.resolvingIndex + 1 < this.resolvingLinks.length,
      negateSummon: () => {
        const uid = this.state.chainTriggerUid;
        if (!uid || this.negatedSummonUids.has(uid)) return;
        this.negatedSummonUids.add(uid);
        this.sendingNegatedSummon = true;
        try {
          this.destroyCard(uid);
        } finally {
          this.sendingNegatedSummon = false;
        }
      },
      zeroThisAttackDamage: () => {
        this.zeroDamageSeat = controller;
      },
      armFakeTrap: () => {
        const victim = this.resolvingLinks[this.resolvingIndex + 1];
        if (!victim) return;
        this.fakeTrapSeat = controller;
        this.fakeTrapSourceUid = victim.sourceUid;
        this.message(
          'fakeTrapReplace',
          { cardId: String(FAKE_TRAP) },
          'Fake Trap is destroyed instead.',
        );
      },
      sparedByReplacement: (uid) => this.sparedByReplacement(uid),
      chosenBoost: () => this.resolvingLinks[this.resolvingIndex]?.boost ?? null,
      summonWindow: () => this.summonKind,
      resolving: () => this.resolvingChain,
    };
  }

  private lordOfDBlocksTarget(uid: string): boolean {
    const hasLord = ([0, 1] as Seat[]).some((s) =>
      getMonsters(this.state.zones[s]).some(
        (m) => m.faceUp && Number(m.cardId) === STARTER_IDS.LORD_OF_D,
      ),
    );
    if (!hasLord) return false;
    const found = findCard(this.state, uid);
    if (!found || found.location.zone !== 'monsters') return false;
    return getCardData(this.library, found.instance.cardId).race === 'Dragon';
  }

  private assertCanNormalSummon(z: MatchState['zones'][Seat]): void {
    if (!z.hasNormalSummoned) return;
    if (z.extraNormalSummons > 0) return;
    throw new EngineError('Normal summon already used this turn', 'normalSummonUsed');
  }

  private markNormalSummonUsed(z: MatchState['zones'][Seat]): void {
    if (z.extraNormalSummons > 0) {
      z.extraNormalSummons -= 1;
      const seat = (this.state.zones[0] === z ? 0 : 1) as Seat;
      const paidWindow =
        this.state.pendingOfferingSummon === seat && this.state.turnPlayer !== seat;
      this.maybeCloseOfferingWait(seat);
      if (paidWindow && this.state.pendingOfferingSummon === null) {
        this.resumeOfferingWindow = true;
      }
      return;
    }
    z.hasNormalSummoned = true;
  }

  private isLegalTribute(seat: Seat, tUid: string): boolean {
    if (getMonsters(this.state.zones[seat]).some((m) => m.uid === tUid)) return true;
    return soulExchangeTributeUids(this.state, seat).includes(tUid);
  }

  private igniteUltimateOffering(seat: Seat, card: CardInstance): void {
    if (
      !canIgniteUltimateOffering(
        this.state.turnPlayer,
        this.state.phase,
        seat,
        this.state.battleStep,
      )
    ) {
      throw new EngineError(
        'Ultimate Offering: your Main Phase or the opponent\'s Battle Phase',
        'offeringPhase',
      );
    }
    const z = this.state.zones[seat];
    if (z.extraNormalSummons > 0) {
      throw new EngineError(
        'Ultimate Offering: Normal Summon or Set the monster you paid for',
        'offeringSummonOwed',
      );
    }
    if (z.lifePoints < 500) throw new EngineError('Not enough Life Points', 'notEnoughLp');
    if (!canNormalOrTributeFromHand(this.state, seat, this.library)) {
      throw new EngineError(
        'No monster you can Normal Summon or Tribute Summon',
        'noOfferingMonster',
      );
    }
    this.changeLifePoints(seat, -500);
    z.extraNormalSummons += 1;
    this.emit({
      type: EventType.EFFECT_RESOLVED,
      cardUid: card.uid,
      detail: getCardData(this.library, card.cardId).name,
    });
    this.message(
      'offeringAgain',
      { cardId: String(STARTER_IDS.ULTIMATE_OFFERING) },
      'Ultimate Offering: you may Normal Summon or Set again.',
    );
    this.offerOfferingSummon(seat);
    this.checkWin();
  }

  private isFaceUpOffering(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c && c.uid === uid);
    return !!card && card.faceUp && Number(card.cardId) === STARTER_IDS.ULTIMATE_OFFERING;
  }

  private isSetOffering(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c && c.uid === uid);
    return !!card && !card.faceUp && Number(card.cardId) === STARTER_IDS.ULTIMATE_OFFERING;
  }

  /**
   * Set Ultimate Offering may open a Minimal window: opponent's Battle Phase,
   * at the start or when an attack is declared. Not the Damage Step, not a
   * summon window, and not once a chain is already building.
   */
  private setOfferingOpensMinimal(seat: Seat): boolean {
    if (this.state.phase !== EnginePhase.BATTLE) return false;
    if (this.state.turnPlayer === seat) return false;
    if (this.state.battleStep === 'DAMAGE') return false;
    if (this.state.chain.length > 0 || this.state.chainTriggerUid) return false;
    return this.defenderBattleStart(seat) || !!this.state.pendingAttack;
  }

  /** Start Step of your Battle Phase, before an attack or a chain. */
  private attackerBattleStart(seat: Seat): boolean {
    return (
      this.state.battleStep === 'START' &&
      this.state.turnPlayer === seat &&
      this.state.chain.length === 0 &&
      !this.state.pendingAttack &&
      !this.state.chainTriggerUid
    );
  }

  /** Start Step of the opponent's Battle Phase, before an attack or a chain. */
  private defenderBattleStart(seat: Seat): boolean {
    return (
      this.state.battleStep === 'START' &&
      this.state.turnPlayer !== seat &&
      this.state.chain.length === 0 &&
      !this.state.pendingAttack &&
      !this.state.chainTriggerUid
    );
  }

  private isSetJustDesserts(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c && c.uid === uid);
    return !!card && !card.faceUp && Number(card.cardId) === STARTER_IDS.JUST_DESSERTS;
  }

  private isSetRobbinGoblin(seat: Seat, uid: string): boolean {
    const card = this.state.zones[seat].spellTraps.find((c) => c && c.uid === uid);
    return !!card && !card.faceUp && Number(card.cardId) === ROBBIN_GOBLIN;
  }

  /**
   * Opponent declared an attack on a face-down monster you control. That
   * flip is the Damage Step; this is the last legal window to activate
   * Robbin' Goblin (EVENT_FREE_CHAIN, not an ATK/DEF modifier).
   */
  private robbinGoblinAnswersSetDefense(seat: Seat): boolean {
    const attack = this.state.pendingAttack;
    if (!attack || attack.seat === seat) return false;
    if (this.state.battleStep === 'DAMAGE') return false;
    if (!attack.targetUid) return false;
    const target = getMonsters(this.state.zones[seat]).find((m) => m.uid === attack.targetUid);
    return !!target && !target.faceUp;
  }

  /**
   * After a flip or a paid ignition on the opponent's Battle Phase, pause the
   * turn player. Before payment they may pass. After payment they summon.
   */
  private offerOfferingSummon(seat: Seat): void {
    if (this.state.turnPlayer === seat) return;
    if (
      !canIgniteUltimateOffering(
        this.state.turnPlayer,
        this.state.phase,
        seat,
        this.state.battleStep,
      )
    ) {
      return;
    }
    if (!canNormalOrTributeFromHand(this.state, seat, this.library)) {
      return;
    }
    this.state.pendingOfferingSummon = seat;
  }

  private clearOfferingWait(): void {
    const seat = this.state.pendingOfferingSummon;
    this.state.pendingOfferingSummon = null;
    if (seat !== null) this.state.zones[seat].extraNormalSummons = 0;
  }

  private maybeCloseOfferingWait(seat: Seat): void {
    if (this.state.pendingOfferingSummon !== seat) return;
    if (this.state.zones[seat].extraNormalSummons > 0) return;
    this.state.pendingOfferingSummon = null;
  }

  private onEnterEndPhase(): void {
    this.tickSteelScorpion();
    this.returnBorrowedMonsters();
    this.clearUntilEndModifiers();
    this.returnWormBeasts();
    if (this.state.reverseStatMods) {
      this.state.reverseStatMods = false;
      this.emitAllMonsterStats();
    }
    this.state.battleDamageImmune = [false, false];
    this.state.battleDestroyImmune = [false, false];
    this.queueMushroomGive(this.state.turnPlayer);
    this.openNextChoice();
  }

  private clearUntilEndModifiers(): void {
    const monsters = [
      ...getMonsters(this.state.zones[0]),
      ...getMonsters(this.state.zones[1]),
    ];
    for (const m of monsters) {
      const beforeAtk = getModifiedAtk(m, this.library, this.state.reverseStatMods);
      const beforeDef = getModifiedDef(m, this.library, this.state.reverseStatMods);
      m.statModifiers = m.statModifiers.filter((mod) => !mod.source.startsWith('untilEnd:'));
      const atk = getModifiedAtk(m, this.library, this.state.reverseStatMods);
      const def = getModifiedDef(m, this.library, this.state.reverseStatMods);
      if (beforeAtk !== atk || beforeDef !== def) this.emitStat(m);
    }
  }

  private returnWormBeasts(): void {
    const seat = this.state.turnPlayer;
    const name = getCardData(this.library, String(STARTER_IDS.WORM_BEAST)).name;
    for (const m of [...getMonsters(this.state.zones[seat])]) {
      if (!m.faceUp || Number(m.cardId) !== STARTER_IDS.WORM_BEAST) continue;
      this.emit({
        type: EventType.EFFECT_RESOLVED,
        cardUid: m.uid,
        detail: name,
      });
      this.returnToHand(m.uid);
    }
  }

  private emitAllMonsterStats(): void {
    for (const seat of [0, 1] as Seat[]) {
      for (const m of getMonsters(this.state.zones[seat])) {
        this.emitStat(m);
      }
    }
  }

  /**
   * Mysterious Puppeteer (2002): each successful Normal or Flip Summon of
   * *another* monster (not a Set, not its own summon) gains its controller
   * 500 LP. Called only after summon negation has passed. Emits
   * EFFECT_RESOLVED so the field destello and heal SFX play like other
   * starter triggers.
   */
  private applyPuppeteer(summonedUid: string): void {
    const name = getCardData(this.library, String(STARTER_IDS.PUPPETEER)).name;
    for (const seat of [0, 1] as Seat[]) {
      for (const m of getMonsters(this.state.zones[seat])) {
        if (m.uid === summonedUid) continue;
        if (m.faceUp && Number(m.cardId) === STARTER_IDS.PUPPETEER) {
          this.changeLifePoints(seat, 500);
          this.emit({
            type: EventType.EFFECT_RESOLVED,
            cardUid: m.uid,
            detail: name,
          });
        }
      }
    }
  }

  private discardHand(seat: Seat): number {
    const uids = this.state.zones[seat].hand.map((c) => c.uid);
    for (const uid of uids) this.sendToGraveyard(uid);
    return uids.length;
  }

  /**
   * Old photos stored `lastWillArmed`. A live duel keeps one charge so a
   * monster sent after the restore still offers the summon.
   */
  private normalizeLastWillZones(): void {
    for (const z of this.state.zones) {
      const legacy = (z as { lastWillArmed?: boolean }).lastWillArmed;
      if (typeof z.lastWillCharges !== 'number' || !Number.isFinite(z.lastWillCharges)) {
        z.lastWillCharges = legacy === true ? 1 : 0;
      } else {
        z.lastWillCharges = Math.max(0, Math.floor(z.lastWillCharges));
      }
      if (z.lastWillReady !== true) z.lastWillReady = false;
      if (z.lastWillAsk !== true) z.lastWillAsk = false;
    }
  }

  /** One uid per Soul Exchange. Older boards stored a single `soulExchangeUid`. */
  private normalizeSoulExchange(): void {
    for (const z of this.state.zones) {
      const legacy = (z as { soulExchangeUid?: string | null }).soulExchangeUid;
      if (!Array.isArray(z.soulExchangeUids)) {
        z.soulExchangeUids = legacy ? [legacy] : [];
      }
      delete (z as { soulExchangeUid?: string | null }).soulExchangeUid;
    }
  }

  /** The monster Solemn or Horn is destroying right now never entered the field. */
  private leavingFromNegatedSummon(uid: string): boolean {
    return this.sendingNegatedSummon && uid === this.state.chainTriggerUid;
  }

  private noteGraveTrigger(
    card: CardInstance,
    home: Seat,
    fromZone: string | null,
    controller: Seat,
  ): void {
    if (!fromZone || fromZone === 'graveyard' || fromZone === 'banished') return;
    const id = Number(card.cardId);
    // Solemn Judgment and Horn of Heaven destroy the monster, but a negated
    // summon never came into play (same rule as Mysterious Puppeteer).
    // Sangan, Witch, and The Immortal of Thunder only see a card that was
    // on the field. Sword of Deep-Seated still sees any send to the Graveyard.
    // Trap Hole destroys a summon that already succeeded, so those still run.
    const arrived = fromZone === 'monsters' && !this.leavingFromNegatedSummon(card.uid);
    if (arrived && id === IMMORTAL_OF_THUNDER) {
      this.graveQueue.push({
        kind: 'immortal',
        controller,
        sourceUid: card.uid,
        cardId: card.cardId,
      });
    }
    const search = arrived ? graveSearchOf(card.cardId) : null;
    if (search) {
      this.graveQueue.push({
        kind: 'search',
        owner: home,
        sourceUid: card.uid,
        cardId: card.cardId,
        which: search,
      });
    }
    if (id === SWORD_OF_DEEP_SEATED) {
      this.graveQueue.push({
        kind: 'sword',
        owner: home,
        sourceUid: card.uid,
        cardId: card.cardId,
      });
    }
  }

  private graveTriggersBlocked(): boolean {
    return (
      this.state.pendingTargets !== null ||
      this.state.pendingFlipEffect !== null ||
      this.state.pendingPriority !== null ||
      this.state.pendingDiscard !== null ||
      this.state.pendingCoinCall !== null ||
      this.state.pendingChoice !== null ||
      this.state.pendingChainOrder !== null ||
      this.state.pendingOfferingSummon !== null ||
      this.state.chain.length > 0 ||
      this.resolvingChain ||
      this.graveBuild !== null
    );
  }

  /**
   * Simultaneous mandatory triggers become one chain. The turn player
   * places theirs first (and chooses that order), then the opponent.
   * A faster card may chain after that. Resolve last-in first-out.
   * Battle-damage jobs flush first; Graveyard jobs wait for that chain.
   */
  private flushBattleTriggers(): void {
    this.flushTriggerQueue(this.battleQueue);
  }

  private flushGraveTriggers(): void {
    this.flushTriggerQueue(this.graveQueue);
  }

  private flushTriggerQueue(queue: GraveJob[]): void {
    if (queue.length === 0 || this.state.status !== 'active') return;
    if (this.graveTriggersBlocked()) return;
    const jobs = queue.splice(0);
    const turn = this.state.turnPlayer;
    const mine = jobs.filter((job) => this.graveController(job) === turn);
    const theirs = jobs.filter((job) => this.graveController(job) !== turn);
    this.graveBuild = [mine, theirs].filter((group) => group.length > 0);
    this.placeNextGraveGroup();
  }

  private graveController(job: GraveJob): Seat {
    if (job.kind === 'search' || job.kind === 'sword') return job.owner;
    return job.controller;
  }

  /**
   * Within one group, Chain Link 1 resolves last. A draw stays under a
   * discard so the opponent loses a card from the old hand, then draws.
   * Sword stays under a search so the shuffle happens first.
   */
  private preferredGraveOrder(jobs: GraveJob[]): GraveJob[] {
    const rank = (job: GraveJob): number => {
      if (job.kind === 'sword' || job.kind === 'drawOpp2') return 0;
      if (job.kind === 'immortal' || job.kind === 'draw1') return 1;
      return 2;
    };
    return [...jobs].sort(
      (a, b) =>
        rank(a) - rank(b) ||
        a.cardId.localeCompare(b.cardId) ||
        a.sourceUid.localeCompare(b.sourceUid),
    );
  }

  private placeNextGraveGroup(): void {
    const groups = this.graveBuild;
    if (!groups || groups.length === 0) {
      this.graveBuild = null;
      if (this.state.chain.length > 0) {
        this.graveChainPending = true;
        this.advanceChain();
      }
      return;
    }
    const group = groups[0];
    if (!group || group.length === 0) {
      groups.shift();
      this.placeNextGraveGroup();
      return;
    }
    const seat = this.graveController(group[0]!);
    const ordered = this.preferredGraveOrder(group);
    if (ordered.length < 2 || this.state.seats[seat].isAI) {
      groups.shift();
      for (const job of ordered) this.placeGraveLink(job);
      this.placeNextGraveGroup();
      return;
    }
    groups[0] = ordered;
    this.openChainOrder(seat, ordered);
  }

  private openChainOrder(seat: Seat, jobs: GraveJob[]): void {
    this.priorityCounter += 1;
    this.state.pendingChainOrder = {
      requestId: `ord-${this.priorityCounter}`,
      chooser: seat,
      cards: jobs.map((job) => ({ uid: job.sourceUid, cardId: job.cardId })),
    };
  }

  /**
   * Last Will asked while the chain order was still open. Put that summon
   * back so this answer can place Sangan and Witch. It asks again after
   * that chain, because flushLastWill waits for the order window.
   */
  private deferOverlappingLastWill(): void {
    if (!this.state.pendingChainOrder) return;
    const targets = this.state.pendingTargets;
    if (!targets?.specialSummonFromDeck) return;
    if (Number(targets.cardId) !== STARTER_IDS.LAST_WILL) return;
    this.state.pendingTargets = null;
    const zone = this.state.zones[targets.chooser];
    if (zone.lastWillCharges > 0 && zone.lastWillReady) zone.lastWillAsk = true;
  }

  /**
   * Ultimate Offering reopened the Battle Phase window while Sangan and
   * Witch were still unordered. Drop that empty window. It asks again
   * after their chain, because tryResumePendingAttack waits for the order.
   */
  private deferOverlappingOfferingWindow(): void {
    if (!this.state.pendingChainOrder) return;
    if (this.state.chain.length > 0) return;
    if (!this.state.pendingPriority) return;
    if (this.state.chainTriggerUid) return;
    this.state.pendingPriority = null;
    this.state.chainPasses = 0;
    if (
      this.state.phase === EnginePhase.BATTLE &&
      this.state.turnPlayer !== this.state.pendingChainOrder.chooser
    ) {
      this.resumeOfferingWindow = true;
    }
  }

  private orderChain(seat: Seat, requestId: string, cardUids: string[]): void {
    const pending = this.state.pendingChainOrder;
    if (!pending || pending.requestId !== requestId) {
      throw new EngineError('No pending response', 'noPendingResponse');
    }
    if (seat !== pending.chooser) {
      throw new EngineError('It is not your response to make', 'notYourResponse');
    }
    const ordered = this.jobsForChainOrder(seat, cardUids);
    const usedLive = this.graveBuild?.[0] != null;
    this.state.pendingChainOrder = null;
    if (usedLive) this.graveBuild?.shift();
    for (const job of ordered) this.placeGraveLink(job);
    this.placeNextGraveGroup();
  }

  /** The chosen order, from the live build or from the cards on the prompt. */
  private jobsForChainOrder(chooser: Seat, cardUids: string[]): GraveJob[] {
    const pending = this.state.pendingChainOrder;
    const shown = pending?.cards ?? [];
    if (
      shown.length !== cardUids.length ||
      new Set(cardUids).size !== cardUids.length ||
      !cardUids.every((uid) => shown.some((card) => card.uid === uid))
    ) {
      throw new EngineError('Select valid targets for this card', 'selectValidTargets');
    }
    const live = this.graveBuild?.[0];
    if (live && live.length === cardUids.length && cardUids.every((uid) => live.some((job) => job.sourceUid === uid))) {
      const byUid = new Map(live.map((job) => [job.sourceUid, job]));
      return cardUids.map((uid) => byUid.get(uid)!);
    }
    if (live) throw new EngineError('Select valid targets for this card', 'selectValidTargets');
    return cardUids.map((uid) => {
      const card = shown.find((row) => row.uid === uid);
      const job = card ? this.rebuildGraveJob(chooser, card.uid, card.cardId) : null;
      if (!job) throw new EngineError('Select valid targets for this card', 'selectValidTargets');
      return job;
    });
  }

  /** A restarted duel kept the prompt and lost the private build. */
  private rebuildGraveJob(chooser: Seat, uid: string, cardId: string): GraveJob | null {
    const id = Number(cardId);
    const owner = findCard(this.state, uid)?.instance.owner ?? chooser;
    const search = graveSearchOf(cardId);
    if (search) return { kind: 'search', owner, sourceUid: uid, cardId, which: search };
    if (id === IMMORTAL_OF_THUNDER) {
      return { kind: 'immortal', controller: chooser, sourceUid: uid, cardId };
    }
    if (id === SWORD_OF_DEEP_SEATED) return { kind: 'sword', owner, sourceUid: uid, cardId };
    if (id === WHITE_MAGICAL_HAT || id === ROBBIN_GOBLIN) {
      return { kind: 'discardRandom', controller: chooser, sourceUid: uid, cardId };
    }
    if (id === MASKED_SORCERER) return { kind: 'draw1', controller: chooser, sourceUid: uid, cardId };
    if (id === THE_BISTRO_BUTCHER) {
      return { kind: 'drawOpp2', controller: chooser, sourceUid: uid, cardId };
    }
    return null;
  }

  /** Places a mandatory trigger without the Spell Speed 1 chain ban. */
  private placeGraveLink(job: GraveJob): void {
    const triggerKind = job.kind === 'search' ? job.which : job.kind;
    this.state.chain.push({
      link: this.state.chain.length + 1,
      controller: this.graveController(job),
      sourceUid: job.sourceUid,
      cardId: job.cardId,
      spellSpeed: 1,
      targetUids: [],
      destroyUids: [],
      triggerUid: null,
      triggerKind,
    });
    this.state.chainPasses = 0;
    const name = getCardData(this.library, job.cardId).name;
    this.emit({
      type: EventType.EFFECT_RESOLVED,
      cardUid: job.sourceUid,
      detail: name,
    });
    if (this.state.chain.length >= 2) {
      this.emit({
        type: EventType.CHAIN_LINK_ADDED,
        links: this.state.chain.map(chainLinkView),
      });
    }
  }

  /** True when this link opened a Deck search and the chain must wait. */
  private resolveGraveChainLink(link: ChainLink): boolean {
    if (link.negated) {
      const name = getCardData(this.library, link.cardId).name;
      this.message('activationNegated', { cardId: link.cardId }, `${name} was negated.`);
      return false;
    }
    if (link.triggerKind === 'immortal') {
      this.resolveImmortalLoss({
        kind: 'immortal',
        controller: link.controller,
        sourceUid: link.sourceUid,
        cardId: link.cardId,
      });
      return false;
    }
    if (link.triggerKind === 'sword') {
      this.placeSwordOnDeck({
        kind: 'sword',
        owner: link.controller,
        sourceUid: link.sourceUid,
        cardId: link.cardId,
      });
      return false;
    }
    if (link.triggerKind === 'sangan' || link.triggerKind === 'witch') {
      return this.resolveGraveSearch({
        kind: 'search',
        owner: link.controller,
        sourceUid: link.sourceUid,
        cardId: link.cardId,
        which: link.triggerKind,
      });
    }
    if (link.triggerKind === 'discardRandom') {
      this.resolveDiscardRandom(link);
      return false;
    }
    if (link.triggerKind === 'draw1' || link.triggerKind === 'drawOpp2') {
      this.resolveBattleDraw(link);
      return false;
    }
    return false;
  }

  /** The opponent discards 1 at random. An empty hand still resolves. */
  private resolveDiscardRandom(link: ChainLink): void {
    const name = getCardData(this.library, link.cardId).name;
    const hand = this.state.zones[otherSeat(link.controller)].hand;
    if (hand.length === 0) {
      this.message(
        'noCardToDiscard',
        { cardId: link.cardId },
        `${name}: your opponent has no cards in hand.`,
      );
    } else {
      const roll = nextRandom(this.state.rngState);
      this.state.rngState = roll.state;
      const index = Math.floor(roll.value * hand.length);
      const card = hand[index];
      if (card) this.sendToGraveyard(card.uid);
      this.message(
        'discardedRandom',
        { cardId: link.cardId },
        `${name}: your opponent discarded 1 card.`,
      );
    }
    // placeGraveLink already played the destello. Hat and Robbin' Goblin
    // stay on the field, so a second EFFECT_RESOLVED flashes them again.
  }

  /** Masked Sorcerer draws 1. The Bistro Butcher makes the opponent draw 2. */
  private resolveBattleDraw(link: ChainLink): void {
    const name = getCardData(this.library, link.cardId).name;
    const opp = otherSeat(link.controller);
    if (link.triggerKind === 'drawOpp2') {
      this.message(
        'battleOppDrew',
        { cardId: link.cardId },
        `${name}: your opponent drew 2 cards.`,
      );
      this.drawCards(opp, 2);
    } else {
      this.message(
        'battleDrew',
        { cardId: link.cardId },
        `${name}: you drew 1 card.`,
      );
      this.drawCards(link.controller, 1);
    }
    // placeGraveLink already emitted EFFECT_RESOLVED when this link was
    // built. A second one here plays another destello after the draws, so
    // Butcher looks like it activates once per card.
  }

  /**
   * Queues Hat, Sorcerer, Butcher, and each face-up Robbin' Goblin of the
   * player whose monster just inflicted battle damage. Only real Life Point
   * loss counts. Destruction with no damage does not.
   */
  private noteBattleDamage(inflictor: CardInstance, controller: Seat): void {
    const id = Number(inflictor.cardId);
    if (id === WHITE_MAGICAL_HAT) this.queueBattleJob('discardRandom', controller, inflictor);
    if (id === MASKED_SORCERER) this.queueBattleJob('draw1', controller, inflictor);
    if (id === THE_BISTRO_BUTCHER) this.queueBattleJob('drawOpp2', controller, inflictor);
    for (const trap of getSpellTraps(this.state.zones[controller])) {
      if (!trap?.faceUp || Number(trap.cardId) !== ROBBIN_GOBLIN) continue;
      this.queueBattleJob('discardRandom', controller, trap);
    }
  }

  private queueBattleJob(
    kind: 'discardRandom' | 'draw1' | 'drawOpp2',
    controller: Seat,
    source: CardInstance,
  ): void {
    this.battleQueue.push({
      kind,
      controller,
      sourceUid: source.uid,
      cardId: source.cardId,
    });
  }

  private resolveImmortalLoss(job: Extract<GraveJob, { kind: 'immortal' }>): void {
    const name = getCardData(this.library, job.cardId).name;
    this.changeLifePoints(job.controller, -5000);
    this.message(
      'immortalLoss',
      { cardId: job.cardId },
      `${name}: you lose 5000 Life Points.`,
    );
    this.checkWin();
  }

  private deckSearchCandidates(seat: Seat, which: GraveSearch): CardInstance[] {
    return this.state.zones[seat].deck.filter((c) => {
      if (kindOf(c, this.library) !== CardKind.MONSTER) return false;
      const data = getCardData(this.library, c.cardId);
      if (isExtraDeckCard(data)) return false;
      if (which === 'sangan') return (data.atk ?? 0) <= 1500;
      return (data.def ?? 0) <= 1500;
    });
  }

  /** Highest ATK, then highest DEF. Witch wants the big body with low DEF. */
  private searchRank(card: CardInstance): number {
    const data = getCardData(this.library, card.cardId);
    return (data.atk ?? 0) * 10_000 + (data.def ?? 0);
  }

  /**
   * Sangan's search is utility, not the biggest body of 1500 ATK or less.
   * Faith and Mask of Darkness rise when the graveyard can pay them.
   */
  private sanganSearchRank(seat: Seat, card: CardInstance): number {
    const id = Number(card.cardId);
    const gy = this.state.zones[seat].graveyard;
    const hasSpell = gy.some((c) => kindOf(c, this.library) === CardKind.SPELL);
    const hasTrap = gy.some((c) => kindOf(c, this.library) === CardKind.TRAP);
    if (id === 31560081) return hasSpell ? 500 : 320;
    if (id === 40640057) return this.state.zones[seat].lifePoints <= 3000 ? 460 : 300;
    if (id === 28933734) return hasTrap ? 450 : 280;
    if (id === 26202165) return 260;
    if (id === 54652250 || id === 7089711) return 200;
    return (getCardData(this.library, card.cardId).atk ?? 0) / 100;
  }

  /**
   * Man-Eater Bug and Hane-Hane: their face-up monster with the highest ATK,
   * otherwise a Set of theirs, otherwise our weakest. A Set's stats stay
   * unread. The effect is mandatory, so an empty opposing field still pays
   * with one of ours.
   */
  private bestMonsterFlipTarget(seat: Seat, uids: string[]): string | null {
    const opp = otherSeat(seat);
    const onOpp = (uid: string): boolean => findCard(this.state, uid)?.location.seat === opp;
    const faceUp = (uid: string): boolean => findCard(this.state, uid)?.instance.faceUp === true;
    const up = this.strongestOf(uids.filter((uid) => onOpp(uid) && faceUp(uid)));
    if (up) return up;
    const set = uids.find((uid) => onOpp(uid));
    if (set) return set;
    return this.weakestOf(uids);
  }

  /** Power Spells and Traps first, then a missing Exodia piece, then a small beater. */
  private bestAiFlipTarget(uids: string[]): string | null {
    const rank: Record<string, number> = {
      '55144522': 100,
      '12580477': 95,
      '53129443': 90,
      '83764718': 85,
      '4031928': 80,
      '19613556': 75,
      '44095762': 70,
      '41420027': 65,
      '77414722': 60,
      '3819470': 55,
      '4206964': 50,
    };
    let best: string | null = null;
    let bestScore = -1;
    for (const uid of uids) {
      const id = findCard(this.state, uid)?.instance.cardId ?? '';
      const score = rank[id] ?? 10;
      if (score > bestScore) {
        bestScore = score;
        best = uid;
      }
    }
    return best;
  }

  /** Destroy theirs. A junk body of ours is only the second victim. */
  private aiBlastVictims(seat: Seat, victims: string[]): string[] | null {
    const seatOf = (uid: string): Seat | null => findCard(this.state, uid)?.location.seat ?? null;
    const theirs = victims.filter((uid) => seatOf(uid) !== seat);
    const ours = victims.filter((uid) => seatOf(uid) === seat);
    const byAtk = (uids: string[]) =>
      [...uids].sort((a, b) => this.atkOfUid(b) - this.atkOfUid(a));
    if (theirs.length >= 2) return byAtk(theirs).slice(0, 2);
    if (theirs.length === 1) {
      const junk = ours.filter((uid) => this.atkOfUid(uid) <= 600);
      const second = this.weakestOf(junk);
      return second ? [theirs[0]!, second] : null;
    }
    return null;
  }

  /** The next draw. Power spells, a finishing Exodia piece, then a Level 4 beater. */
  private bigEyeOrder(seat: Seat, top: CardInstance[]): string[] {
    const power = new Set([
      '55144522', '12580477', '53129443', '83764718', '4031928',
      '19613556', '44095762', '72302403', '72892473',
    ]);
    const handIds = new Set(this.state.zones[seat].hand.map((card) => card.cardId));
    const have = EXODIA_PIECE_IDS.filter((id) => handIds.has(id));
    const missing = have.length >= 4
      ? EXODIA_PIECE_IDS.find((id) => !handIds.has(id))
      : undefined;
    const score = (card: CardInstance): number => {
      if (missing && card.cardId === missing) return 10_000;
      if (power.has(card.cardId)) return 5000;
      if (kindOf(card, this.library) !== CardKind.MONSTER) return 0;
      const data = getCardData(this.library, card.cardId);
      if ((data.level ?? 0) > 4) return 0;
      return data.atk ?? 0;
    };
    let best = top[0];
    let bestScore = best ? score(best) : 0;
    for (const card of top) {
      const value = score(card);
      if (value > bestScore) {
        bestScore = value;
        best = card;
      }
    }
    if (!best || bestScore <= 0) return top.map((card) => card.uid);
    return [best.uid, ...top.filter((card) => card.uid !== best!.uid).map((card) => card.uid)];
  }

  /** True when a human still has to pick. The AI adds the best monster now. */
  private resolveGraveSearch(job: Extract<GraveJob, { kind: 'search' }>): boolean {
    const ranked = this.deckSearchCandidates(job.owner, job.which).sort(
      (a, b) => this.searchRank(b) - this.searchRank(a),
    );
    if (ranked.length === 0) {
      const name = getCardData(this.library, job.cardId).name;
      this.message('noLegalTarget', { cardId: job.cardId }, `${name}: no legal target.`);
      return false;
    }
    if (this.state.seats[job.owner].isAI) {
      const pool = job.which === 'sangan'
        ? [...ranked].sort(
          (a, b) => this.sanganSearchRank(job.owner, b) - this.sanganSearchRank(job.owner, a),
        )
        : ranked;
      const best = pool[0];
      if (best) {
        this.takeDeckCardsToHand(job.owner, [best.uid]);
        this.message(
          'searched',
          { cardId: job.cardId, n: 1 },
          'A monster was added from the Deck.',
        );
      }
      return false;
    }
    this.openDeckAddPrompt(
      job.owner,
      job.sourceUid,
      job.cardId,
      ranked.map((c) => c.uid),
      1,
      1,
    );
    return true;
  }

  private openDeckAddPrompt(
    seat: Seat,
    sourceUid: string,
    cardId: string,
    candidateUids: string[],
    min: number,
    max: number,
  ): void {
    this.priorityCounter += 1;
    const name = getCardData(this.library, cardId).name;
    const prompt = `${name}: add from your Deck`;
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid,
      cardId,
      prompt,
      candidateUids,
      min,
      max,
      addToHandFromDeck: true,
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt,
      candidateUids,
      min,
      max,
      sourceUid,
      sourceCardId: cardId,
    });
  }

  private takeDeckCardsToHand(seat: Seat, uids: string[]): number {
    const deck = this.state.zones[seat].deck;
    const shown: { uid: string; cardId: string }[] = [];
    for (const uid of uids) {
      const idx = deck.findIndex((c) => c.uid === uid);
      if (idx < 0) continue;
      const [card] = deck.splice(idx, 1);
      if (!card) continue;
      card.faceUp = true;
      card.position = CardPosition.ATTACK;
      this.state.zones[seat].hand.push(card);
      this.emit({ type: EventType.CARD_TO_HAND, cardUid: card.uid, seat });
      shown.push({ uid: card.uid, cardId: card.cardId });
    }
    if (shown.length > 0) {
      if (this.shuffleDeck(seat)) {
        this.emit({ type: EventType.DECK_SHUFFLED, seat });
      }
      // The opponent has to see the card. A search that stays hidden
      // cannot be checked against Sangan, Witch, or Thunder Dragon.
      this.rememberCards(seat, shown, true);
      this.emit({
        type: EventType.PEEK_CARDS,
        seat,
        cards: shown,
        reveal: true,
        search: true,
      });
      this.checkExodia(seat);
    }
    return shown.length;
  }

  private placeSwordOnDeck(job: Extract<GraveJob, { kind: 'sword' }>): void {
    const gy = this.state.zones[job.owner].graveyard;
    const idx = gy.findIndex((c) => c.uid === job.sourceUid);
    if (idx < 0) return;
    const [card] = gy.splice(idx, 1);
    if (!card) return;
    card.faceUp = false;
    card.position = CardPosition.ATTACK;
    card.statModifiers = [];
    delete card.atkFinal;
    card.equippedUids = [];
    card.equippedToUid = null;
    delete card.properlySummoned;
    this.state.zones[job.owner].deck.unshift(card);
    const name = getCardData(this.library, card.cardId).name;
    this.emit({
      type: EventType.CARD_MOVED,
      cardUid: card.uid,
      from: { seat: job.owner, zone: 'graveyard' },
      to: { seat: job.owner, zone: 'deck', index: 0 },
    });
    this.message('swordTop', { cardId: card.cardId }, `${name} is placed on top of your Deck.`);
  }

  /** Your monster left your Monster Zone for your Graveyard this turn. */
  private markLastWillReady(seat: Seat): void {
    const z = this.state.zones[seat];
    z.lastWillReady = true;
    if (z.lastWillCharges > 0) z.lastWillAsk = true;
  }

  /** A new phase is another chance to use a Last Will that was declined. */
  private noteLastWillPhase(seat: Seat): void {
    const z = this.state.zones[seat];
    if (z.lastWillCharges > 0 && z.lastWillReady) z.lastWillAsk = true;
  }

  private lastWillCandidates(seat: Seat): CardInstance[] {
    return this.state.zones[seat].deck.filter((c) => {
      if (kindOf(c, this.library) !== CardKind.MONSTER) return false;
      if (isExtraDeckCard(getCardData(this.library, c.cardId))) return false;
      return (getCardData(this.library, c.cardId).atk ?? 0) <= 1500;
    });
  }

  /** True when the picker can actually put a monster on the field. */
  private lastWillCanOffer(seat: Seat): boolean {
    const z = this.state.zones[seat];
    return (
      z.lastWillCharges > 0 &&
      z.lastWillReady &&
      this.hasFreeMonsterZone(seat) &&
      this.lastWillCandidates(seat).length > 0
    );
  }

  private flushLastWill(): void {
    if (
      this.state.status !== 'active' ||
      this.state.pendingTargets ||
      this.state.pendingFlipEffect ||
      this.state.pendingPriority ||
      this.state.pendingDiscard ||
      this.state.pendingCoinCall ||
      this.state.pendingChoice ||
      this.state.pendingChainOrder ||
      this.state.pendingOfferingSummon !== null ||
      this.state.chain.length > 0
    ) {
      return;
    }
    const seat = this.state.turnPlayer;
    const z = this.state.zones[seat];
    if (!z.lastWillAsk || z.lastWillCharges <= 0 || !z.lastWillReady) return;
    if (!this.hasFreeMonsterZone(seat)) return;
    const candidates = this.lastWillCandidates(seat);
    if (candidates.length === 0) return;
    if (this.state.seats[seat].isAI) {
      while (z.lastWillCharges > 0 && this.hasFreeMonsterZone(seat)) {
        const pool = this.lastWillCandidates(seat);
        if (pool.length === 0) break;
        // Sangan (26202165) and Witch (78010363) before a higher vanilla.
        const recruit = (id: string) => {
          const n = Number(id);
          return n === 26202165 || n === 78010363;
        };
        const best = pool.reduce((a, b) => {
          const ar = recruit(a.cardId);
          const br = recruit(b.cardId);
          if (ar !== br) return ar ? a : b;
          return (getCardData(this.library, a.cardId).atk ?? 0) >=
            (getCardData(this.library, b.cardId).atk ?? 0)
            ? a
            : b;
        });
        this.specialSummon(best.uid, seat, CardPosition.ATTACK);
        const landed = this.state.zones[seat].monsters.some((m) => m?.uid === best.uid);
        if (!landed) break;
        this.shuffleDeck(seat);
        z.lastWillCharges -= 1;
      }
      z.lastWillAsk = z.lastWillCharges > 0 && z.lastWillReady;
      return;
    }
    z.lastWillAsk = false;
    this.priorityCounter += 1;
    const name = getCardData(this.library, String(STARTER_IDS.LAST_WILL)).name;
    const candidateUids = candidates.map((c) => c.uid);
    this.state.pendingTargets = {
      requestId: `tgt-${this.priorityCounter}`,
      chooser: seat,
      sourceUid: z.graveyard.find((c) => Number(c.cardId) === STARTER_IDS.LAST_WILL)?.uid ?? '',
      cardId: String(STARTER_IDS.LAST_WILL),
      prompt: `${name}: Special Summon from the Deck?`,
      candidateUids,
      min: 0,
      max: 1,
      specialSummonFromDeck: true,
    };
    this.emit({
      type: EventType.REQUEST_TARGETS,
      requestId: this.state.pendingTargets.requestId,
      seat,
      prompt: this.state.pendingTargets.prompt,
      candidateUids,
      min: 0,
      max: 1,
      sourceUid: this.state.pendingTargets.sourceUid,
      sourceCardId: this.state.pendingTargets.cardId,
    });
  }
}

export class EngineError extends Error {
  readonly vars?: Record<string, string | number>;

  constructor(
    message: string,
    readonly code: DuelErrorCode,
    vars?: Record<string, string | number>,
  ) {
    super(message);
    this.name = 'EngineError';
    if (vars) this.vars = vars;
  }
}
